import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X, Save, UserPlus, Eye, EyeOff, ChevronDown, ArrowLeft, Trash2, Printer, Download, Share2, Mail, MessageCircle, MessageSquare } from 'lucide-react';
import { itemsApi, customersApi, vouchersApi, otherLedgerApi, vchTypeApi, activitiesApi, ledgerGroupApi, getApiOrigin } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast/Toast';
import { findRoundOffLedger, isRoundOffLedgerName } from '../utils/roundOff';
import VoucherAttachments, { uploadStagedAttachments } from '../components/Vouchers/VoucherAttachments';
import { isNativeApp, hasNativePdf, savePdf, NATIVE_PDF_UPDATE_MSG } from '../services/pdfBridge';

const MY_STATE = 'Assam';

// The customer master normally supplies a printable state name through the
// pincode join.  A few older records can still carry only the numeric state
// id, which must not be treated as a state name when deciding CGST/SGST vs
// IGST.
const readableStateName = (...values: unknown[]): string => {
  for (const value of values) {
    const state = String(value ?? '').trim();
    if (state && state !== '-' && !/^\d+$/.test(state)) return state;
  }
  return '';
};

const isInterStateParty = (stateName: string, gstin?: unknown): boolean => {
  if (stateName) return stateName.toLowerCase() !== MY_STATE.toLowerCase();

  // A party record created before the pincode/state join was fixed may have
  // neither a printable state nor a usable state id here. GSTIN's first two
  // characters are the state code; Assam is 18, so any other valid GSTIN is
  // necessarily an inter-state party.
  const normalizedGstin = String(gstin ?? '').trim().toUpperCase();
  return /^\d{2}[A-Z0-9]{13}$/.test(normalizedGstin) && normalizedGstin.slice(0, 2) !== '18';
};

const COMPANY_KEY = 'print-voucher-company';
const BANKS_KEY   = 'print-voucher-banks';
const TERMS_KEY   = 'print-voucher-terms';
const ACTIVE_BANK_KEY = 'print-voucher-active-bank';

const DEFAULT_COMPANY = {
  name:    'ABS Technologies',
  address: '1st Floor, Ram Kumar Plaza, A.T. Road,\nChatribari, Guwahati, Assam, 781001',
  email:   'accounts@abstechnologies.co.in',
  phone:   '9706050760',
  gstin:   '18ACMFA5628G1Z7',
  logo_url: '/logo.png',
};
const DEFAULT_BANK = {
  id: 'default',
  account_name:   'ABS Technologies',
  account_number: '50200117974614',
  ifsc:           'HDFC0004707',
  bank_name:      'HDFC Bank',
  branch:         'Paltan Bazar',
  upi_id:         'abstechnologies@hdfcbank',
  qr_image:       '',
};
const DEFAULT_TERMS = [
  'Payment of bill must be made within 7 Days.',
  'Services once activated are non-refundable.',
];

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return { ...fallback, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return fallback;
}
function loadList<T>(key: string, fallback: T[]): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch { /* ignore */ }
  return fallback;
}

const displayDate = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const addDays = (s: string, n: number) => {
  if (!s) return '';
  const d = new Date(s);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

function numberToWords(n: number): string {
  if (n == null || isNaN(n)) return '';
  const num = Math.round(Math.abs(n) * 100) / 100;
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);

  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
    'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];

  const twoDigits = (x: number): string => {
    if (x < 20) return ones[x];
    return tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : '');
  };
  const threeDigits = (x: number): string => {
    const h = Math.floor(x / 100);
    const r = x % 100;
    return (h ? ones[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? twoDigits(r) : '');
  };
  if (rupees === 0 && paise === 0) return 'Zero Rupees Only';

  let out = '';
  const crore = Math.floor(rupees / 10000000);
  const lakh  = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const rest = rupees % 1000;
  if (crore)    out += twoDigits(crore) + ' Crore ';
  if (lakh)     out += twoDigits(lakh) + ' Lakh ';
  if (thousand) out += twoDigits(thousand) + ' Thousand ';
  if (rest)     out += threeDigits(rest);
  out = out.trim();
  if (out === '') out = 'Zero';
  let result = `${out} Rupees`;
  if (paise > 0) result += ` and ${twoDigits(paise)} Paise`;
  return result + ' Only';
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex text-[12px] py-0.5">
      <div className="w-32 text-slate-600">{label}</div>
      <div className="text-slate-400 mx-1">:</div>
      <div className="flex-1 text-slate-900">{value || '—'}</div>
    </div>
  );
}

interface BatchRow {
  id: string;
  batch_name: string;
  qty: number;
  rate: number;
  amount: number;
  serialSearch?: string;
  serialOpen?: boolean;
}

interface LineItem {
  product_id: string;
  item_name: string;
  // Custom-dropdown state — replaces the native <select> so the picker
  // always opens DOWNWARD instead of letting the browser flip it upward
  // when the row is near the bottom of the viewport.
  item_search?: string;
  item_open?: boolean;
  qty: number;
  rate: number;
  amount: number;
  gst_rate: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  line_total: number;
  batch_rows?: BatchRow[];
}

interface LedgerRow {
  id: string;           // local key
  ledger_id: number | null;
  ledger_name: string;
  amount: number;
  auto: boolean;        // true = CGST/SGST/IGST auto rows
  search: string;
  open: boolean;
}

interface JournalRow {
  id: string;
  drOrCr: 'Dr' | 'Cr';
  ledger_id: number | null;
  ledger_name: string;
  dr_amount: number;
  cr_amount: number;
  search: string;
  open: boolean;
  results: any[];
  billByBill: boolean;
  billAlloc: { id: string; type: 'New' | 'Agr.' | 'On Account'; refno: string; amount: number; direction?: string; refSearch?: string; refOpen?: boolean }[];
}

const emptyJournalRow = (): JournalRow => ({
  id: uid(), drOrCr: 'Dr', ledger_id: null, ledger_name: '',
  dr_amount: 0, cr_amount: 0, search: '', open: false, results: [],
  billByBill: false, billAlloc: [],
});

const emptyLine = (): LineItem => ({
  product_id: '', item_name: '', qty: 1, rate: 0,
  amount: 0, gst_rate: 0, cgst_amount: 0, sgst_amount: 0, igst_amount: 0, line_total: 0,
});

const applyGst = (item: LineItem, amount: number, isIgst: boolean): LineItem => {
  let cgst_amount = 0, sgst_amount = 0, igst_amount = 0;
  if (isIgst) {
    igst_amount = +(amount * item.gst_rate / 100).toFixed(2);
  } else {
    cgst_amount = +(amount * (item.gst_rate / 2) / 100).toFixed(2);
    sgst_amount = +(amount * (item.gst_rate / 2) / 100).toFixed(2);
  }
  const line_total = +(amount + cgst_amount + sgst_amount + igst_amount).toFixed(2);
  return { ...item, amount, cgst_amount, sgst_amount, igst_amount, line_total };
};

const calcLine = (item: LineItem, isIgst: boolean): LineItem => {
  const amount = +(item.qty * item.rate).toFixed(2);
  return applyGst(item, amount, isIgst);
};

// When amount is edited directly: back-calculate rate = amount / qty
const calcLineFromAmount = (item: LineItem, isIgst: boolean, newAmount: number): LineItem => {
  const rate = item.qty > 0 ? +(newAmount / item.qty).toFixed(4) : item.rate;
  return applyGst({ ...item, rate }, newAmount, isIgst);
};

const uid = () => Math.random().toString(36).slice(2);

interface VchTypeItem {
  id: number;
  name: string;
  parent_id: number | null;
  parent_name: string | null;
  is_system: number;
  numbering_mode?: 'manual' | 'automatic';
}

interface StockLine { id: string; item_id: string | null; item_name: string; search: string; open: boolean; qty: number; rate: number; amount: number; gst_rate: number; batch_yes: boolean; batch_rows: { id: string; batch_name: string; qty: number; rate: number; amount: number }[]; results?: any[] }
const emptyStockLine = (): StockLine => ({ id: uid(), item_id: null, item_name: '', search: '', open: false, qty: 0, rate: 0, amount: 0, gst_rate: 0, batch_yes: false, batch_rows: [] });
const emptyBatchRow = () => ({ id: uid(), batch_name: '', qty: 0, rate: 0, amount: 0 });
const fmt = (n: number) => n.toFixed(2);

// Batch popup — opened when clicking the batch button on a stock line
interface StockBatchPopupProps {
  line: StockLine;
  onSave: (rows: StockLine['batch_rows']) => void;
  onClose: () => void;
}
const StockBatchPopup: React.FC<StockBatchPopupProps> = ({ line, onSave, onClose }) => {
  const [rows, setRows] = React.useState<StockLine['batch_rows']>(
    line.batch_rows.length ? line.batch_rows : [emptyBatchRow()]
  );
  const totQty = rows.reduce((s, b) => s + b.qty, 0);
  const totAmt = rows.reduce((s, b) => s + b.amount, 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 flex flex-col" style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-4 pb-2 border-b border-gray-100">
          <div>
            <p className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider">Batch / Serial Entry</p>
            <p className="text-base font-bold text-gray-800 mt-0.5">{line.item_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 mt-0.5"><X size={18} /></button>
        </div>
        {/* Table */}
        <div className="overflow-y-auto flex-1 px-4 py-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-gray-500 uppercase border-b border-gray-200">
                <th className="pb-1.5 text-left w-6">#</th>
                <th className="pb-1.5 text-left">Serial / Batch No.</th>
                <th className="pb-1.5 text-right w-20">Qty</th>
                <th className="pb-1.5 text-right w-20">Rate</th>
                <th className="pb-1.5 text-right w-24">Amount</th>
                <th className="pb-1.5 w-5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((br, idx) => (
                <tr key={br.id} className="border-b border-gray-50">
                  <td className="py-1.5 text-gray-400 text-xs pr-1">{idx + 1}</td>
                  <td className="py-1.5 pr-1">
                    <input autoFocus={idx === 0} type="text" value={br.batch_name} placeholder="Enter serial no."
                      onChange={e => setRows(p => p.map(b => b.id === br.id ? { ...b, batch_name: e.target.value } : b))}
                      className="w-full border border-gray-200 rounded py-1 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </td>
                  <td className="py-1.5 pr-1">
                    <input type="number" step="any" value={br.qty || ''} placeholder=""
                      onChange={e => {
                        const qty = parseFloat(e.target.value) || 0;
                        setRows(p => p.map(b => b.id === br.id ? { ...b, qty, amount: +(qty * b.rate).toFixed(2) } : b));
                      }}
                      className="w-full border border-gray-200 rounded py-1 px-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </td>
                  <td className="py-1.5 pr-1">
                    <input type="number" step="any" value={br.rate || ''} placeholder=""
                      onChange={e => {
                        const rate = parseFloat(e.target.value) || 0;
                        setRows(p => p.map(b => b.id === br.id ? { ...b, rate, amount: +(b.qty * rate).toFixed(2) } : b));
                      }}
                      className="w-full border border-gray-200 rounded py-1 px-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </td>
                  <td className="py-1.5 pr-1">
                    <input type="number" step="any" value={br.amount || ''} placeholder=""
                      onChange={e => setRows(p => p.map(b => b.id === br.id ? { ...b, amount: parseFloat(e.target.value) || 0 } : b))}
                      className="w-full border border-gray-200 rounded py-1 px-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-400 font-medium" />
                  </td>
                  <td className="py-1.5 text-center">
                    {rows.length > 1 && (
                      <button onClick={() => setRows(p => p.filter(b => b.id !== br.id))} className="text-red-400 hover:text-red-600"><X size={13} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => setRows(p => [...p, emptyBatchRow()])}
            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 mt-2">
            <Plus size={12} /> Add Serial No.
          </button>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <div className="text-sm text-gray-600 flex gap-4">
            <span>Total Qty: <strong>{totQty.toFixed(3)}</strong></span>
            <span>Total Amt: <strong>₹{totAmt.toFixed(2)}</strong></span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
            <button onClick={() => onSave(rows)} className="px-5 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StockSide: React.FC<{ title: string; lines: StockLine[]; setLines: React.Dispatch<React.SetStateAction<StockLine[]>>; onOpenBatch: (lineId: string) => void }> = ({ title, lines, setLines, onOpenBatch }) => (
  <div className="flex-1 min-w-0">
    <div className="text-center text-[11px] font-bold uppercase tracking-wide bg-gray-100 border-b border-gray-300 py-1.5 text-gray-600">{title}</div>
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr className="text-[11px] text-gray-500 uppercase">
          <th className="py-1.5 px-2 text-left w-7">#</th>
          <th className="py-1.5 px-2 text-left">Name of Item</th>
          <th className="py-1.5 px-2 text-right w-20">Quantity</th>
          <th className="py-1.5 px-2 text-right w-20">Rate</th>
          <th className="py-1.5 px-2 text-right w-24">Amount</th>
          <th className="py-1.5 px-2 w-6"></th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, idx) => (
          <tr key={line.id} className="border-t border-gray-100 hover:bg-gray-50">
            <td className="py-1 px-2 text-gray-400 text-xs">{idx + 1}</td>
            <td className="py-1 px-1">
              <div className="relative">
                <input type="text" value={line.search}
                  onChange={e => {
                    const q = e.target.value;
                    setLines(p => p.map(l => l.id === line.id ? { ...l, search: q, item_id: null, item_name: '', open: q.length >= 1, batch_yes: false, batch_rows: [] } : l));
                    if (q.length >= 1) {
                      itemsApi.getAll().then((res: any) => {
                        const all = res?.data || res || [];
                        const list = all.filter((it: any) => (it.item_name || '').toLowerCase().includes(q.toLowerCase()));
                        setLines(p => p.map(l => l.id === line.id ? { ...l, results: list, open: true } : l));
                      }).catch(() => {});
                    }
                  }}
                  onBlur={() => setTimeout(() => setLines(p => p.map(l => l.id === line.id ? { ...l, open: false } : l)), 200)}
                  placeholder="Search item..."
                  className={`w-full border rounded text-sm py-1 px-2 focus:outline-none focus:ring-1 ${line.item_id ? 'border-green-400 bg-green-50' : 'border-gray-200 focus:ring-blue-400'}`}
                />
                {line.open && (line.results || []).length > 0 && (
                  <AnchoredList maxH={240}>
                    {(line.results || []).slice(0, 20).map((it: any) => (
                      <div key={it.id} onPointerDown={() => {
                        const batchYes = it.batch === 'Yes';
                        setLines(p => p.map(l => l.id === line.id ? {
                          ...l, item_id: String(it.id), item_name: it.item_name, search: it.item_name, open: false,
                          batch_yes: batchYes, batch_rows: batchYes ? [emptyBatchRow()] : [],
                        } : l));
                        if (batchYes) setTimeout(() => onOpenBatch(line.id), 50);
                      }} className="px-2 py-1.5 text-sm cursor-pointer hover:bg-blue-50 flex items-center justify-between">
                        <span>{it.item_name}</span>
                        {it.batch === 'Yes' && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Batch</span>}
                      </div>
                    ))}
                  </AnchoredList>
                )}
              </div>
            </td>
            <td className="py-1 px-1">
              {line.batch_yes ? (
                <button onClick={() => onOpenBatch(line.id)}
                  className={`w-full border rounded text-sm py-1 px-1 text-right ${line.batch_rows.length > 0 && line.qty > 0 ? 'border-green-400 bg-green-50 text-green-700' : 'border-orange-300 bg-orange-50 text-orange-600'}`}>
                  {line.qty > 0 ? line.qty : 'Set →'}
                </button>
              ) : (
                <input type="number" step="any" value={line.qty || ''} placeholder="0"
                  onChange={e => { const qty = parseFloat(e.target.value) || 0; setLines(p => p.map(l => l.id === line.id ? { ...l, qty, amount: +(qty * l.rate).toFixed(2) } : l)); }}
                  className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400" />
              )}
            </td>
            <td className="py-1 px-1">
              <input type="number" step="any" value={line.rate || ''} placeholder="0.00"
                onChange={e => { const rate = parseFloat(e.target.value) || 0; setLines(p => p.map(l => l.id === line.id ? { ...l, rate, amount: +(l.qty * rate).toFixed(2) } : l)); }}
                disabled={line.batch_yes}
                className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400" />
            </td>
            <td className="py-1 px-1">
              {line.batch_yes ? (
                <button onClick={() => onOpenBatch(line.id)}
                  className={`w-full border rounded text-sm py-1 px-1 text-right font-medium ${line.batch_rows.length > 0 && line.amount > 0 ? 'border-green-400 bg-green-50 text-green-700' : 'border-orange-300 bg-orange-50 text-orange-600'}`}>
                  {line.amount > 0 ? fmt(line.amount) : 'Set →'}
                </button>
              ) : (
                <input type="number" step="any" value={line.amount || ''} placeholder="0.00"
                  onChange={e => setLines(p => p.map(l => l.id === line.id ? { ...l, amount: parseFloat(e.target.value) || 0 } : l))}
                  className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium" />
              )}
            </td>
            <td className="py-1 px-1 text-center">
              {lines.length > 1 && (
                <button onClick={() => setLines(p => p.filter(l => l.id !== line.id))} className="text-red-400 hover:text-red-600"><X size={14} /></button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-gray-100">
          <td colSpan={6} className="py-1 px-2">
            <button onClick={() => setLines(p => [...p, emptyStockLine()])}
              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800">
              <Plus size={12} /> Add Item
            </button>
          </td>
        </tr>
        <tr className="border-t-2 border-gray-200 bg-gray-50">
          <td colSpan={4} className="py-1.5 px-2 text-xs font-semibold text-gray-500 uppercase">Total</td>
          <td className="py-1.5 px-1 text-right text-sm font-bold text-blue-600">
            {fmt(lines.reduce((s, l) => s + (l.amount || 0), 0))}
          </td>
          <td />
        </tr>
      </tfoot>
    </table>
  </div>
);

/**
 * Suggestion list that escapes whatever is clipping it.
 *
 * The voucher grid sits inside a `overflow-y-auto` scroll container, which
 * clips any absolutely-positioned descendant — so a dropdown opened on a row
 * near the bottom was cut off after its first entry, no matter how many matches
 * there were. Rendering into a portal with `position: fixed` takes the list out
 * of that container entirely, and it flips above the field when there is not
 * enough room below.
 *
 * Anchors off its own parent element, so it drops into existing markup without
 * threading a ref through every call site. React events still bubble through
 * the portal, so the existing onPointerDown handlers keep working.
 */
const AnchoredList: React.FC<{ maxH?: number; className?: string; children: React.ReactNode }> =
  ({ maxH = 240, className = '', children }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const host = anchorRef.current?.parentElement;
      if (!host) return;
      const r = host.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom - 8;
      const spaceAbove = r.top - 8;
      // Flip up only when below genuinely cannot hold the list AND above is roomier.
      const flip = spaceBelow < Math.min(maxH, 120) && spaceAbove > spaceBelow;
      const height = Math.min(maxH, flip ? spaceAbove : spaceBelow);
      setPos({
        top: flip ? r.top - height - 2 : r.bottom + 2,
        left: r.left,
        width: r.width,
      });
    };
    measure();
    // Capture phase so scrolling ANY ancestor keeps the list on its field.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [maxH]);

  return (
    <>
      <span ref={anchorRef} className="hidden" aria-hidden />
      {pos && createPortal(
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: maxH }}
          className={`z-[200] bg-white border border-gray-200 rounded shadow-lg overflow-y-auto ${className}`}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
};

const Vouchers: React.FC = () => {
  const { user, isAdmin, canCheckPermission, canDelete, canEdit, canCreate } = useAuth();
  const { showSuccess, showError } = useToast();

  // Runs the print page in a hidden iframe (?share=1 or ?pdf=1) and waits for
  // it to post the result back: the uploaded share token, or the PDF itself.
  const runPrintFrame = <T,>(vid: number, mode: 'share' | 'pdf') => new Promise<T>((resolve, reject) => {
    const iframe = document.createElement('iframe');
    // Real (offscreen) size so html2canvas renders the invoice properly
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:900px;height:1200px;border:none;';
    const cleanup = () => { window.removeEventListener('message', onMsg); try { document.body.removeChild(iframe); } catch {} };
    const timer = setTimeout(() => { cleanup(); reject(new Error('Timed out preparing the PDF')); }, 45000);
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const d = ev.data;
      if (!d || Number(d.voucherId) !== Number(vid)) return;
      if (d.type === `voucher-${mode}-ready`) { clearTimeout(timer); cleanup(); resolve(d as T); }
      else if (d.type === `voucher-${mode}-error`) { clearTimeout(timer); cleanup(); reject(new Error(d.message || 'Failed to prepare PDF')); }
    };
    window.addEventListener('message', onMsg);
    iframe.src = `/billing/print-voucher/${vid}?${mode}=1`;
    document.body.appendChild(iframe);
  });

  const [downloadBusy, setDownloadBusy] = useState(false);
  const handleDirectDownload = async (id: number | string) => {
    // The Android app's WebView drops blob: downloads, so there the PDF comes
    // back from the print page and is saved natively.
    if (isNativeApp) {
      if (!hasNativePdf()) { showError('Update required', NATIVE_PDF_UPDATE_MSG); return; }
      setDownloadBusy(true);
      try {
        const { blob, filename } = await runPrintFrame<{ blob: Blob; filename: string }>(Number(id), 'pdf');
        await savePdf(blob, filename);
      } catch (e: any) {
        showError('Download failed', e?.message || 'Could not save the voucher PDF');
      } finally {
        setDownloadBusy(false);
      }
      return;
    }
    const iframe = document.createElement('iframe');
    // Real (offscreen) size — a 1×1 iframe collapses the invoice layout and
    // the generated PDF comes out stretched/distorted.
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:900px;height:1200px;border:none;';
    iframe.src = `/billing/print-voucher/${id}?download=1`;
    document.body.appendChild(iframe);
    setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 30000);
  };

  // ── Share via Email / WhatsApp / SMS ──
  // The print page renders the invoice in a hidden iframe (?share=1),
  // produces the PDF, uploads it, and posts the public token back here.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState<null | 'email' | 'whatsapp' | 'sms'>(null);
  const requestSharePdf = (vid: number) => runPrintFrame<{ token: string; public_path: string }>(vid, 'share');

  const handleShare = async (mode: 'email' | 'whatsapp' | 'sms', vid: number) => {
    setShareOpen(false);
    setShareBusy(mode);
    try {
      const { token, public_path } = await requestSharePdf(vid);
      if (mode === 'email') {
        const res = await vouchersApi.shareVoucherEmail(vid, { token });
        showSuccess('Email sent', `Voucher emailed to ${res.sent_to}`);
        return;
      }
      const sum = await vouchersApi.getShareSummary(vid);
      const s = sum.data;
      const digitsRaw = String(s.party_mobile || '').replace(/\D/g, '');
      if (!digitsRaw) {
        showError('No mobile number', `No registered mobile found for ${s.party_name || 'this customer'} — add one on the Customers page.`);
        return;
      }
      const digits = digitsRaw.length === 10 ? `91${digitsRaw}` : digitsRaw;
      const link = `${getApiOrigin()}${public_path}`;
      const dateStr = s.vch_date ? new Date(s.vch_date).toLocaleDateString('en-GB') : '';
      const msg = `Dear ${s.contact_person || s.party_name || 'Customer'},\n` +
        `Your ${s.vch_type}${s.vch_no ? ` ${s.vch_no}` : ''}${dateStr ? ` dated ${dateStr}` : ''} ` +
        `for Rs.${Number(s.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })} from ABS Technologies.\n` +
        `Download / view PDF: ${link}`;
      if (mode === 'whatsapp') {
        const waUrl = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
        // In the Android app a page navigation to another site is handed to
        // the phone (WhatsApp opens); window.open has no window to open there.
        if (isNativeApp) window.location.href = waUrl;
        else window.open(waUrl, '_blank');
      } else {
        window.location.href = `sms:+${digits}?body=${encodeURIComponent(msg)}`;
      }
    } catch (e: any) {
      showError('Share failed', e?.message || 'Could not share the voucher');
    } finally {
      setShareBusy(null);
    }
  };

  // --- Print States ---
  const [printCompany] = useState(() => loadJson(COMPANY_KEY, DEFAULT_COMPANY));
  const [printBanks]     = useState(() => loadList(BANKS_KEY, [DEFAULT_BANK]));
  const [printActiveBankId] = useState(() => localStorage.getItem(ACTIVE_BANK_KEY) || 'default');
  const [printTerms]     = useState(() => loadList(TERMS_KEY, DEFAULT_TERMS));
  const [printBillTo, setPrintBillTo]   = useState({
    name: '', address1: '', address2: '', city: '', state: '',
    pincode: '', gstin: '', phone: '', email: '', contact: ''
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // 'mark' = confirm marking as Checked, 'unmark' = confirm removing the flag.
  // Null when no confirmation is pending. Saving is handled inline in the
  // modal's confirm button — keeping a single state prevents two modals
  // ever being open at once.
  const [checkConfirm, setCheckConfirm] = useState<null | 'mark' | 'unmark'>(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Lead → Voucher linkage ──

  // Read-only mode is set when navigated here from Ledger Report (or any
  // other report) by a user without activities.edit permission, OR when
  // the loaded voucher is marked as Checked and the current user isn't
  // admin. Both feed the same `readOnly` flag.
  const navReadOnly = !!(location.state as any)?.readOnly;
  // When a caller (e.g. the Ledger Report drill-through) wants us to return to
  // a specific URL after save/close instead of the generic history-back, it
  // passes returnTo in navigation state. Deterministic — no reliance on the
  // browser history stack.
  const navReturnTo: string | undefined = (location.state as any)?.returnTo;
  const [checkedBy, setCheckedBy]   = useState<string | null>(null);
  const [checkedAt, setCheckedAt]   = useState<string | null>(null);
  const isAdminUser = isAdmin();
  const checkedLockActive = !!checkedBy && !isAdminUser;
  // A voucher is read-only when the caller marked it so, a non-admin opens a
  // Checked (audit-locked) voucher, OR — crucially — the user simply lacks the
  // permission to modify it. The permission check is what makes read-only work
  // no matter HOW the voucher was opened (Daybook, Ledger, Outstanding, deep
  // link…) instead of trusting every caller to remember state.readOnly.
  // Editing an existing voucher needs edit rights; a brand-new one needs create.
  const isExistingVoucher = !!params.id || !!(location.state as any)?.editVoucher?.id;
  const canModifyVouchers = isExistingVoucher
    ? (canEdit('vouchers') || canEdit('activities'))
    : (canCreate('vouchers') || canCreate('activities'));
  const readOnly = navReadOnly || checkedLockActive || !canModifyVouchers;

  // Anyone with edit access to vouchers (or activities) can mark a voucher
  // as Checked — that's the routine reviewer flow. Unmarking is locked down
  // to admins because it removes an audit trail entry.
  const canMarkAsChecked =
    canCheckPermission('vouchers', 'check')
    || canEdit('vouchers')
    || canEdit('activities');
  const canUnmarkAsChecked = isAdminUser;

  // Vch types
  const [allVchTypes, setAllVchTypes]       = useState<VchTypeItem[]>([]);
  // Child + parent type names as returned by the voucher-detail API, captured
  // at load. Lets Print/Download/Share resolve "is this a printable invoice"
  // WITHOUT depending on allVchTypes (which may be empty / still loading for a
  // read-only viewer — otherwise a child type like "Tally Billing" whose own
  // name lacks "sales" would hide the buttons).
  const [loadedTypeText, setLoadedTypeText] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);

  const [editId, setEditId] = useState<number | null>(null);
  // Supporting documents picked BEFORE the voucher exists. A new voucher has no
  // id to attach to, so they wait here and go up right after the save returns
  // one (see flushAttachments). On an existing voucher the picker uploads
  // straight away and this stays empty.
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  // Mirrors editId synchronously (refs update immediately, unlike state) so
  // an async getNextNo response can check "are we still on this voucher?"
  // before applying — otherwise a slow response from a previously-open
  // voucher could land on whatever voucher is open now.
  const editIdRef = useRef<number | null>(null);
  // Each type/date change gets its own request sequence. This prevents an
  // older next-number response from replacing the number for a newer type or
  // date when the requests finish out of order.
  const autoNumberRequestRef = useRef(0);
  // Lets us clear a generated number when the user switches away from an
  // automatic type, without clearing a number they entered for a manual type.
  const previousAutoNumberTypeRef = useRef<number | null>(null);
  const [voucherNo, setVoucherNo]     = useState('');
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().slice(0, 10));

  // Party
  const [partyId, setPartyId]               = useState('');
  const [partyDisplay, setPartyDisplay]     = useState('');
  const [partyState, setPartyState]         = useState('');
  const [isIgst, setIsIgst]               = useState(false);
  // Guards the authoritative customer lookup below so a slower response for
  // the previous party can never change the GST mode for the current one.
  const partyTaxLookupRequestRef            = useRef(0);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDrop, setShowCustomerDrop] = useState(false);
  const [customers, setCustomers]           = useState<any[]>([]);
  const customerRef                         = useRef<HTMLDivElement>(null);
  // Refs used to TRAP focus on ledger / customer fields that have free-typed
  // text but no selection. setTimeout-driven so dropdown clicks (which fire
  // before blur) get a chance to set the id first.
  const customerInputRef                     = useRef<HTMLInputElement>(null);
  const journalInputRefs                     = useRef<Map<string, HTMLInputElement | null>>(new Map());
  const ledgerInputRefs                      = useRef<Map<string, HTMLInputElement | null>>(new Map());
  // Refs for popups so we can auto-focus their first input when they open.
  const batchPopupRef                        = useRef<HTMLDivElement>(null);
  const newCustomerPopupRef                  = useRef<HTMLDivElement>(null);
  const billAllocPopupRef                    = useRef<HTMLDivElement>(null);
  const cloudPopupRef                        = useRef<HTMLDivElement>(null);

  // ── Cloud-category billing-activity picker ──
  // When an item with category "Cloud" is selected for a voucher line, we
  // open a popup listing the customer's pending cloud_activities (those
  // with no voucher_no yet). User multi-selects → amounts sum into the
  // line. After voucher save, the selected activities are stamped with
  // the voucher's vch_no via activitiesApi.markBilled.
  const CLOUD_CATEGORY_ID = 3;
  const [cloudPopup, setCloudPopup] = useState<{
    lineIdx: number;
    activities: any[];
    selectedIds: Set<string>;
    loading: boolean;
    isCreditNote?: boolean;
  } | null>(null);
  const [activitiesToLink, setActivitiesToLink] = useState<string[]>([]);

  // Inline customer creation
  const SUNDRY_DEBTORS_ID = 26; // ledgergroup id for parties (Sundry Debtors)
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const blankCustForm = () => ({ company: '', mobile: '', gstin: '', email: '', pincode: '', address1: '', address2: '', area: '', state: '', ledgergroup: SUNDRY_DEBTORS_ID });
  const [custForm, setCustForm] = useState(blankCustForm());
  const [ledgerGroups, setLedgerGroups] = useState<any[]>([]);

  // Items
  const [lines, setLines]       = useState<LineItem[]>([emptyLine()]);
  const [products, setProducts] = useState<any[]>([]);
  const [showGst, setShowGst]   = useState(false);

  // Ledger rows (auto + user)
  const [ledgerRows, setLedgerRows] = useState<LedgerRow[]>([]);
  const [allLedgers, setAllLedgers] = useState<any[]>([]); // all from customer table (other ledgers)
  const [taxLedgerIds, setTaxLedgerIds] = useState<{ cgst: number|null; sgst: number|null; igst: number|null }>({ cgst: null, sgst: null, igst: null });

  const [submitting, setSubmitting] = useState(false);
  const [remark, setRemark]         = useState('');

  // Keyboard navigation
  const [activeDropIdx, setActiveDropIdx] = useState(-1);
  const formRef = useRef<HTMLFieldSetElement>(null);

  // Batch popup
  const [batchPopupIdx, setBatchPopupIdx] = useState<number | null>(null);
  const [batchDraft, setBatchDraft]       = useState<BatchRow[]>([]);
  const [batchSerials, setBatchSerials]   = useState<string[]>([]);
  const [batchNoFlavour, setBatchNoFlavour] = useState(false);
  const [batchSerialHiIdx, setBatchSerialHiIdx] = useState(-1);
  const batchScrollRef = useRef<HTMLDivElement>(null);

  // Bill allocation
  const [customerBillByBill, setCustomerBillByBill] = useState(false);
  const [billAllocOpen, setBillAllocOpen]           = useState(false);
  const [billAllocEntries, setBillAllocEntries]     = useState<{ id: string; type: 'New' | 'Agr.' | 'On Account'; refno: string; amount: number; direction?: string; refSearch?: string; refOpen?: boolean }[]>([]);
  const [pendingRefs, setPendingRefs]               = useState<{ billname: string; amount: number; vch_date: string; vch_no: string; direction: string }[]>([]);
  const [pendingRefsDir, setPendingRefsDir]         = useState<string>('Cr');

  // Derived: system parents — filtered by allowed_vch_parent_ids if set
  const allowedVchParentIds: number[] = user?.permissions?.vouchers?.allowed_vch_parent_ids ?? [];
  const systemParents = allVchTypes.filter(t =>
    t.is_system === 1 &&
    (isAdmin() || allowedVchParentIds.length === 0 || allowedVchParentIds.includes(t.id))
  );
  // A type belongs to the selected family if the selected parent appears
  // ANYWHERE up its parent chain — so sub-types of sub-types (e.g. a custom
  // type filed under Cloud Billing, itself under Sales) still show up.
  const isInFamily = useCallback((t: any, rootId: number | null) => {
    if (rootId === null) return true;
    const byId = new Map(allVchTypes.map((x: any) => [x.id, x]));
    let cur: any = t;
    for (let hops = 0; cur && hops < 20; hops++) {
      if (cur.id === rootId) return true;
      if (cur.parent_id === cur.id || cur.parent_id == null) return false; // reached a root
      cur = byId.get(cur.parent_id);
    }
    return false;
  }, [allVchTypes]);
  const childTypes    = allVchTypes.filter(t => isInFamily(t, selectedParentId));

  const [voucherType, setVoucherType] = useState('');
  // Keep this resolution in sync with the id sent on save/get-next-no. A
  // manual type leaves the field editable; an automatic one owns the value.
  const selectedVoucherType = childTypes.find(t => t.name === voucherType);
  const selectedVoucherTypeId = selectedVoucherType?.id || selectedParentId;
  const isAutomaticVoucherNumbering = selectedVoucherType?.numbering_mode === 'automatic';

  // Purchase mode = Purchase or Debit Note → user types serial no.
  // Sales mode    = Sales or Credit Note   → user searches & selects existing serial no.
  const isPurchaseMode = (() => {
    const vt = voucherType.toLowerCase();
    const parent = (allVchTypes.find(t => t.id === selectedParentId)?.name || '').toLowerCase();
    return vt.includes('purchase') || vt.includes('debit') || parent.includes('purchase') || parent.includes('debit');
  })();

  const isSalesType = (() => {
    const vt = voucherType.toLowerCase();
    const parent = (allVchTypes.find(t => t.id === selectedParentId)?.name || '').toLowerCase();
    return vt.includes('sales') || parent.includes('sales') || vt.includes('tax invoice') || parent.includes('tax invoice');
  })();

  const isCreditNote = (() => {
    const vt = voucherType.toLowerCase();
    const parent = (allVchTypes.find(t => t.id === selectedParentId)?.name || '').toLowerCase();
    return vt.includes('credit') || parent.includes('credit');
  })();

  const isDebitNote = (() => {
    const vt = voucherType.toLowerCase();
    const parent = (allVchTypes.find(t => t.id === selectedParentId)?.name || '').toLowerCase();
    return vt.includes('debit') || parent.includes('debit');
  })();

  // Vouchers that produce a printable invoice document (items + tax): Sales /
  // Tax Invoice plus the Credit/Debit Note families. Drives the Print /
  // Download / Share actions — the dedicated PrintVoucher page supports all of
  // these. View-only users see the actions too (they don't require edit).
  // Fallback: derive printability from the loaded voucher's own type/parent
  // names when the allVchTypes-based checks can't (e.g. list not loaded for a
  // read-only viewer). Sales / Tax Invoice / Credit Note / Debit Note qualify.
  const loadedPrintable = /sales|tax invoice|credit|debit/.test(loadedTypeText);
  const printableInvoice = isSalesType || isCreditNote || isDebitNote || loadedPrintable;

  // Party's natural bill-allocation side for non-journal vouchers. The return
  // notes FLIP their family's side: Sales → Dr but Credit Note → Cr (customer
  // credited); Purchase → Cr but Debit Note → Dr. Checking the notes first is
  // required because isPurchaseMode already includes Debit Note.
  const customerPartyDir: 'Dr' | 'Cr' =
    isCreditNote ? 'Cr' : isDebitNote ? 'Dr' : isPurchaseMode ? 'Cr' : 'Dr';

  // Journal mode = Contra / Journal / Payment / Receipt — no inventory, Dr/Cr ledger table
  const isStockJournal = (() => {
    const parent = (allVchTypes.find(t => t.id === selectedParentId)?.name || '').toLowerCase();
    const child = voucherType.toLowerCase();
    return parent.includes('stock journal') || child.includes('stock journal');
  })();

  const isJournalType = (() => {
    if (isStockJournal) return false;
    const parent = (allVchTypes.find(t => t.id === selectedParentId)?.name || '').toLowerCase();
    return parent.includes('contra') || parent.includes('journal') || parent.includes('payment') || parent.includes('receipt');
  })();

  // Item Invoice vs Accounting Invoice (Tally-style toggle). Only goods
  // vouchers — Sales / Purchase / Credit Note / Debit Note — offer the choice.
  // Accounting Invoice = pick the party then post ledgers directly (no items).
  const [invoiceMode, setInvoiceMode] = useState<'item' | 'accounting'>('item');
  const canAccountingInvoice = !isJournalType && !isStockJournal;
  const isAccountingInvoice = canAccountingInvoice && invoiceMode === 'accounting';

  // Does this voucher have anything to put on an invoice?
  //
  // The Print / Download / Share buttons used to require `lines.some(l =>
  // l.product_id)` — at least one ITEM row. An Accounting Invoice has none by
  // definition; it IS its ledger rows. So a perfectly valid ledger-only invoice
  // could never be printed, even though PrintVoucher already renders one.
  const hasPrintableContent = isAccountingInvoice
    ? ledgerRows.some(r => r.ledger_id && (r.amount || 0) !== 0)
    : lines.some(l => l.product_id);

  // Fit the voucher card to the space between the app navbar and the viewport
  // bottom so only the form body scrolls (header + total bar stay put). The
  // Layout's <main> is a block, so % heights don't resolve reliably — measure.
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const measure = () => {
      const card = cardRef.current;
      if (!card) return;
      const top = card.getBoundingClientRect().top;
      setCardHeight(Math.max(320, Math.round(window.innerHeight - top - 12))); // 12px = card's p-3 bottom gap
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Align the bottom Grand-Total value under the table's AMOUNT column. The
  // item table uses auto-layout, so the column width is dynamic — measure the
  // AMOUNT header's right edge and pad the bottom bar's total to match.
  const amountAnchorRef = useRef<HTMLTableCellElement>(null);
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const [amtPadRight, setAmtPadRight] = useState(44);
  useLayoutEffect(() => {
    const measure = () => {
      const bar = bottomBarRef.current, anchor = amountAnchorRef.current;
      if (!bar || !anchor) return;
      // amount value right-aligns to (header right − cell px-2 padding = 8px).
      const p = bar.getBoundingClientRect().right - anchor.getBoundingClientRect().right - 8;
      setAmtPadRight(Math.max(0, Math.round(p)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (bottomBarRef.current) ro.observe(bottomBarRef.current);
    if (amountAnchorRef.current) ro.observe(amountAnchorRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    // ResizeObserver handles live layout changes; deps just re-bind on the
    // structural switches that add/remove trailing columns.
  }, [showGst, isIgst, isAccountingInvoice, customerBillByBill, readOnly]);

  // Stock Journal state
  const [stockSource, setStockSource] = useState<StockLine[]>([emptyStockLine()]);
  const [stockDest, setStockDest] = useState<StockLine[]>([emptyStockLine()]);
  // Batch popup: { side: 'src'|'dst', lineId: string } | null
  const [stockBatchPopup, setStockBatchPopup] = useState<{ side: 'src' | 'dst'; lineId: string } | null>(null);
  const stockBatchLine = stockBatchPopup
    ? (stockBatchPopup.side === 'src' ? stockSource : stockDest).find(l => l.id === stockBatchPopup.lineId) ?? null
    : null;

  // Journal rows state
  const [journalRows, setJournalRows] = useState<JournalRow[]>([emptyJournalRow(), emptyJournalRow()]);
  const [mobileStep, setMobileStep] = useState(1);
  // Which journal row's bill-allocation popup is currently open. The popup's
  // working state (billAllocEntries) is synced into this row's own billAlloc
  // array so each bill-by-bill row keeps its own independent allocations.
  const [activeJournalRowId, setActiveJournalRowId] = useState<string | null>(null);
  // When parent changes to something the current voucherType doesn't belong
  // to, default to its first child. Deliberately does NOT reset whenever
  // voucherType is already a valid child of selectedParentId — otherwise
  // this fires on every incidental re-render (allVchTypes refetch, a quick
  // link forcing a specific type, edit-mode Phase 2 resolving the real
  // type) and stomps a type someone/something else just deliberately set,
  // e.g. showing "Sales" with a blank item grid for a Payment voucher, or
  // silently reverting a "create Tax Invoice for this customer" quick link
  // back to whatever Sales' first child happens to be.
  useEffect(() => {
    if (params.id) return;
    if ((location.state as any)?.editVoucher?.id) return;
    const children = allVchTypes.filter(t => isInFamily(t, selectedParentId));
    if (children.some(t => t.name === voucherType)) return;
    setVoucherType(children[0]?.name ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParentId, allVchTypes, params.id, (location.state as any)?.editVoucher?.id]);

  // Auto-generate voucher numbers only for automatic types (and only for a
  // new voucher). Manual types deliberately retain whatever the user typed.
  // We also sequence requests, so a slow response for the previous type/date
  // can never overwrite the current automatic number.
  useEffect(() => {
    const requestId = ++autoNumberRequestRef.current;
    if (editId) return; // don't overwrite when editing (post Phase-2)
    if ((location.state as any)?.editVoucher?.id) return; // edit nav incoming
    if (params.id) return; // URL-based edit route
    if (!selectedVoucherType) return; // voucher types are still loading

    if (!isAutomaticVoucherNumbering) {
      // Remove only a value that came from the immediately previous automatic
      // selection. Do not clear a manually entered voucher number on a date
      // change or ordinary re-render.
      if (previousAutoNumberTypeRef.current !== null) setVoucherNo('');
      previousAutoNumberTypeRef.current = null;
      return;
    }

    const vtId = selectedVoucherTypeId;
    if (!vtId) return;
    previousAutoNumberTypeRef.current = vtId;
    vouchersApi.getNextNo(vtId, voucherDate).then((r: any) => {
      // Defensive double-checks cover edit navigation plus out-of-order
      // responses after the selected type or voucher date changed.
      if (editId || requestId !== autoNumberRequestRef.current) return;
      if (r.success) setVoucherNo(r.data || '');
    }).catch(() => {});
  }, [editId, isAutomaticVoucherNumbering, location.state, params.id, selectedVoucherType, selectedVoucherTypeId, voucherDate]);

  // ----- Data loading -----
  useEffect(() => {
    // Captured once at mount: are we opening this page to edit an existing
    // voucher? If so, Phase 2 (below) owns selectedParentId/voucherType —
    // defaulting to Sales here would otherwise race it.
    const isEditRoute = !!params.id || !!(location.state as any)?.editVoucher?.id;
    vchTypeApi.getAll().then((r: any) => {
      if (r.success) {
        setAllVchTypes(r.data);
        if (!isEditRoute) {
          // Default select first system parent
          // Default to Sales (preferred) or first system type
          const salesType = r.data.find((t: VchTypeItem) => t.is_system === 1 && t.name === 'Sales');
          const firstParent = salesType || r.data.find((t: VchTypeItem) => t.is_system === 1);
          if (firstParent) setSelectedParentId(firstParent.id);
        }
      }
    }).catch(() => {});
    itemsApi.getAll().then(r => { if (r.success) setProducts(r.data); }).catch(() => {});
    // Load the FULL chart of accounts for voucher entry (CGST/SGST/IGST/
    // Sales/Round Off…). scope='all' bypasses the per-user ledger-group
    // filter — otherwise a scoped user gets no tax ledgers and GST posting
    // silently breaks.
    otherLedgerApi.getAll('all').then((r: any) => {
      const data: any[] = r.success ? r.data : (Array.isArray(r) ? r : []);
      setAllLedgers(data);
      // Pre-find tax ledger IDs by name
      const find = (name: string) => data.find((l: any) => (l.company || '').toUpperCase() === name)?.id ?? null;
      setTaxLedgerIds({ cgst: find('CGST'), sgst: find('SGST'), igst: find('IGST') });
    }).catch(() => {});
  }, []);

  // Ref to hold latest handleSubmit so the global-key useEffect doesn't
  // need it in its dependency list (it's defined further down in the file).
  const handleSubmitRef = useRef<() => void>(() => {});

  // 1. Global Keyboard Controller (Shortcuts)
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      // Save: Alt + S
      if (e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSubmitRef.current();
      }
      // Switch types: F4 - F9 (Tally style)
      const typeMap: Record<string, string> = {
        'f4': 'Contra',
        'f5': 'Payment',
        'f6': 'Receipt',
        'f7': 'Journal',
        'f8': 'Sales',
        'f9': 'Purchase',
      };
      const type = typeMap[e.key.toLowerCase()];
      if (type) {
        e.preventDefault();
        const p = allVchTypes.find(t => t.is_system === 1 && t.name === type);
        if (p) setSelectedParentId(p.id);
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [allVchTypes]);

  const moveFocus = (current: HTMLElement, direction: 'forward' | 'backward' | 'up' | 'down') => {
    // Find the nearest container (either a modal or the main form)
    const container = current.closest('.fixed, fieldset') as HTMLElement || formRef.current;
    if (!container) return;
    const focusable = Array.from(container.querySelectorAll(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
    )) as HTMLElement[];
    const index = focusable.indexOf(current);
    if (index === -1) return;

    if (direction === 'forward') {
      if (index < focusable.length - 1) focusable[index + 1].focus();
      else if (container.classList.contains('fixed')) {
         // Loop in modal or stay on last button? Stay on last for safety.
      }
    } else if (direction === 'backward') {
      if (index > 0) focusable[index - 1].focus();
    } else if (direction === 'up' || direction === 'down') {
      // Find element in same column of next/prev row
      const field = current.getAttribute('data-field');
      const rowIdx = parseInt(current.getAttribute('data-row') || '-1', 10);
      if (field && rowIdx !== -1) {
        const targetRow = direction === 'up' ? rowIdx - 1 : rowIdx + 1;
        const target = container.querySelector(`[data-field="${field}"][data-row="${targetRow}"]`) as HTMLElement;
        if (target) target.focus();
      } else if (direction === 'down' && index < focusable.length - 1) {
         focusable[index+1].focus();
      } else if (direction === 'up' && index > 0) {
         focusable[index-1].focus();
      }
    }
  };

  const focusNext = (target: HTMLElement) => {
    setTimeout(() => moveFocus(target, 'forward'), 30);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const target = e.currentTarget;

    // Tally Style: Enter/Tab moves forward
    if (e.key === 'Enter') {
      e.preventDefault();

      // 1. Dropdown open? Pick the highlighted item — and if nothing is
      //    explicitly highlighted yet, default to the FIRST option so a
      //    user typing fast can press Enter without ever pressing Down.
      if (showCustomerDrop && customers.length > 0) {
        const idx = activeDropIdx >= 0 ? activeDropIdx : 0;
        if (customers[idx]) {
          selectParty(customers[idx]);
          setActiveDropIdx(-1);
          focusNext(target); // jump to next field (item dropdown / etc.)
          return;
        }
      }

      const journalOpen = journalRows.find(r => r.open);
      if (journalOpen && journalOpen.results.length > 0) {
        const idx = activeDropIdx >= 0 ? activeDropIdx : 0;
        const l = journalOpen.results[idx];
        if (l) {
          const isBillByBill = l.billbybill === 'Yes';
          setJournalRows(p => p.map(r => r.id === journalOpen.id
            ? { ...r, ledger_id: l.id, ledger_name: l.company, search: l.company, open: false, billByBill: isBillByBill, billAlloc: isBillByBill ? r.billAlloc : [] }
            : r));
          setActiveDropIdx(-1);
          focusNext(target); // jump to amount column
          return;
        }
      }

      const ledgerOpen = ledgerRows.find(r => r.open);
      if (ledgerOpen) {
        const options = ledgerOptions(ledgerOpen.search);
        if (options.length > 0) {
          const idx = activeDropIdx >= 0 ? activeDropIdx : 0;
          if (options[idx]) {
            selectLedger(ledgerOpen.id, options[idx]);
            setActiveDropIdx(-1);
            focusNext(target); // jump to amount column
            return;
          }
        }
      }

      // Item picker (custom dropdown, replaces native <select>).
      // Pick only if (a) the user explicitly highlighted via Down (idx>0),
      // (b) there's an EXACT name match for the typed query, or (c) there's
      // exactly one match. Otherwise let the user keep typing — typing "s"
      // shouldn't auto-pick "SAMPLE8585" just because it's first.
      const openLineIdx = lines.findIndex(l => l.item_open);
      if (openLineIdx >= 0) {
        const ln = lines[openLineIdx];
        const q = (ln.item_search ?? '').trim().toLowerCase();
        const matches = products
          .filter((p: any) => !q || (p.item_name || '').toLowerCase().includes(q))
          .slice(0, 50);
        let pickIdx: number | null = null;
        if (activeDropIdx > 0) pickIdx = activeDropIdx;
        else if (matches.length === 1) pickIdx = 0;
        else if (q) {
          const exact = matches.findIndex((p: any) => (p.item_name || '').toLowerCase() === q);
          if (exact >= 0) pickIdx = exact;
        }
        if (pickIdx !== null && matches[pickIdx]) {
          const p = matches[pickIdx];
          updateLine(openLineIdx, 'product_id', String(p.id));
          updateLine(openLineIdx, 'item_name', p.item_name);
          updateLine(openLineIdx, 'item_search', '');
          updateLine(openLineIdx, 'item_open', false);
          if (p.batch === 'Yes') {
            setTimeout(() => openBatchPopup(openLineIdx, String(p.id)), 0);
          } else if (p.category_id === CLOUD_CATEGORY_ID) {
            setTimeout(() => openCloudPopup(openLineIdx), 0);
          }
          setActiveDropIdx(-1);
          focusNext(target); // jump to Qty column
          return;
        }
        // No clean match — close dropdown but stay on the field so the
        // user can keep typing. Don't move focus, don't auto-pick.
        return;
      }

      // 2. Smart Skip Logic: If on a blank selector, skip to next section
      if (target.tagName === 'SELECT' && (target as HTMLSelectElement).value === '') {
          const field = target.getAttribute('data-field');
          if (field === 'item') {
              // Skip items -> focus Remark
              const rem = formRef.current?.querySelector('[placeholder="Remark (optional)"]') as HTMLElement;
              if (rem) { rem.focus(); return; }
          }
      }
      if (target.tagName === 'INPUT' && target.getAttribute('data-field') === 'ledger-search' && (target as HTMLInputElement).value === '') {
          // Skip ledgers -> focus Remark
          const rem = formRef.current?.querySelector('[placeholder="Remark (optional)"]') as HTMLElement;
          if (rem) { rem.focus(); return; }
      }

      // Normal move forward
      moveFocus(target, 'forward');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      moveFocus(target, e.shiftKey ? 'backward' : 'forward');
    } else if (e.key === 'Backspace') {
      const input = target as HTMLInputElement;
      if (input.tagName === 'INPUT' && (input.value === '' || input.selectionStart === 0)) {
        e.preventDefault();
        moveFocus(target, 'backward');
      }
    } else if (e.key === 'ArrowDown') {
      const itemOpenLine = lines.find(l => l.item_open);
      const anyOpen = showCustomerDrop || journalRows.some(r => r.open) || ledgerRows.some(r => r.open) || !!itemOpenLine;
      if (anyOpen) {
        e.preventDefault();
        // Determine list length
        let len = 0;
        const jOpen = journalRows.find(r => r.open);
        const lOpen = ledgerRows.find(r => r.open);
        if (showCustomerDrop) len = customers.length;
        else if (jOpen) len = jOpen.results.length;
        else if (lOpen) len = ledgerOptions(lOpen.search).length;
        else if (itemOpenLine) {
          const q = (itemOpenLine.item_search ?? '').toLowerCase();
          len = products.filter((p: any) => !q || (p.item_name || '').toLowerCase().includes(q)).slice(0, 50).length;
        }
        setActiveDropIdx(p => Math.min(p + 1, (len || 20) - 1));
      } else {
        moveFocus(target, 'down');
      }
    } else if (e.key === 'ArrowUp') {
      const anyOpen = showCustomerDrop || journalRows.some(r => r.open) || ledgerRows.some(r => r.open) || lines.some(l => l.item_open);
      if (anyOpen) {
        e.preventDefault();
        setActiveDropIdx(p => Math.max(p - 1, 0));
      } else {
        moveFocus(target, 'up');
      }
    } else if (e.key === 'Escape') {
      setShowCustomerDrop(false);
      setJournalRows(p => p.map(r => ({ ...r, open: false })));
      setLedgerRows(p => p.map(r => ({ ...r, open: false })));
      setLines(p => p.map(l => ({ ...l, item_open: false })));
      setActiveDropIdx(-1);
    }
  };

  // Keep the bill-by-bill flag in sync with the selected party's ledger master.
  // On edit the party is restored BEFORE allLedgers finishes loading, so the
  // one-shot derive in the edit loader can miss (partyLedger undefined) and, if
  // the voucher carried no allocations yet — e.g. an accounting invoice created
  // before auto On-Account — customerBillByBill stays false and the Grand Total
  // isn't clickable, so the user "can't update bill allocation". Re-deriving
  // here whenever the party or the ledger list changes fixes that. Skipped for
  // journal types (they use per-row bill-by-bill, not this single-party flag).
  useEffect(() => {
    if (!partyId || isJournalType) return;
    const pl = allLedgers.find((l: any) => String(l.id) === String(partyId));
    if (pl && pl.billbybill !== undefined) setCustomerBillByBill(pl.billbybill === 'Yes');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, allLedgers, isJournalType]);

  // ── Lead prefill (Bill & Close flow) ──
  // ── Edit-mode loader (split into two phases) ──
  // Phase 1: fetch the voucher payload as soon as we have an id in
  // location.state. This deliberately does NOT wait for vchTypes / ledgers
  // because those endpoints can race or fail silently on permission errors,
  // which used to leave the form blank with the user staring at an empty
  // sidebar.
  const [editVoucherData, setEditVoucherData] = useState<any>(null);
  // Cloud activity details (server/serial, users, cycle, expiry) behind a
  // Cloud Billing voucher — shown under the item on the voucher screen.
  const [cloudDetails, setCloudDetails] = useState<any[]>([]);
  useEffect(() => {
    // Edit signal can come from either source:
    //  1. URL param  /billing/vouchers/edit/:id  (preferred — survives refresh)
    //  2. location.state.editVoucher.id          (legacy callers)
    const urlId   = params.id ? parseInt(params.id, 10) : NaN;
    const stateId = (location.state as any)?.editVoucher?.id;
    const editId  = !isNaN(urlId) ? urlId : stateId;
    // Files staged for a new voucher must not follow the user into an existing
    // one — that voucher uploads immediately, so anything staged is stale.
    setPendingAttachments([]);
    if (!editId) { setEditVoucherData(null); setCloudDetails([]); return; }
    vouchersApi.getById(editId)
      .then(res => { if (res.success && res.data) setEditVoucherData(res.data); })
      .catch(() => {});
    activitiesApi.getByVoucher(Number(editId))
      .then(res => { if (res.success) setCloudDetails(res.data || []); })
      .catch(() => setCloudDetails([]));
  }, [params.id, (location.state as any)?.editVoucher?.id]);

  // Phase 2: apply the fetched payload to form state. Re-runs as
  // vchTypes / ledgers stream in so even if those load late, the type
  // dropdown / party state get filled in correctly.
  useEffect(() => {
    const v = editVoucherData;
    if (!v) return;
    editIdRef.current = v.id;
    setEditId(v.id);
    setMobileStep(2); // skip type-selection screen when editing

      // 1. Voucher type — prefer matching against the loaded allVchTypes
      //    (so children of the parent type populate correctly). When that
      //    list hasn't loaded (vchtypes API gated by separate permission),
      //    fall back to the response's own vch_type_name so the dropdown
      //    still shows "Sales"/"Purchase"/etc. instead of "-- No types --".
      const vt = allVchTypes.find(t => t.id === v.vch_type_id);
      if (vt) {
        const parentId = vt.parent_id && vt.parent_id !== vt.id ? vt.parent_id : vt.id;
        setSelectedParentId(parentId);
        setVoucherType(vt.name);
      } else if (v.vch_type_name) {
        setVoucherType(v.vch_type_name);
        if (v.vch_type_parent_id) setSelectedParentId(v.vch_type_parent_id);
      }
      // Capture the type text from the response itself (child + parent names),
      // so printability is resilient to allVchTypes not being loaded.
      setLoadedTypeText((`${v.vch_type_name || ''} ${(v as any).vch_parent_type_name || ''}`).toLowerCase());

      // 2. Header fields
      setVoucherNo(v.vch_no || '');
      setVoucherDate(v.vch_date ? v.vch_date.split('T')[0] : '');
      setRemark(v.remark || '');
      setCheckedBy(v.checked_by || null);
      setCheckedAt(v.checked_at || null);
      // Accounting Invoice (goods voucher saved with ledgers, no items) — the
      // backend flags this so the form opens in the right mode.
      setInvoiceMode(v.is_accounting_invoice ? 'accounting' : 'item');

      // This voucher was saved without a number (e.g. imported, or created
      // before automatic numbering was set up for its type). Suggest one now,
      // same as a brand-new voucher would get, so it can be numbered on save.
      // Guarded so it never overwrites an already-assigned number or a number
      // the user is mid-typing: only applies if vch_no was empty on load, and
      // only lands if the field is STILL empty and we're still on this voucher.
      if (!v.vch_no && v.vch_type_id) {
        vouchersApi.getNextNo(v.vch_type_id, v.vch_date ? v.vch_date.split('T')[0] : undefined)
          .then((r: any) => {
            if (r.success && r.data && editIdRef.current === v.id) {
              setVoucherNo(current => current || r.data);
            }
          }).catch(() => {});
      }

      // 3. Party — also resolve state to set isIgst correctly
      setPartyId(String(v.party_ledger_id));
      // party_name comes from customer JOIN; fall back to ledger entry name
      // in case the customer row was deleted or the party is a non-customer ledger
      const partyEntryName = (v.ledgerEntries || []).find(
        (le: any) => String(le.ledger_id) === String(v.party_ledger_id)
      )?.ledger_name;
      setPartyDisplay(v.party_name || partyEntryName || '');
      const partyLedger = allLedgers.find((l: any) => l.id === v.party_ledger_id);
      // The chart-of-accounts payload deliberately does not include a party's
      // state.  The voucher detail does (`party_state`), so use it first when
      // reopening a voucher.  Previously this read an undefined `state` from
      // allLedgers, which always fell back to CGST+SGST on edit.
      const partyStateName = readableStateName(v.party_state, partyLedger?.state_name, partyLedger?.state);
      const editIgst = isInterStateParty(partyStateName, v.party_gst);
      setPartyState(partyStateName);
      setIsIgst(editIgst);

      // Restore customerBillByBill for items-mode vouchers. Without this,
      // re-saving sends bill_allocation: undefined and the backend wipes the
      // outstanding entries. Use the party's billbybill flag from allLedgers,
      // falling back to "the voucher already has bill allocations" as proof.
      if (partyLedger?.billbybill === 'Yes' || (v.billAllocations?.length ?? 0) > 0) {
        setCustomerBillByBill(true);
      }

      // 4. Split ledger entries
      // Note: inventory hangs on the GOODS ledger entry (Purchase/Sales), NOT the party ledger entry
      const allEntries: any[] = v.ledgerEntries || [];
      // Find the entry that actually has inventory (could be Purchase/Sales row, not party row)
      const inventoryEntry = allEntries.find((le: any) => le.inventoryEntries?.length > 0);
      // Exclude both party entry AND goods/inventory entry (both are auto-managed by the system)
      const otherEntries: any[] = allEntries.filter((le: any) =>
        String(le.ledger_id) !== String(v.party_ledger_id) &&
        le.id !== inventoryEntry?.id
      );

      // 5. Item lines (from inventory entry) — including batch rows
      if (inventoryEntry?.inventoryEntries?.length) {
        setLines(inventoryEntry.inventoryEntries.map((ie: any) => {
          const base = {
            product_id: String(ie.item_id),
            item_name: ie.item_name || '',
            qty: Math.abs(Number(ie.qty)),
            rate: Number(ie.rate),
            amount: Math.abs(Number(ie.amount)),
            gst_rate: Number(ie.gst_rate) || 0,
            cgst_amount: 0,
            sgst_amount: 0,
            igst_amount: 0,
            line_total: Math.abs(Number(ie.amount)),
            // Batch tracking off → the stored row is the mirror the server
            // keeps, not an allocation of the user's. Editing the qty would
            // otherwise be refused for disagreeing with a row they never set.
            batch_rows: String(ie.item_batch || '').toLowerCase() !== 'yes' ? [] : (ie.batchRows || []).map((b: any) => ({
              // Every row needs its own id. Without one they are all `undefined`,
              // so the batch popup's `r.id === row.id` matches EVERY row and
              // editing one serial rewrites them all — clearing row 1 blanked
              // rows 2 and 3. React's `key` collapses on undefined too.
              id: uid(),
              batch_name: b.batch_name || '',
              qty: Math.abs(Number(b.qty)),
              rate: Number(b.rate),
              amount: Math.abs(Number(b.amount)),
            })),
          };
          // When qty=0 but amount>0 (amount-only batch entry), apply amount
          // directly so it isn't lost by qty*rate=0 in calcLine.
          return base.qty === 0 && base.amount > 0
            ? calcLineFromAmount(base, editIgst, base.amount)
            : calcLine(base, editIgst);
        }));
      }

      // 6. Determine if journal type / stock journal
      const parentName = (allVchTypes.find(t => t.id === (vt?.parent_id && vt.parent_id !== vt.id ? vt.parent_id : vt?.id))?.name || '').toLowerCase();
      const vtNameLc = (vt?.name || v.vch_type_name || '').toLowerCase();
      const isStockJournalEdit = parentName.includes('stock journal') || vtNameLc.includes('stock journal');
      const isJournal = !isStockJournalEdit && ['contra','journal','payment','receipt'].some(k => parentName.includes(k) || vtNameLc.includes(k));

      if (isStockJournalEdit) {
        // Load source and destination inventory entries from the dummy ledger entry
        const allInvEntries: any[] = (v.ledgerEntries || []).flatMap((le: any) => le.inventoryEntries || []);
        const mapStockLine = (ie: any) => {
          const itemIsBatched = String(ie.item_batch || '').toLowerCase() === 'yes';
          const batchRows = !itemIsBatched ? [] : (ie.batchRows || []).map((b: any) => ({
            id: uid(),
            batch_name: b.batch_name || '',
            qty: Math.abs(Number(b.qty)),
            rate: Number(b.rate),
            amount: Math.abs(Number(b.amount)),
          }));
          return {
            id: uid(),
            item_id: String(ie.item_id),
            item_name: ie.item_name || '',
            search: ie.item_name || '',
            open: false,
            qty: Math.abs(Number(ie.qty)),
            rate: Number(ie.rate),
            amount: Math.abs(Number(ie.amount)),
            gst_rate: Number(ie.gst_rate) || 0,
            batch_yes: itemIsBatched,
            batch_rows: batchRows,
          };
        };
        const srcEntries = allInvEntries.filter(ie => ie.side === 'source');
        const dstEntries = allInvEntries.filter(ie => ie.side === 'destination');
        setStockSource(srcEntries.length ? srcEntries.map(mapStockLine) : [emptyStockLine()]);
        setStockDest(dstEntries.length ? dstEntries.map(mapStockLine) : [emptyStockLine()]);
      } else if (isJournal) {
        // Bill allocations are grouped by their own ledger (bill_allocation.ledger)
        // so each bill-by-bill ledger row gets back exactly its own allocations,
        // independent of which ledger happens to be the voucher's party_ledger_id.
        const allocsByLedger = new Map<string, any[]>();
        for (const ba of (v.billAllocations || [])) {
          const key = String(ba.ledger);
          if (!allocsByLedger.has(key)) allocsByLedger.set(key, []);
          allocsByLedger.get(key)!.push(ba);
        }
        // Journal rows: all ledger entries as Dr/Cr rows
        setJournalRows((v.ledgerEntries || []).map((le: any) => {
          const rowAllocs = allocsByLedger.get(String(le.ledger_id)) || [];
          return {
            id: uid(),
            drOrCr: Number(le.amount) >= 0 ? 'Dr' as const : 'Cr' as const,
            ledger_id: le.ledger_id,
            ledger_name: le.ledger_name || '',
            dr_amount: Number(le.amount) > 0 ? Number(le.amount) : 0,
            cr_amount: Number(le.amount) < 0 ? Math.abs(Number(le.amount)) : 0,
            search: le.ledger_name || '',
            open: false,
            results: [],
            billByBill: rowAllocs.length > 0,
            billAlloc: rowAllocs.map((ba: any) => ({
              id: uid(),
              // No bill reference stored → it was saved "On Account"; a
              // present bill name means it's tied to a bill (Agr.). The table
              // has no type column, so this is how the type round-trips.
              type: (ba.billname && String(ba.billname).trim()) ? 'Agr.' as const : 'On Account' as const,
              refno: ba.billname || '',
              refSearch: ba.billname || '',
              amount: Math.abs(Number(ba.amount)),
              direction: Number(ba.amount) >= 0 ? 'Dr' : 'Cr',
            })),
          };
        }));
      } else {
        // Ledger rows for normal mode. Show ALL non-party / non-goods
        // entries, including tax rows (CGST / SGST / IGST / Roundoff), so
        // the user can SEE the full breakdown. Tax rows are tagged auto so
        // they're protected from the manual-edit handlers, and the auto-sync
        // effect that re-derives them from items uses the same `auto-cgst`
        // ids — replacing this row instead of duplicating it.
        if (otherEntries.length) {
          // Derive tax ledger IDs fresh from allLedgers to avoid stale-closure
          // race with taxLedgerIds state (both are set in the same async effect).
          const findId = (name: string) => allLedgers.find((l: any) => (l.company || '').toUpperCase() === name)?.id ?? null;
          const cgstId = findId('CGST'); const sgstId = findId('SGST'); const igstId = findId('IGST');
          const taxLedgerIdSet = new Set([cgstId, sgstId, igstId].filter(Boolean));

          // Deduplicate by ledger_id FIRST — tally imports often save duplicate
          // entries for the same ledger in a single voucher. Keep the first
          // occurrence (highest id = most recent if re-imported, but first is
          // fine since amounts should be identical).
          const seenLedgerIds = new Set<number>();
          const uniqueEntries = otherEntries.filter((le: any) => {
            if (!le.ledger_id) return true; // null-id rows always pass
            if (seenLedgerIds.has(le.ledger_id)) return false;
            seenLedgerIds.add(le.ledger_id);
            return true;
          });

          const mapped = uniqueEntries.map((le: any) => {
            // If the backend JOIN missed the name, fall back to allLedgers already in state
            const resolvedName = le.ledger_name ||
              (le.ledger_id ? (allLedgers.find((l: any) => l.id === le.ledger_id)?.company || '') : '');
            const name = resolvedName.toLowerCase();
            const isCgst = /^cgst$/.test(name) || name.includes('cgst') || le.ledger_id === cgstId;
            const isSgst = /^sgst$/.test(name) || name.includes('sgst') || le.ledger_id === sgstId;
            const isIgst = /^igst$/.test(name) || name.includes('igst') || le.ledger_id === igstId;
            const isRound = isRoundOffLedgerName(resolvedName);
            const isTaxById = le.ledger_id && taxLedgerIdSet.has(le.ledger_id);
            const presetId =
              isCgst ? 'auto-cgst' :
              isSgst ? 'auto-sgst' :
              isIgst ? 'auto-igst' :
              isRound ? 'auto-roundoff' :
              uid();
            return {
              id: presetId,
              ledger_id: le.ledger_id,
              ledger_name: resolvedName,
              amount: Math.abs(Number(le.amount)),
              // In an Accounting Invoice every ledger (incl. tax) is manual, so
              // never flag them auto — otherwise they'd be treated as
              // item-derived and stripped / made non-removable.
              auto: !v.is_accounting_invoice && (isCgst || isSgst || isIgst || isTaxById),
              search: resolvedName,
              open: false,
            };
          });
          // Second dedup pass by row id (catches any remaining preset-id collisions)
          const deduped = Array.from(new Map(mapped.map(r => [r.id, r])).values());
          setLedgerRows(deduped);
        }
      }

      // 7. Bill allocations — non-journal (customer) mode only; journal-mode
      // allocations were already distributed into each row's own billAlloc above.
      if (!isJournal && v.billAllocations?.length) {
        setBillAllocEntries(v.billAllocations.map((ba: any) => ({
          id: uid(),
          // Present bill name → Agr. (against that bill); none → On Account.
          type: (ba.billname && String(ba.billname).trim()) ? 'Agr.' as const : 'On Account' as const,
          refno: ba.billname || '',
          refSearch: ba.billname || '',
          amount: Math.abs(Number(ba.amount)),
          direction: Number(ba.amount) >= 0 ? 'Dr' : 'Cr',
        })));
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editVoucherData, allVchTypes.length, allLedgers.length]);

  // Autocomplete is intentionally lightweight and an older customer may expose
  // only a numeric state id there. Re-read the selected party's full master so
  // the tax mode is authoritative for every entry path (new selection, lead
  // prefill, and an existing voucher opened for edit). The voucher-detail
  // state remains a fallback while opening historical vouchers.
  useEffect(() => {
    const requestId = ++partyTaxLookupRequestRef.current;
    if (!partyId) return;

    const isLoadedParty = String(editVoucherData?.party_ledger_id ?? '') === String(partyId);
    const detailState = isLoadedParty ? editVoucherData?.party_state : '';
    const detailGstin = isLoadedParty ? editVoucherData?.party_gst : '';

    customersApi.getById(partyId)
      .then(res => {
        if (requestId !== partyTaxLookupRequestRef.current || !res?.success || !res.data) return;
        const customer = res.data;
        const stateName = readableStateName(customer.state_name, customer.state, detailState);
        setPartyState(stateName);
        setIsIgst(isInterStateParty(stateName, customer.gstin || detailGstin));
      })
      // Keep the state already resolved from autocomplete or voucher detail if
      // this secondary lookup is unavailable for a restricted user.
      .catch(() => {});
  }, [partyId, editVoucherData]);

  // Sync print Bill To details whenever partyId / editVoucherData changes
  useEffect(() => {
    if (editVoucherData) {
      setPrintBillTo({
        name:     editVoucherData.party_name           || '',
        address1: editVoucherData.party_address1       || '',
        address2: editVoucherData.party_address2       || '',
        city:     editVoucherData.party_city           || '',
        state:    editVoucherData.party_state          || '',
        pincode:  editVoucherData.party_pincode ? String(editVoucherData.party_pincode) : '',
        gstin:    editVoucherData.party_gst            || '',
        phone:    editVoucherData.party_mobile ? String(editVoucherData.party_mobile) : '',
        email:    editVoucherData.party_email          || '',
        contact:  editVoucherData.party_contact_person || '',
      });
      // Fetch full customer details to merge any newer/fuller fields
      if (editVoucherData.party_ledger_id) {
        customersApi.getById(String(editVoucherData.party_ledger_id))
          .then(res => {
            if (res?.success && res.data) {
              const c = res.data;
              setPrintBillTo(prev => ({
                ...prev,
                address1: c.address1 || prev.address1,
                address2: c.address2 || prev.address2,
                city: c.pincode_city || c.city || prev.city,
                state: c.state_name || c.state || prev.state,
                pincode: c.pincode ? String(c.pincode) : prev.pincode,
                gstin: c.gstin || prev.gstin,
                phone: c.mobile ? String(c.mobile) : prev.phone,
                email: c.email || prev.email,
                contact: c.person || c.contact_person || prev.contact,
              }));
            }
          })
          .catch(() => {});
      }
    } else if (partyId) {
      customersApi.getById(partyId)
        .then(res => {
          if (res?.success && res.data) {
            const c = res.data;
            setPrintBillTo({
              name: c.company || partyDisplay || '',
              address1: c.address1 || '',
              address2: c.address2 || '',
              city: c.pincode_city || c.city || '',
              state: c.state_name || c.state || '',
              pincode: c.pincode ? String(c.pincode) : '',
              gstin: c.gstin || '',
              phone: c.mobile ? String(c.mobile) : '',
              email: c.email || '',
              contact: c.person || c.contact_person || '',
            });
          } else {
            setPrintBillTo(b => ({ ...b, name: partyDisplay, state: partyState }));
          }
        })
        .catch(() => {
          setPrintBillTo(b => ({ ...b, name: partyDisplay, state: partyState }));
        });
    } else {
      setPrintBillTo({
        name: '', address1: '', address2: '', city: '', state: '',
        pincode: '', gstin: '', phone: '', email: '', contact: ''
      });
    }
  }, [partyId, partyDisplay, editVoucherData, partyState]);

  // Live party autocomplete
  useEffect(() => {
    if (customerSearch.length < 2) {
      setCustomers([]);
      setShowCustomerDrop(false);
      return;
    }
    const t = setTimeout(() => {
      customersApi.search(customerSearch).then((r: any) => {
        const list = Array.isArray(r) ? r : (r?.data || []);
        setCustomers(list);
        if (list.length > 0) setShowCustomerDrop(true);
      }).catch((e: any) => {
        console.warn('[CustomerSearch] failed:', e?.message || e);
        setCustomers([]);
      });
    }, 200);
    return () => clearTimeout(t);
  }, [customerSearch]);

  // Close party dropdown on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (customerRef.current && !customerRef.current.contains(e.target as Node)) setShowCustomerDrop(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ----- Party selection -----
  const selectParty = async (c: any) => {
    setPartyDisplay(c.company);
    setPartyId(String(c.id));
    setCustomerSearch('');
    setShowCustomerDrop(false);

    // state_name comes directly from autocomplete (pincode JOIN in backend)
    let stateName = readableStateName(c?.state_name, c?.state);

    // Fallback: pincodeApi lookup if autocomplete didn't include state
    if (!stateName && c?.pincode && String(c.pincode).replace(/\D/g,'').length === 6) {
      try {
        const { pincodeApi } = await import('../services/api');
        const pr = await pincodeApi.lookup(String(c.pincode).replace(/\D/g,''));
        stateName = readableStateName(pr?.state);
      } catch {}
    }

    setPartyState(stateName);
    const igst = isInterStateParty(stateName, c?.gstin);
    setIsIgst(igst);
    setLines(prev => prev.map(l => calcLine(l, igst)));
    setCustomerBillByBill(c.billbybill === 'Yes');
    setBillAllocEntries([]);
  };

  // Recalculate lines when GST type changes
  useEffect(() => {
    setLines(prev => prev.map(l => calcLine(l, isIgst)));
  }, [isIgst]);

  // ----- Totals -----
  const subtotal   = +lines.reduce((s, l) => s + l.amount, 0).toFixed(2);
  const totalCgst  = +lines.reduce((s, l) => s + l.cgst_amount, 0).toFixed(2);
  const totalSgst  = +lines.reduce((s, l) => s + l.sgst_amount, 0).toFixed(2);
  const totalIgst  = +lines.reduce((s, l) => s + l.igst_amount, 0).toFixed(2);
  // Exclude roundoff row from rawTotal to avoid circular dependency
  const ledgerTotal  = ledgerRows.filter(r => r.id !== 'auto-roundoff').reduce((s, r) => s + (r.amount || 0), 0);
  const rawTotal     = +(subtotal + ledgerTotal).toFixed(2);
  // Rounding the total is only legitimate when a Round Off ledger row carries
  // the difference. That row is injected for Item Invoices only — an
  // Accounting Invoice IS its ledger rows, so rounding the total there just
  // makes the voucher not balance (ledgers 53.23, total 53.00).
  // A Round Off ledger the user added themselves means they are handling the
  // rounding explicitly, so the automatic row must stand down — otherwise the
  // total is rounded twice (0.88 entered by hand became a 1.00 grand total via
  // an injected 0.12). Same reasoning already applied to Accounting Invoices
  // just below: whoever enters the rounding owns it.
  const userRoundoffRow = ledgerRows.some(
    r => r.id !== 'auto-roundoff' && isRoundOffLedgerName(r.ledger_name || r.search),
  );
  const roundoffAmt  = (isAccountingInvoice || userRoundoffRow)
    ? 0
    : +(Math.round(rawTotal) - rawTotal).toFixed(2);
  const grandTotal   = +(rawTotal + roundoffAmt).toFixed(2);

  // ----- Auto-sync GST ledger rows (CGST/SGST/IGST) -----
  useEffect(() => {
    // Accounting Invoice has no items to derive tax from — every ledger row
    // (including CGST/SGST) is entered manually, so never auto-manage them here
    // (otherwise the manually-entered tax rows would be wiped).
    if (isAccountingInvoice) return;
    setLedgerRows(prev => {
      // Keep user rows except roundoff (managed separately)
      const user = prev.filter(r => !r.auto && r.id !== 'auto-roundoff');
      const roundoffRow = prev.find(r => r.id === 'auto-roundoff');
      const auto: LedgerRow[] = [];

      // Only emit auto tax rows when the matching ledger truly exists in the
      // backend. Skipping when ledger_id is null avoids showing labels the
      // user can't actually transact against — and matches the backend's
      // per-item GST persistence (which derives totals from items[] regardless).
      if (!isIgst) {
        if (totalCgst > 0 && taxLedgerIds.cgst) {
          const led = allLedgers.find(l => l.id === taxLedgerIds.cgst);
          auto.push({
            id: 'auto-cgst', ledger_id: taxLedgerIds.cgst,
            ledger_name: led?.company || 'CGST',
            amount: totalCgst, auto: true, search: led?.company || 'CGST', open: false,
          });
        }
        if (totalSgst > 0 && taxLedgerIds.sgst) {
          const led = allLedgers.find(l => l.id === taxLedgerIds.sgst);
          auto.push({
            id: 'auto-sgst', ledger_id: taxLedgerIds.sgst,
            ledger_name: led?.company || 'SGST',
            amount: totalSgst, auto: true, search: led?.company || 'SGST', open: false,
          });
        }
      } else {
        if (totalIgst > 0 && taxLedgerIds.igst) {
          const led = allLedgers.find(l => l.id === taxLedgerIds.igst);
          auto.push({
            id: 'auto-igst', ledger_id: taxLedgerIds.igst,
            ledger_name: led?.company || 'IGST',
            amount: totalIgst, auto: true, search: led?.company || 'IGST', open: false,
          });
        }
      }

      const restored = auto.map(a => {
        const existing = prev.find(p => p.id === a.id);
        if (existing && existing.ledger_id && existing.ledger_id !== a.ledger_id) {
          return { ...a, ledger_id: existing.ledger_id, ledger_name: existing.ledger_name, search: existing.search };
        }
        return a;
      });

      // Preserve roundoff row position at the end
      return roundoffRow ? [...restored, ...user, roundoffRow] : [...restored, ...user];
    });
  }, [totalCgst, totalSgst, totalIgst, isIgst, taxLedgerIds, isAccountingInvoice]);

  // ----- Auto-sync Roundoff ledger row -----
  useEffect(() => {
    // Accounting Invoice: the ledger rows ARE the total — don't inject an
    // auto roundoff row (the user enters exact amounts, incl. any rounding).
    if (isAccountingInvoice) return;
    const roundoffLedger = findRoundOffLedger(allLedgers);
    setLedgerRows(prev => {
      const withoutRoundoff = prev.filter(r => r.id !== 'auto-roundoff');
      if (roundoffAmt === 0) return withoutRoundoff;
      const existing = prev.find(r => r.id === 'auto-roundoff');
      return [...withoutRoundoff, {
        id: 'auto-roundoff',
        ledger_id: existing?.ledger_id ?? (roundoffLedger?.id || null),
        ledger_name: existing?.ledger_name ?? (roundoffLedger?.company || 'Roundoff'),
        amount: roundoffAmt,
        auto: false,  // editable
        search: existing?.search ?? (roundoffLedger?.company || 'Roundoff'),
        open: false,
      }];
    });
  // userRoundoffRow is a dependency so adding a Round Off row with a zero
  // amount — which leaves rawTotal untouched — still clears any auto row that
  // is already there. It cannot loop: the flag ignores the auto row itself.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTotal, isAccountingInvoice, userRoundoffRow]);

  // ----- Line item updates -----
  const updateLine = useCallback((idx: number, field: keyof LineItem, value: any) => {
    setLines(prev => {
      const updated = [...prev];
      let line = { ...updated[idx], [field]: value };
      if (field === 'product_id') {
        const prod = products.find(p => String(p.id) === String(value));
        if (prod) { line.item_name = prod.item_name; line.gst_rate = Number(prod.gst) || 0; }
      }
      if (field === 'amount') {
        line = calcLineFromAmount(line, isIgst, Number(value) || 0);
      } else if (['product_id', 'qty', 'rate', 'gst_rate'].includes(field as string)) {
        line = calcLine(line, isIgst);
      }
      updated[idx] = line;
      return updated;
    });
  }, [products, isIgst]);

  const addRow    = () => setLines(p => [...p, emptyLine()]);
  const removeRow = (idx: number) => { if (lines.length > 1) setLines(p => p.filter((_, i) => i !== idx)); };

  // ----- Ledger row management -----
  const addLedgerRow = () => setLedgerRows(p => [...p, {
    id: uid(), ledger_id: null, ledger_name: '', amount: 0, auto: false, search: '', open: false,
  }]);

  const removeLedgerRow = (id: string) => setLedgerRows(p => p.filter(r => r.id !== id));

  const updateLedgerRow = (id: string, patch: Partial<LedgerRow>) =>
    setLedgerRows(p => p.map(r => r.id === id ? { ...r, ...patch } : r));

  const selectLedger = (rowId: string, l: any) =>
    updateLedgerRow(rowId, { ledger_id: l.id, ledger_name: l.company, search: l.company, open: false });

  // Auto-focus the first interactive input inside a popup when it opens, so
  // keyboard-only users don't have to mouse into the modal.
  useEffect(() => {
    if (batchPopupIdx === null) return;
    setTimeout(() => {
      const el = batchPopupRef.current?.querySelector('input:not([disabled]):not([type="hidden"])') as HTMLInputElement | null;
      el?.focus();
    }, 50);
  }, [batchPopupIdx]);

  useEffect(() => {
    if (!showNewCustomer) return;
    setTimeout(() => {
      const el = newCustomerPopupRef.current?.querySelector('input:not([disabled]):not([type="hidden"])') as HTMLInputElement | null;
      el?.focus();
    }, 50);
    // Ledger group options for the new party (default Sundry Debtors)
    if (ledgerGroups.length === 0) {
      ledgerGroupApi.getAll()
        .then(res => { if (res.success) setLedgerGroups(res.data || []); })
        .catch(() => { /* dropdown just stays with the default */ });
    }
  }, [showNewCustomer]);

  useEffect(() => {
    if (!billAllocOpen) return;
    setTimeout(() => {
      const el = billAllocPopupRef.current?.querySelector('input:not([disabled]):not([type="hidden"])') as HTMLInputElement | null;
      el?.focus();
    }, 50);
  }, [billAllocOpen]);

  useEffect(() => {
    if (!cloudPopup) return;
    setTimeout(() => {
      const el = cloudPopupRef.current?.querySelector('input[type="checkbox"]:not([disabled])') as HTMLInputElement | null;
      el?.focus();
    }, 50);
  }, [cloudPopup?.lineIdx]);

  // Helper: open the cloud picker for a given voucher line.
  // Credit Note → fetch purchase activities (server-mapped to customer).
  // All other types → fetch billing activities (Sales) for the customer.
  const openCloudPopup = async (lineIdx: number) => {
    if (!partyId) {
      showError('Pick customer first', 'Select the customer before adding a Cloud-category item.');
      return;
    }
    const isCreditNote = voucherType.toLowerCase().includes('credit');
    setCloudPopup({ lineIdx, activities: [], selectedIds: new Set(), loading: true, isCreditNote });
    try {
      // When editing an existing voucher, pass its id so the backend also
      // returns activities already linked to THIS voucher (not just
      // unbilled ones) — otherwise re-opening this popup on an edit makes
      // the previously-picked activities vanish (they read as "billed"),
      // and the user loses track of what was selected before.
      // Credit Note → only activities generated as bill_type='Credit Note'
      // (e.g. a mid-cycle user decrease). Everything else → only
      // bill_type='Tax Invoice' activities, so the two never mix.
      const res = isCreditNote
        ? await activitiesApi.getPendingCreditNoteByCustomer(partyId, editId ?? undefined)
        : await activitiesApi.getPendingByCustomer(partyId, editId ?? undefined);
      const list = res.success ? res.data : [];
      const preSelected = new Set(
        list.filter((a: any) => editId && String(a.voucher_id) === String(editId)).map((a: any) => String(a.id))
      );
      setCloudPopup(prev => prev && prev.lineIdx === lineIdx
        ? { ...prev, activities: list, selectedIds: preSelected, loading: false }
        : prev);
    } catch (e: any) {
      showError('Error', e.message || 'Failed to load pending activities');
      setCloudPopup(null);
    }
  };

  // Confirm selection → fill the line with the summed amount, mark
  // activity ids for post-save linking.
  const applyCloudSelection = () => {
    if (!cloudPopup) return;
    const selected = cloudPopup.activities.filter(a => cloudPopup.selectedIds.has(String(a.id)));
    if (!selected.length) {
      showError('No selection', 'Select at least one activity to bill.');
      return;
    }
    // bill_amount is negative on Credit Note activities (a mid-cycle
    // reduction). Voucher line items are entered as POSITIVE values — the
    // Credit Note voucher TYPE itself drives the Dr/Cr treatment, not a
    // negative amount on the line. Tax Invoice rows are already positive
    // and are left untouched.
    const getActivityAmt = (a: any) => {
      const amt = Number(a.bill_amount || 0);
      return cloudPopup.isCreditNote ? Math.abs(amt) : amt;
    };
    const total = +selected.reduce((s, a) => s + getActivityAmt(a), 0).toFixed(2);
    // For Cloud lines: qty=1, rate=total, amount=total. Internally consistent
    // with inventory_entries.qty/rate (which are NOT NULL), but the user only
    // supplies the amount.
    setLines(prev => {
      const updated = [...prev];
      updated[cloudPopup.lineIdx] = calcLine({
        ...updated[cloudPopup.lineIdx],
        qty: 1,
        rate: total,
        amount: total,
      }, isIgst);
      return updated;
    });
    setActivitiesToLink(prev => Array.from(new Set([...prev, ...selected.map(a => String(a.id))])));
    // Auto-write the voucher remark from the picked activities so the
    // printed voucher itself says exactly what was billed:
    // type, bill type, cycle, mode, start → expiry, users @ rate = amount.
    const fmtD = (s: any) => {
      if (!s) return '';
      const d = new Date(String(s).replace(' ', 'T'));
      return isNaN(d.getTime()) ? String(s) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    };
    const pretty = (s: any) => String(s || '').replace(/_/g, ' ');
    const autoRemark = selected.map(a => [
      a.activity_type || 'Renewal',
      a.bill_type || '',
      a.billing_cycle || '',
      pretty(a.billing_mode),
      [fmtD(a.start_from), fmtD(a.new_expiry_date)].filter(Boolean).join(' to '),
      `${cloudPopup.isCreditNote ? Math.abs(Number(a.billing_units) || 0) : (Number(a.billing_units) || 0)} users @ ${Number(a.last_bill_rate) || 0} = ${Number(getActivityAmt(a)).toLocaleString('en-IN')}`,
    ].filter(Boolean).join(', ')).join(' || ');
    if (autoRemark) setRemark(autoRemark);
    setCloudPopup(null);
  };

  const toggleCloudActivity = (id: string) => {
    setCloudPopup(prev => {
      if (!prev) return prev;
      const next = new Set(prev.selectedIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...prev, selectedIds: next };
    });
  };

  // ----- Batch popup helpers -----
  const openBatchPopup = async (idx: number, overrideProductId?: string) => {
    const existing = lines[idx].batch_rows;
    const draft = existing?.length
      // `id: r.id || uid()` is a safety net, not decoration: every edit handler
      // in this popup targets `r.id === row.id`, so two rows sharing an id (or
      // both being undefined) makes one keystroke rewrite both.
      ? existing.map(r => ({ ...r, id: r.id || uid(), serialSearch: r.batch_name, serialOpen: false }))
      : [{ id: uid(), batch_name: '', qty: 0, rate: 0, amount: 0, serialSearch: '', serialOpen: false }];
    setBatchDraft(draft);
    setBatchPopupIdx(idx);
    setBatchSerials([]);

    // Sales/Credit Note: fetch existing serial nos for search-and-select
    // Purchase/Debit Note: user types manually — no fetch needed
    setBatchNoFlavour(false);
    if (!isPurchaseMode && partyId) {
      const resolvedProductId = overrideProductId ?? lines[idx].product_id;
      const prod = products.find((p: any) => String(p.id) === String(resolvedProductId));
      const flavourId = prod?.tally_flavour_id || undefined;
      if (!flavourId) {
        // Item has no flavour configured — cannot filter serials by product
        setBatchNoFlavour(true);
        setBatchSerials([]);
      } else {
        try {
          const res = await vouchersApi.getSerials(parseInt(partyId, 10), flavourId);
          if (res.success) setBatchSerials(res.data);
        } catch { setBatchSerials([]); }
      }
    }
  };

  const saveBatch = () => {
    if (batchPopupIdx === null) return;
    // Reject empty serial/batch numbers right here so the user can't dismiss
    // the popup with a blank field and end up with a batch=Yes line that
    // has no traceability after save.
    const targetLine = lines[batchPopupIdx];
    const prod = targetLine ? products.find((p: any) => String(p.id) === String(targetLine.product_id)) : null;
    if (prod?.batch === 'Yes') {
      // Allow rows that have qty>0 OR amount>0 — qty is optional when amount is entered directly
      const rowsWithValue = batchDraft.filter(r => r.qty > 0 || r.amount > 0);
      if (rowsWithValue.length === 0) {
        showError('Validation', `"${prod.item_name}" requires at least one batch row with a serial number and an amount.`);
        return;
      }
      const blankIdx = rowsWithValue.findIndex(r => !(r.batch_name || '').trim());
      if (blankIdx !== -1) {
        showError('Validation', `Serial / batch number is empty for row ${blankIdx + 1}. Enter the serial before saving.`);
        return;
      }
    }
    // Accept rows with qty>0 OR amount>0 (serial-only with a direct amount is valid)
    const valid = batchDraft.filter(r => r.qty > 0 || r.amount > 0);
    const totalQty    = +valid.reduce((s, r) => s + r.qty, 0).toFixed(3);
    const totalAmount = +valid.reduce((s, r) => s + r.amount, 0).toFixed(2);
    const avgRate     = totalQty > 0 ? +(totalAmount / totalQty).toFixed(4) : 0;
    setLines(prev => {
      const updated = [...prev];
      const base = { ...updated[batchPopupIdx], qty: totalQty, rate: avgRate };
      // When qty=0 but amount>0, apply amount directly so it isn't computed as qty*rate=0
      const line = totalQty === 0 && totalAmount > 0
        ? { ...calcLineFromAmount(base, isIgst, totalAmount), batch_rows: valid }
        : { ...calcLine(base, isIgst), batch_rows: valid };
      updated[batchPopupIdx] = line;
      return updated;
    });
    setBatchPopupIdx(null);
    setBatchDraft([]);
  };

  // ----- New customer -----
  const handleCreateCustomer = async () => {
    if (!custForm.company.trim()) { showError('Validation', 'Company name is required'); return; }
    if (!custForm.mobile.trim())  { showError('Validation', 'Mobile is required'); return; }
    setCreatingCustomer(true);
    try {
      const res = await customersApi.create({
        company: custForm.company.trim(), mobile: custForm.mobile.trim(),
        gstin: custForm.gstin.trim() || undefined, email: custForm.email.trim() || undefined,
        pincode: custForm.pincode.trim() || undefined, address1: custForm.address1.trim() || undefined,
        address2: custForm.address2.trim() || undefined, area: custForm.area.trim() || undefined,
        state: custForm.state.trim() || undefined, status: 'Active',
        ledgergroup: custForm.ledgergroup || SUNDRY_DEBTORS_ID,
      } as any);
      if (res.success && res.data) {
        setPartyDisplay(res.data.company || custForm.company);
        setPartyId(String(res.data.id));
        const st = readableStateName(res.data.state_name, res.data.state, custForm.state);
        setPartyState(st);
        setIsIgst(isInterStateParty(st, res.data.gstin || custForm.gstin));
        setShowNewCustomer(false);
        setCustForm(blankCustForm());
        showSuccess('Created', 'Customer created');
      }
    } catch (e: any) { showError('Error', e.message || 'Failed'); }
    finally { setCreatingCustomer(false); }
  };

  // ----- Journal totals -----
  const journalDrTotal = +journalRows.filter(r => r.drOrCr === 'Dr').reduce((s, r) => s + r.dr_amount, 0).toFixed(2);
  const journalCrTotal = +journalRows.filter(r => r.drOrCr === 'Cr').reduce((s, r) => s + r.cr_amount, 0).toFixed(2);
  const journalBalanced = Math.abs(journalDrTotal - journalCrTotal) < 0.01;

  // The row whose bill-allocation popup is currently open (per-row model).
  const activeJournalRow = activeJournalRowId ? journalRows.find(r => r.id === activeJournalRowId) ?? null : null;
  const activeRowAmount = activeJournalRow ? (activeJournalRow.drOrCr === 'Dr' ? activeJournalRow.dr_amount : activeJournalRow.cr_amount) : 0;

  const effectiveGrandTotal = isJournalType
    ? (activeJournalRow ? activeRowAmount : journalDrTotal)
    : grandTotal;

  // Bill allocation required if ANY row has ledger (bill-by-bill rows need popup; non-bill-by-bill get auto On Account)
  const journalBillByBill = isJournalType && journalRows.some(r => r.ledger_id && (r.dr_amount > 0 || r.cr_amount > 0));

  // ----- Bill allocation helpers -----
  // Party direction determines sign of grand total
  // Journal: use the active row's drOrCr | Normal: Purchase/Debit Note → Cr, Sales/Credit Note → Dr
  const partyDir: 'Dr' | 'Cr' = isJournalType
    ? (activeJournalRow ? activeJournalRow.drOrCr : 'Cr')
    : customerPartyDir;
  const signedGrandTotal = partyDir === 'Dr' ? effectiveGrandTotal : -effectiveGrandTotal;

  // Signed sum: Cr entries = negative, Dr entries = positive
  const billAllocSigned = +billAllocEntries.reduce((s, e) => {
    const amt = Number(e.amount) || 0;
    return s + (e.direction === 'Cr' ? -amt : amt);
  }, 0).toFixed(2);

  // Balance = signedGrandTotal − billAllocSigned → 0 when fully allocated
  const billAllocBalance  = +(signedGrandTotal - billAllocSigned).toFixed(2);
  const billAllocTotal    = Math.abs(billAllocSigned); // absolute for display
  const billAllocBalanced = !(customerBillByBill || journalBillByBill) || Math.abs(billAllocBalance) < 0.01;

  const isRowBillAllocBalanced = (r: JournalRow) => {
    const amt = r.drOrCr === 'Dr' ? r.dr_amount : r.cr_amount;
    const signed = r.drOrCr === 'Dr' ? amt : -amt;
    const allocSigned = r.billAlloc.reduce((s, e) => s + (e.direction === 'Cr' ? -(Number(e.amount) || 0) : (Number(e.amount) || 0)), 0);
    return Math.abs(signed - allocSigned) < 0.01;
  };

  // Whether ALL bill-by-bill rows are individually balanced (used to gate submit)
  const allRowsBillAllocBalanced = !journalBillByBill || journalRows.filter(r => r.billByBill && r.ledger_id && (r.dr_amount > 0 || r.cr_amount > 0)).every(isRowBillAllocBalanced);

  // First bill-by-bill row that still needs allocation
  const firstBillAllocRowId = (() => {
    const billByBillRows = journalRows.filter(r => r.billByBill && r.ledger_id && (r.dr_amount > 0 || r.cr_amount > 0));
    if (billByBillRows.length === 0) return null;
    const unbalanced = billByBillRows.find(r => !isRowBillAllocBalanced(r));
    return (unbalanced || billByBillRows[0]).id;
  })();

  // Opens the bill-alloc popup for the first row that needs it (journal mode)
  // or the single customer-mode popup. Used by summary/preview screens that
  // don't have a specific row in context.
  const openBillAllocAny = () => {
    if (isJournalType) {
      if (firstBillAllocRowId) openBillAlloc(firstBillAllocRowId);
    } else {
      openCustomerBillAlloc();
    }
  };

  // Auto-fill "On Account" for non-bill-by-bill rows silently (no popup needed)
  const autoFillOnAccount = (rowId: string, amt: number, dir: 'Dr' | 'Cr') => {
    setJournalRows(p => p.map(r => {
      if (r.id !== rowId) return r;
      const entry = { id: uid(), type: 'On Account' as const, refno: '', amount: amt, direction: dir };
      return { ...r, billAlloc: [entry] };
    }));
  };

  // Non-journal (Sales/Purchase/etc.) single-party bill allocation — unchanged
  // from the original single-party model since customers only have ONE party ledger.
  const openCustomerBillAlloc = async () => {
    const snapGrandTotal = grandTotal;
    const snapPartyDir: 'Dr' | 'Cr' = customerPartyDir;
    if (billAllocEntries.length === 0) {
      setBillAllocEntries([{ id: uid(), type: 'New', refno: voucherNo || '', amount: snapGrandTotal, direction: snapPartyDir }]);
    }
    setBillAllocOpen(true);
    const lookupId = parseInt(partyId, 10);
    if (lookupId) {
      try {
        setPendingRefsDir('Cr');
        const excludeVchId = editVoucherData?.id ? Number(editVoucherData.id) : undefined;
        const res = await vouchersApi.getPendingRefs(lookupId, 'Cr', excludeVchId);
        if (res.success) setPendingRefs(res.data);
      } catch { setPendingRefs([]); }
    }
  };

  const openBillAlloc = async (rowId: string) => {
    const row = journalRows.find(r => r.id === rowId);
    if (!row) return;
    const rowAmount = row.drOrCr === 'Dr' ? row.dr_amount : row.cr_amount;
    const rowDir: 'Dr' | 'Cr' = row.drOrCr;
    setActiveJournalRowId(rowId);
    const existing = row.billAlloc.length > 0
      ? row.billAlloc
      : [{ id: uid(), type: 'New' as const, refno: voucherNo || '', amount: rowAmount, direction: rowDir }];
    setBillAllocEntries(existing);
    setBillAllocOpen(true);
    // For journal mode use this row's own ledger; otherwise use partyId
    const lookupId = isJournalType ? row.ledger_id : parseInt(partyId, 10);
    if (lookupId) {
      try {
        // Direction: Cr row = settling outstanding bills (show positive pending)
        //            Dr row = settling credit notes (show negative pending)
        const direction = isJournalType ? rowDir : 'Cr';
        setPendingRefsDir(direction);
        // When editing, exclude this voucher's own allocations from the netting
        // so the bill it currently settles still appears as pending.
        const excludeVchId = editVoucherData?.id ? Number(editVoucherData.id) : undefined;
        const res = await vouchersApi.getPendingRefs(lookupId, direction, excludeVchId);
        if (res.success) {
          setPendingRefs(res.data);
          // For journal-type vouchers (Receipt/Payment/Journal/Contra), auto-select
          // the best matching pending bill so users don't have to manually switch
          // New→Agr. every time. Only applies to the freshly-created single 'New' entry.
          if (isJournalType && res.data.length > 0) {
            setBillAllocEntries(prev => {
              if (prev.length !== 1 || prev[0].type !== 'New') return prev;
              // Prefer a bill whose amount exactly matches; fall back to the first.
              const exactMatch = res.data.find((p: any) => Math.abs(Number(p.amount) - rowAmount) < 0.01);
              const pick = exactMatch || res.data[0];
              const billDir = pick.direction || (Number(pick.amount) > 0 ? 'Dr' : 'Cr');
              const settleDir: 'Dr' | 'Cr' = billDir === 'Dr' ? 'Cr' : 'Dr';
              const autoAmt = +Math.min(Number(pick.amount), rowAmount).toFixed(2);
              return [{ ...prev[0], type: 'Agr.', refno: pick.billname, refSearch: pick.billname, amount: autoAmt, direction: settleDir }];
            });
          }
        }
      } catch { setPendingRefs([]); }
    }
  };

  const closeBillAlloc = () => { setBillAllocOpen(false); setActiveJournalRowId(null); };

  // ── Two-partition bill picker helpers (Pending ⇄ Selected) ──
  // Click a PENDING bill → add it as an Agr. allocation (auto amount = min of
  // the bill's balance and the still-unallocated amount). Dropping any empty
  // placeholder "New" row keeps the total from over-allocating.
  const pickPendingBill = (p: { billname: string; amount: number; direction?: string }) => {
    setBillAllocEntries(d => {
      if (d.some(r => r.refno === p.billname)) return d; // already selected
      const base = d.filter(r => r.refno || r.type === 'On Account');
      const otherAllocated = base.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      const remaining = Math.max(0, effectiveGrandTotal - otherAllocated);
      const autoAmount = +Math.min(Number(p.amount), remaining).toFixed(2);
      const billDir = p.direction || (Number(p.amount) > 0 ? 'Dr' : 'Cr');
      const settleDir = billDir === 'Dr' ? 'Cr' : 'Dr';
      return [...base, { id: uid(), type: 'Agr.' as const, refno: p.billname, refSearch: p.billname, amount: autoAmount, direction: settleDir, refOpen: false }];
    });
  };
  // Click a SELECTED bill → remove that allocation (keep at least one row).
  const removeSelectedBill = (entryId: string) => {
    setBillAllocEntries(d => {
      const next = d.filter(r => r.id !== entryId);
      if (next.length) return next;
      const dir = signedGrandTotal >= 0 ? 'Dr' : 'Cr';
      return [{ id: uid(), type: 'New' as const, refno: '', amount: Math.abs(signedGrandTotal), direction: dir }];
    });
  };

  // MANUAL: reset to a clean single Agr. row so the click-to-pick panel shows.
  const setManualAllocation = () => {
    setBillAllocEntries([{ id: uid(), type: 'Agr.' as const, refno: '', refSearch: '', amount: 0, direction: partyDir, refOpen: false }]);
  };
  // AUTO (FIFO): settle the voucher amount against the oldest open bills this
  // voucher offsets (settle direction === party direction), oldest first, until
  // the amount is consumed; any leftover goes On Account.
  const autoAllocateFifo = () => {
    const target = effectiveGrandTotal;
    let remaining = target;
    const sorted = pendingRefs.slice().sort((a, b) => {
      const ta = a.vch_date ? new Date(a.vch_date).getTime() : Infinity;
      const tb = b.vch_date ? new Date(b.vch_date).getTime() : Infinity;
      return ta - tb;
    });
    const entries: typeof billAllocEntries = [];
    for (const p of sorted) {
      if (remaining <= 0.01) break;
      const billDir = p.direction || (Number(p.amount) > 0 ? 'Dr' : 'Cr');
      const settleDir = billDir === 'Dr' ? 'Cr' : 'Dr';
      if (settleDir !== partyDir) continue; // only bills this payment offsets
      const amt = +Math.min(Number(p.amount), remaining).toFixed(2);
      if (amt <= 0) continue;
      entries.push({ id: uid(), type: 'Agr.', refno: p.billname, refSearch: p.billname, amount: amt, direction: settleDir, refOpen: false });
      remaining = +(remaining - amt).toFixed(2);
    }
    if (remaining > 0.01) {
      entries.push({ id: uid(), type: 'On Account', refno: '', amount: remaining, direction: partyDir, refOpen: false });
    }
    setBillAllocEntries(entries.length ? entries : [{ id: uid(), type: 'New', refno: voucherNo || '', amount: target, direction: partyDir }]);
  };

  // Persist the popup's working state back onto the active row's own billAlloc
  // array so each bill-by-bill row keeps independent allocations.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!activeJournalRowId) return;
    setJournalRows(prev => prev.map(r => r.id === activeJournalRowId ? { ...r, billAlloc: billAllocEntries } : r));
  }, [billAllocEntries, activeJournalRowId]);

  // Keep a single 'New' bill alloc entry in sync when the active row's amount changes.
  // This prevents the balance going stale if the user adjusts the amount after
  // bill alloc was auto-populated (only applies to the auto-created New entry).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!activeJournalRow) return;
    setBillAllocEntries(prev => {
      if (prev.length !== 1 || prev[0].type !== 'New') return prev;
      const newAmt = activeRowAmount;
      const newDir = partyDir;
      if (Math.abs((prev[0].amount || 0) - newAmt) < 0.01 && prev[0].direction === newDir) return prev;
      return [{ ...prev[0], amount: newAmt, direction: newDir }];
    });
  }, [activeRowAmount, partyDir, activeJournalRow]);

  // ----- Submit -----
  /** Uploads documents staged while the voucher had no id yet. Called from all
   *  three save branches once the response is in. A failed upload never undoes
   *  a saved voucher — the user is told to re-attach from the saved entry. */
  const flushAttachments = async (res: any) => {
    if (!pendingAttachments.length) return;
    const savedId = (res as any)?.data?.id ?? (typeof editId === 'number' ? editId : undefined);
    if (!savedId) return;
    const uploaded = await uploadStagedAttachments(savedId, pendingAttachments);
    if (uploaded) setPendingAttachments([]);
    else showError('Warning', 'Voucher saved, but the attached document(s) could not be uploaded. Open the voucher and attach them again.');
  };

  const handleSubmit = async () => {
    if (isStockJournal) {
      const validSrc  = stockSource.filter(l => l.item_id && l.qty > 0);
      const validDest = stockDest.filter(l => l.item_id && l.qty > 0);
      if (validSrc.length === 0 && validDest.length === 0) {
        showError('Validation', 'Add at least one item in Source or Destination');
        return;
      }
      setSubmitting(true);
      try {
        const vtId = childTypes.find(t => t.name === voucherType)?.id || selectedParentId;
        const payload: any = {
          vch_type_id: vtId,
          vch_no: voucherNo || undefined,
          vch_date: voucherDate || undefined,
          remark: remark.trim() || undefined,
          stock_source: validSrc.map(l => ({ item_id: Number(l.item_id), qty: l.qty, rate: l.rate, amount: l.amount, gst_rate: l.gst_rate, batch_rows: l.batch_rows })),
          stock_destination: validDest.map(l => ({ item_id: Number(l.item_id), qty: l.qty, rate: l.rate, amount: l.amount, gst_rate: l.gst_rate, batch_rows: l.batch_rows })),
        };
        const res = editId ? await vouchersApi.update(editId, payload) : await vouchersApi.create(payload);
        if (res.success) {
          await flushAttachments(res);
          const sjData = (res as any)?.data;
          if (sjData?.vch_no_bumped && sjData?.vch_no) {
            showError('Voucher No. Changed', `Number already taken by another user. Saved as ${sjData.vch_no}`);
          } else {
            showSuccess('Saved', res.message || (editId ? 'Stock Journal updated' : 'Stock Journal created'));
          }
          setStockSource([emptyStockLine()]);
          setStockDest([emptyStockLine()]);
          if (!editId && vtId) {
            const newNo = await vouchersApi.getNextNo(vtId, voucherDate).catch(() => null);
            if (newNo?.success) setVoucherNo(newNo.data ?? '');
          }
        } else showError('Error', res.message || 'Failed to save');
      } catch (e: any) { showError('Error', e?.message || 'Failed'); }
      finally { setSubmitting(false); }
      return;
    }

    if (isJournalType) {
      // Reject any row that has either typed text without a selection OR an
      // amount entered without a ledger picked. Both indicate an incomplete
      // line that must not be saved.
      const orphanText = journalRows.find(r => !r.ledger_id && r.search.trim());
      if (orphanText) {
        showError('Validation', `"${orphanText.search.trim()}" is not a valid ledger. Pick one from the search dropdown.`);
        return;
      }
      const amountWithoutLedger = journalRows.find(r => !r.ledger_id && (r.dr_amount > 0 || r.cr_amount > 0));
      if (amountWithoutLedger) {
        const idx = journalRows.indexOf(amountWithoutLedger) + 1;
        showError('Validation', `Row ${idx}: amount entered but no ledger selected. Pick a ledger from the search dropdown first.`);
        return;
      }
      const validRows = journalRows.filter(r => r.ledger_id && (r.dr_amount > 0 || r.cr_amount > 0));
      if (validRows.length === 0) { showError('Validation', 'Add at least one ledger entry'); return; }
      // If any row has a ledger but no amount → treat as incomplete.
      const blankAmt = journalRows.find(r => r.ledger_id && r.dr_amount === 0 && r.cr_amount === 0);
      if (blankAmt) {
        showError('Validation', `Enter an amount for ${blankAmt.ledger_name || 'the selected ledger'}.`);
        return;
      }
      if (!journalBalanced) { showError('Validation', 'Dr total must equal Cr total'); return; }
      if (journalBillByBill && !allRowsBillAllocBalanced) { showError('Validation', 'Complete bill allocation for every bill-by-bill ledger — each must total its own Dr/Cr amount'); return; }

      // Party-row picker is voucher-type aware:
      //   Receipt  → party (customer)  is on Cr (we receive money from them)
      //   Payment  → party (supplier)  is on Dr (we pay them)
      //   Journal/Contra → either side; fall back to first bill-by-bill row or first Dr row
      // For Receipt/Payment we IGNORE a bill-by-bill row when it sits on the wrong
      // side — that situation arises when the bank ledger also has
      // billbybill='Yes' and the auto-detector fires while the user is filling
      // the bank row. The party MUST be the customer/supplier ledger, so we
      // pick the first row on the correct side regardless of billByBill.
      const parentNameLc = (allVchTypes.find(t => t.id === selectedParentId)?.name || '').toLowerCase();
      const isReceipt = parentNameLc.includes('receipt');
      const isPayment = parentNameLc.includes('payment');
      const defaultPartySide: 'Dr' | 'Cr' = isReceipt ? 'Cr' : 'Dr';
      const billByBillRow = validRows.find(r => r.billByBill) || null;
      const billByBillRowOnRightSide = billByBillRow?.drOrCr === defaultPartySide;
      const useBillByBillRow = billByBillRow && (
        (!isReceipt && !isPayment) || billByBillRowOnRightSide
      );
      const partyRow = useBillByBillRow
        ? (billByBillRow || validRows[0])
        : (validRows.find(r => r.drOrCr === defaultPartySide) || validRows[0]);
      setSubmitting(true);
      try {
        const payload = {
          vch_type_id: (childTypes.find(t => t.name === voucherType)?.id || selectedParentId) || null,
          vch_no:          voucherNo || null,
          vch_date:        voucherDate || null,
          remark:          remark.trim() || null,
          party_ledger_id: partyRow.ledger_id!,
          items:           [],
          ledgers: validRows.map(r => ({
            ledger_id: r.ledger_id!,
            amount: r.drOrCr === 'Dr' ? r.dr_amount : -r.cr_amount,
          })),
          bill_allocation: journalBillByBill
            ? validRows.flatMap(r => r.billAlloc.map(e => ({
                type: e.type, refno: e.refno, amount: e.amount, direction: e.direction, ledger_id: r.ledger_id!,
              })))
            : undefined,
        };
        const res = editId
          ? await vouchersApi.update(editId, payload)
          : await vouchersApi.create(payload);
        if (res.success) {
          await flushAttachments(res);
          const wasEdit = !!editId;
          showSuccess('Saved', res.message || (wasEdit ? 'Voucher updated' : 'Voucher created'));
          // After updating an existing voucher, return to wherever the user
          // came from (typically Daybook with its date filter preserved in
          // the URL). For brand-new creates, stay on the form to make the
          // common "save then enter another voucher" flow keystroke-light.
          if (wasEdit) {
            if (navReturnTo) navigate(navReturnTo);
            else if (window.history.length > 1) navigate(-1);
            else navigate('/billing/daybook');
            return;
          }
          setEditId(null);
          setVoucherNo(''); setVoucherDate(new Date().toISOString().slice(0, 10));
          setRemark(''); setBillAllocEntries([]); setCustomerBillByBill(false);
          setJournalRows([emptyJournalRow(), emptyJournalRow()]);
          setActiveJournalRowId(null);
        }
      } catch (e: any) { showError('Error', e.message || 'Failed to save voucher'); }
      finally { setSubmitting(false); }
      return;
    }

    if (!partyId) {
      // Stricter message when the user typed text but never selected from the
      // dropdown — that's the most common cause of an unset partyId.
      if (partyDisplay.trim()) {
        showError('Validation', `"${partyDisplay.trim()}" is not a valid customer. Pick one from the search dropdown (or click + to create a new one).`);
      } else {
        showError('Validation', 'Select a party — field must be green');
      }
      return;
    }
    if (isAccountingInvoice) {
      // Accounting invoice: no items — need at least one ledger with an amount.
      if (!ledgerRows.some(r => r.ledger_id && r.amount !== 0)) {
        showError('Validation', 'Add at least one ledger with an amount');
        return;
      }
    } else {
      if (lines.every(l => !l.product_id)) { showError('Validation', 'Add at least one item'); return; }

      // For every batch-tracked item (item.batch === 'Yes'), the line must
      // carry at least one batch row AND every batch row must have a non-empty
      // serial. Without this, clearing the serial in the popup let the user
      // save an item with no traceability — a Tax Invoice for stock that
      // can't be located in any batch.
      for (let i = 0; i < lines.length; i++) {
        const ln = lines[i];
        if (!ln.product_id) continue;
        const prod = products.find((p: any) => String(p.id) === String(ln.product_id));
        if (!prod || prod.batch !== 'Yes') continue;
        const rows = ln.batch_rows || [];
        if (rows.length === 0) {
          showError('Validation', `Row ${i + 1}: "${prod.item_name}" requires a serial / batch number — open the batch popup and enter one.`);
          return;
        }
        const blank = rows.findIndex(b => !(b.batch_name || '').trim());
        if (blank !== -1) {
          showError('Validation', `Row ${i + 1}: "${prod.item_name}" — serial / batch number is empty for batch row ${blank + 1}.`);
          return;
        }
      }
    }

    // Reject manual ledger rows where the user typed text but didn't pick
    // a ledger, or entered an amount without a ledger. Same rule as journal-mode.
    const orphanLedger = ledgerRows.find(r => !r.ledger_id && r.search.trim());
    if (orphanLedger) {
      showError('Validation', `"${orphanLedger.search.trim()}" is not a valid ledger. Pick one from the search dropdown.`);
      return;
    }
    const amtWithoutLedger = ledgerRows.find(r => !r.ledger_id && r.amount > 0);
    if (amtWithoutLedger) {
      showError('Validation', 'Ledger row: amount entered but no ledger selected. Pick a ledger from the search dropdown first.');
      return;
    }

    if (!isJournalType && !billAllocBalanced) { showError('Validation', 'Complete bill allocation — balance must reach zero'); return; }

    // Filter ledger rows: only non-zero amounts with a ledger selected.
    const validLedgers = ledgerRows
      .filter(r => r.ledger_id && r.amount !== 0)
      .map(r => ({
        ledger_id: r.ledger_id!,
        amount: r.amount,
      }));


    setSubmitting(true);
    try {
      const payload = {
        vch_type_id: (childTypes.find(t => t.name === voucherType)?.id || selectedParentId) || null,
        vch_no:          voucherNo || null,
        vch_date:        voucherDate || null,
        remark:          remark.trim() || null,
        party_ledger_id: parseInt(partyId, 10),
        is_igst:         isIgst,
        // Accounting Invoice: no items — party is posted the ledger total.
        accounting_invoice: isAccountingInvoice || undefined,
        items: isAccountingInvoice ? [] : lines.filter(l => l.product_id).map(l => ({
          item_id:     Number(l.product_id),
          qty:         l.qty,
          rate:        l.rate,
          amount:      l.amount,
          cgst_amount: l.cgst_amount,
          sgst_amount: l.sgst_amount,
          igst_amount: l.igst_amount,
          batch_rows:  l.batch_rows?.length
            ? l.batch_rows.map(b => ({ batch_name: b.batch_name || null, qty: b.qty, rate: b.rate, amount: b.amount }))
            : null,
        })),
        ledgers: validLedgers,
        bill_allocation: customerBillByBill ? billAllocEntries.map(e => ({ type: e.type, refno: e.refno, amount: e.amount, direction: e.direction })) : undefined,
      };

      const res = editId
        ? await vouchersApi.update(editId, payload)
        : await vouchersApi.create(payload);
      if (res.success) {
        await flushAttachments(res);
        // Link any cloud_activities the user picked to this voucher.
        // We pass voucher_id (FK) so the linkage survives a future vch_no
        // rename — the activities listing JOINs back to vch_details to
        // surface the live number. voucher_no is also sent as a cache hint.
        const savedVoucherId =
          (res as any)?.data?.id ??
          (typeof editId === 'number' ? editId : undefined);
        if (activitiesToLink.length && (savedVoucherId || voucherNo)) {
          try {
            await activitiesApi.markBilled(activitiesToLink, {
              voucherId: savedVoucherId,
              voucherNo: voucherNo || undefined,
            });
          } catch (e: any) {
            showError('Warning', e.message || 'Voucher saved, but failed to link cloud activities. Update them manually.');
          }
        }
        const wasEdit = !!editId;
        const savedData = (res as any)?.data;
        if (savedData?.vch_no_bumped && savedData?.vch_no) {
          showError('Voucher No. Changed', `Number already taken by another user. Saved as ${savedData.vch_no}`);
        } else {
          showSuccess('Saved', res.message || (wasEdit ? 'Voucher updated' : 'Voucher created'));
        }
        // After updating an existing voucher, return to wherever the user
        // came from (typically Daybook with its date filter preserved in
        // the URL). New creates stay on the form for fast successive entry.
        if (wasEdit) {
          if (navReturnTo) navigate(navReturnTo);
          else if (window.history.length > 1) navigate(-1);
          else navigate('/billing/daybook');
          return;
        }
        setEditId(null);
        setLines([emptyLine()]); setVoucherNo('');
        setVoucherDate(new Date().toISOString().slice(0, 10));
        setPartyDisplay(''); setPartyId(''); setPartyState(''); setIsIgst(false);
        setLedgerRows([]); setRemark('');
        setCustomers([]); setBillAllocEntries([]); setCustomerBillByBill(false);
        setActivitiesToLink([]);
        // Re-fetch next auto number for the next voucher
        const vtId = childTypes.find(t => t.name === voucherType)?.id || selectedParentId;
        if (vtId) {
          vouchersApi.getNextNo(vtId, voucherDate).then((r: any) => { if (r?.success) setVoucherNo(r.data ?? ''); }).catch(() => {});
        }
      }
    } catch (e: any) { showError('Error', e.message || 'Failed to save voucher'); }
    finally { setSubmitting(false); }
  };
  // Keep the ref in sync so the global keyboard shortcut (Alt+S) always
  // calls the latest version of handleSubmit.
  handleSubmitRef.current = handleSubmit;

  // fmt is defined at module level

  // Sundry Debtors (party customers) shouldn't appear in the items-mode
  // Add Ledger dropdown — they belong in the Customer Name field at the
  // top, not as contra ledgers on the same voucher. Hide them here.
  const ledgerOptions = (search: string) =>
    allLedgers
      .filter(l => l.ledgergroup !== SUNDRY_DEBTORS_ID)
      .filter(l => (l.company || '').toLowerCase().includes(search.toLowerCase()))
      .slice(0, 20);

  // CGST/SGST/IGST are auto-posted from item tax rates on Sales/Purchase/
  // Tax Invoice vouchers — they don't apply to Journal/Payment/Receipt/
  // Contra, so hide them from the ledger search on those voucher types.
  const TAX_LEDGER_NAMES = ['cgst', 'sgst', 'igst'];
  const filterJournalLedgers = (list: any[]) =>
    list.filter(l => !TAX_LEDGER_NAMES.includes((l.company || '').trim().toLowerCase()));

  const mobileParentColor = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('sales'))    return 'border-emerald-300 bg-emerald-50 text-emerald-700';
    if (n.includes('purchase')) return 'border-blue-300 bg-blue-50 text-blue-700';
    if (n.includes('receipt'))  return 'border-green-300 bg-green-50 text-green-700';
    if (n.includes('payment'))  return 'border-orange-300 bg-orange-50 text-orange-700';
    if (n.includes('journal'))  return 'border-purple-300 bg-purple-50 text-purple-700';
    if (n.includes('contra'))   return 'border-slate-300 bg-slate-50 text-slate-700';
    if (n.includes('credit'))   return 'border-teal-300 bg-teal-50 text-teal-700';
    if (n.includes('debit'))    return 'border-red-300 bg-red-50 text-red-700';
    return 'border-gray-200 bg-white text-gray-700';
  };

  return (
    <>
      <div className="print:hidden">
    {/* ── Mobile Wizard (small screens only) ── */}
    <div className="block md:hidden min-h-screen bg-gray-50 pb-24">
      {readOnly && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-sm text-center py-1.5 px-4">
          {checkedLockActive
            ? <><strong>Checked & Locked</strong> — only an admin can edit.</>
            : <><strong>View Only</strong> — no edit permission.</>}
        </div>
      )}
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button type="button"
          onClick={() => {
            if (mobileStep === 1) { if (window.history.length > 1) navigate(-1); else navigate('/billing/daybook'); }
            else setMobileStep(s => s - 1);
          }}
          className="p-1 text-gray-500 -ml-1">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-gray-400 leading-none">
            {['Select Type', 'Vch Details', 'Line Details', 'Preview & Save'][mobileStep - 1]}
          </p>
          <p className="text-sm font-semibold text-gray-800 leading-tight truncate">
            {selectedParentId ? (allVchTypes.find(t => t.id === selectedParentId)?.name || 'Voucher') : 'New Voucher'}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className={`w-2 h-2 rounded-full transition-colors ${mobileStep >= s ? 'bg-blue-600' : 'bg-gray-300'}`} />
          ))}
        </div>
      </div>

      {/* Step 1: VCH type selection */}
      {mobileStep === 1 && (
        <div className="p-4">
          <p className="text-sm text-gray-500 mb-4">Select the voucher type to create:</p>
          <div className="grid grid-cols-2 gap-3">
            {systemParents.map(p => (
              <button key={p.id} type="button"
                onClick={() => { setSelectedParentId(p.id); setMobileStep(2); }}
                className={`border-2 rounded-xl py-6 px-3 text-center font-semibold text-sm transition-all active:scale-95 ${
                  selectedParentId === p.id
                    ? 'border-blue-500 bg-blue-600 text-white'
                    : mobileParentColor(p.name)
                }`}>
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Voucher Details */}
      {mobileStep === 2 && (
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Voucher Type</label>
            <select value={voucherType} onChange={e => setVoucherType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg text-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
              {childTypes.length === 0 ? (
                voucherType
                  ? <option value={voucherType}>{voucherType}</option>
                  : <option value="">-- No types --</option>
              ) : (
                childTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)
              )}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Voucher No{isAutomaticVoucherNumbering ? ' (Auto)' : ''}</label>
            <input type="text" value={voucherNo} onChange={e => setVoucherNo(e.target.value)}
              readOnly={readOnly || isAutomaticVoucherNumbering}
              aria-readonly={readOnly || isAutomaticVoucherNumbering}
              title={isAutomaticVoucherNumbering ? 'This voucher type uses automatic numbering.' : undefined}
              placeholder={isAutomaticVoucherNumbering ? 'Generating voucher number...' : 'e.g. S-001'}
              className={`w-full border rounded-lg text-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                isAutomaticVoucherNumbering ? 'border-blue-200 bg-blue-50 text-blue-800 cursor-not-allowed' : 'border-gray-300'
              }`} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Voucher Date</label>
            <input type="date" value={voucherDate} onChange={e => setVoucherDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg text-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          {!isJournalType && (
            <div className="relative">
              <label className="block text-xs text-gray-500 mb-1.5">Customer</label>
              <input type="text"
                value={customerSearch !== '' ? customerSearch : partyDisplay}
                onChange={e => {
                  setCustomerSearch(e.target.value);
                  if (!e.target.value) { setPartyId(''); setPartyDisplay(''); }
                  setShowCustomerDrop(true);
                }}
                onFocus={() => { if (customers.length > 0) setShowCustomerDrop(true); }}
                onBlur={() => setTimeout(() => setShowCustomerDrop(false), 300)}
                placeholder="Search customer..."
                className="w-full border border-gray-300 rounded-lg text-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              {showCustomerDrop && customers.length > 0 && (
                <div className="absolute z-20 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                  {customers.map((c: any) => (
                    <div key={c.id} onPointerDown={() => selectParty(c)}
                      className="px-3 py-2.5 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                      <div className="font-medium text-gray-800">{c.company}</div>
                      {c.mobile && <div className="text-xs text-gray-400">{c.mobile}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <button type="button" onClick={() => setMobileStep(3)}
            className="w-full bg-blue-600 text-white text-sm font-semibold py-3 rounded-xl mt-2">
            Next: Line Details →
          </button>
        </div>
      )}

      {/* Step 3: Line Details */}
      {mobileStep === 3 && (
        <div className="p-4">
          {isJournalType ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-700">Dr / Cr Entries</p>
                {!journalBalanced && journalRows.some(r => r.ledger_id) && (
                  <span className="text-xs text-red-500">Dr {fmt(journalDrTotal)} ≠ Cr {fmt(journalCrTotal)}</span>
                )}
              </div>
              {journalRows.map((row, idx) => (
                <div key={row.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {/* Row 1: Ledger search */}
                  <div className="relative px-3 pt-2.5 pb-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[10px] text-gray-400 font-medium">{idx + 1}</span>
                      {journalRows.length > 1 && (
                        <button type="button" onClick={() => setJournalRows(p => p.filter(r => r.id !== row.id))}
                          className="ml-auto text-red-400 p-0.5"><X size={13} /></button>
                      )}
                    </div>
                    <input type="text" value={row.search}
                      onChange={e => {
                        const q = e.target.value;
                        setJournalRows(p => p.map(r => r.id === row.id ? { ...r, search: q, ledger_id: null, ledger_name: '', open: q.length >= 2 } : r));
                        if (q.length >= 2) {
                          customersApi.searchAllLedgers(q).then((res: any) => {
                            const list = filterJournalLedgers(res?.data || []);
                            setJournalRows(p => p.map(r => r.id === row.id ? { ...r, results: list, open: true } : r));
                          }).catch(() => {});
                        }
                      }}
                      onBlur={() => setTimeout(() => setJournalRows(p => p.map(r => r.id === row.id ? { ...r, open: false } : r)), 300)}
                      placeholder="Search ledger..."
                      className={`w-full border rounded-lg text-sm py-2 px-3 focus:outline-none focus:ring-2 ${
                        row.ledger_id ? 'border-green-400 bg-green-50 focus:ring-green-300'
                          : row.search.trim() ? 'border-red-300 focus:ring-red-300'
                          : 'border-gray-300 focus:ring-blue-400'
                      }`} />
                    {row.open && row.results.length > 0 && (
                      <div className="absolute z-30 left-3 right-3 bg-white border border-gray-200 rounded-lg shadow-lg mt-0.5 max-h-48 overflow-y-auto">
                        {row.results.map((l: any) => (
                          <div key={l.id} onPointerDown={() => {
                            const isBillByBill = l.billbybill === 'Yes';
                            setJournalRows(p => p.map(r => r.id === row.id
                              ? { ...r, ledger_id: l.id, ledger_name: l.company, search: l.company, open: false, billByBill: isBillByBill, billAlloc: isBillByBill ? r.billAlloc : [] }
                              : r));
                          }}
                            className="px-3 py-2.5 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                            {l.company}
                            {l.billbybill === 'Yes' && <span className="ml-1 text-[10px] text-blue-400">bill-by-bill</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Row 2: Dr/Cr toggle + Amount on same line */}
                  <div className="flex items-center gap-0 border-t border-gray-100">
                    <div className="flex border-r border-gray-100">
                      <button type="button"
                        onClick={() => setJournalRows(p => p.map(r => r.id === row.id ? { ...r, drOrCr: 'Dr', cr_amount: 0 } : r))}
                        className={`px-4 py-2.5 text-sm font-bold transition-colors ${row.drOrCr === 'Dr' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500'}`}>Dr</button>
                      <button type="button"
                        onClick={() => setJournalRows(p => p.map(r => r.id === row.id ? { ...r, drOrCr: 'Cr', dr_amount: 0 } : r))}
                        className={`px-4 py-2.5 text-sm font-bold transition-colors ${row.drOrCr === 'Cr' ? 'bg-orange-500 text-white' : 'bg-white text-gray-500'}`}>Cr</button>
                    </div>
                    <input type="number" inputMode="decimal"
                      value={row.drOrCr === 'Dr' ? (row.dr_amount || '') : (row.cr_amount || '')}
                      onChange={e => {
                        const amt = parseFloat(e.target.value) || 0;
                        setJournalRows(p => p.map(r => r.id === row.id
                          ? { ...r, dr_amount: r.drOrCr === 'Dr' ? amt : 0, cr_amount: r.drOrCr === 'Cr' ? amt : 0 }
                          : r));
                      }}
                      onBlur={e => {
                        if (isJournalType) {
                          const rowAmt = parseFloat(e.target.value) || 0;
                          const rowSigned = row.drOrCr === 'Dr' ? rowAmt : -rowAmt;
                          const allocSigned = row.billAlloc.reduce((s, en) => s + (en.direction === 'Cr' ? -(Number(en.amount) || 0) : (Number(en.amount) || 0)), 0);
                          if (rowAmt > 0 && (row.billAlloc.length === 0 || Math.abs(rowSigned - allocSigned) >= 0.01)) {
                            if (row.billByBill) setTimeout(() => openBillAlloc(row.id), 50);
                            else autoFillOnAccount(row.id, rowAmt, row.drOrCr);
                          }
                        }
                      }}
                      placeholder="Amount"
                      className="flex-1 text-sm py-2.5 px-3 text-right focus:outline-none bg-transparent font-semibold text-gray-800 placeholder-gray-300" />
                  </div>
                  {/* Bill allocation status bar — only for bill-by-bill rows */}
                  {row.billByBill && (row.drOrCr === 'Dr' ? row.dr_amount : row.cr_amount) > 0 && (() => {
                    const rowAmt = row.drOrCr === 'Dr' ? row.dr_amount : row.cr_amount;
                    const rowSigned = row.drOrCr === 'Dr' ? rowAmt : -rowAmt;
                    const allocSigned = row.billAlloc.reduce((s, en) => s + (en.direction === 'Cr' ? -(Number(en.amount) || 0) : (Number(en.amount) || 0)), 0);
                    const rowBalanced = Math.abs(rowSigned - allocSigned) < 0.01;
                    return (
                      <button type="button" onClick={() => openBillAlloc(row.id)}
                        className={`w-full flex items-center justify-between px-3 py-1.5 border-t text-xs font-semibold transition-colors ${
                          rowBalanced
                            ? 'border-green-100 bg-green-50 text-green-600'
                            : 'border-orange-200 bg-orange-50 text-orange-600 animate-pulse'
                        }`}>
                        <span>{rowBalanced ? '✓ Bill allocation done' : '⚠ Bill allocation required'}</span>
                        <span className="underline">{rowBalanced ? 'Edit' : 'Tap to Allocate'}</span>
                      </button>
                    );
                  })()}
                </div>
              ))}
              <button type="button" onClick={() => setJournalRows(p => [...p, emptyJournalRow()])}
                className="flex items-center gap-1.5 text-sm text-green-600 py-2 mt-1">
                <Plus size={16} /> Add Row
              </button>
              <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-200 mt-2">
                <span className="text-blue-600">Dr: {fmt(journalDrTotal)}</span>
                <span className="text-orange-600">Cr: {fmt(journalCrTotal)}</span>
              </div>
              {/* Remark — journal mode */}
              {remark ? (
                <div className="mt-3 bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-2">
                  <textarea value={remark} onChange={e => setRemark(e.target.value)} rows={2}
                    placeholder="Optional note..."
                    className="flex-1 text-sm text-gray-700 bg-transparent border-none outline-none resize-none" />
                  <button type="button" onClick={() => setRemark('')} className="text-gray-300 hover:text-gray-500 mt-0.5"><X size={14} /></button>
                </div>
              ) : (
                <div className="mt-3 flex justify-end">
                  <button type="button" onClick={() => setRemark(' ')}
                    className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
                    <Plus size={14} /> Add Remark
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* ── Item cards ── */}
              <div className="space-y-2 mb-2">
                {lines.map((line, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    {/* Item name row */}
                    <div className="flex items-center gap-1 px-3 py-3 border-b border-gray-100">
                      <span className="text-[10px] text-gray-400 w-4 flex-shrink-0">{idx + 1}</span>
                      <div className="relative flex-1">
                        <input type="text"
                          value={line.item_search ?? line.item_name}
                          onChange={e => {
                            const q = e.target.value;
                            setLines(p => p.map((l, i) => i !== idx ? l : { ...l, item_search: q, item_name: '', product_id: '', item_open: q.length >= 1 }));
                          }}
                          onBlur={() => setTimeout(() => setLines(p => p.map((l, i) => i !== idx ? l : { ...l, item_open: false })), 300)}
                          placeholder="Tap to search item..."
                          className="w-full text-sm font-semibold text-gray-800 placeholder-gray-400 bg-transparent border-none outline-none" />
                        {line.item_open && products.filter((p: any) => !(line.item_search) || (p.item_name || '').toLowerCase().includes((line.item_search || '').toLowerCase())).length > 0 && (
                          <div className="absolute z-20 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-44 overflow-y-auto">
                            {products.filter((p: any) => !(line.item_search) || (p.item_name || '').toLowerCase().includes((line.item_search || '').toLowerCase())).slice(0, 20).map((p: any) => (
                              <div key={p.id} onPointerDown={() => {
                                setLines(prev => prev.map((l, i) => i !== idx ? l : calcLine({
                                  ...l, product_id: String(p.id), item_name: p.item_name,
                                  item_search: p.item_name, item_open: false, gst_rate: Number(p.gst) || 0,
                                }, isIgst)));
                                if (p.batch === 'Yes') setTimeout(() => openBatchPopup(idx, String(p.id)), 0);
                                else if (p.category_id === CLOUD_CATEGORY_ID) setTimeout(() => openCloudPopup(idx), 0);
                              }} className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                                <div className="font-medium">{p.item_name}</div>
                                {p.sale_price != null && <div className="text-xs text-gray-400">₹{p.sale_price}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {lines.length > 1 && (
                        <button type="button" onClick={() => setLines(p => p.filter((_, i) => i !== idx))}
                          className="text-red-400 p-2 flex-shrink-0"><X size={14} /></button>
                      )}
                    </div>
                    {(() => {
                      const prod = products.find((p: any) => String(p.id) === String(line.product_id));
                      const namedRows = (line.batch_rows || []).filter(b => (b.batch_name || '').trim());
                      const named = namedRows.length;
                      if (prod?.batch !== 'Yes' && named === 0) return null;
                      // View mode: <fieldset disabled> blocks a <button>, so show
                      // the serials inline with a plain <span> opener to inspect.
                      if (readOnly) {
                        if (named === 0) return null;
                        return (
                          <div className="px-3 pb-1 -mt-1 text-[11px] italic text-slate-500">
                            Batch : {namedRows.map(b => (b.batch_name || '').trim()).join(', ')}
                            <span role="button" tabIndex={0} onClick={() => openBatchPopup(idx)}
                              className="ml-2 not-italic text-blue-500 hover:underline cursor-pointer">view</span>
                          </div>
                        );
                      }
                      return (
                        <div className="px-3 pb-1 -mt-1">
                          <button type="button" onClick={() => openBatchPopup(idx)}
                            className={`text-[11px] hover:underline ${named > 0 ? 'text-blue-500' : 'text-orange-500'}`}>
                            {named > 0 ? `${named} serial(s) — edit` : 'Add batch / serial details'}
                          </button>
                        </div>
                      );
                    })()}
                    {/* Qty | Rate | Amount — single-line label:value in bordered box */}
                    <div className="grid grid-cols-3 gap-2 px-3 py-2.5">
                      <label className="flex items-center gap-1 border border-gray-200 rounded-lg px-2.5 py-2.5 bg-gray-50 cursor-text">
                        <span className="text-[9px] text-gray-400 uppercase tracking-wide shrink-0">Qty</span>
                        <span className="text-[9px] text-gray-300 shrink-0">:</span>
                        <input type="number" inputMode="decimal" value={line.qty || ''}
                          disabled={products.find((p: any) => String(p.id) === String(line.product_id))?.batch === 'Yes'}
                          onChange={e => { const qty = parseFloat(e.target.value) || 0; setLines(p => p.map((l, i) => i !== idx ? l : calcLine({ ...l, qty }, isIgst))); }}
                          className="w-full min-w-0 text-sm font-semibold text-gray-800 bg-transparent border-none outline-none text-right disabled:text-gray-500 disabled:cursor-not-allowed" />
                      </label>
                      <label className="flex items-center gap-1 border border-gray-200 rounded-lg px-2.5 py-2.5 bg-gray-50 cursor-text">
                        <span className="text-[9px] text-gray-400 uppercase tracking-wide shrink-0">Rate</span>
                        <span className="text-[9px] text-gray-300 shrink-0">:</span>
                        <input type="number" inputMode="decimal" value={line.rate || ''}
                          disabled={products.find((p: any) => String(p.id) === String(line.product_id))?.batch === 'Yes'}
                          onChange={e => { const rate = parseFloat(e.target.value) || 0; setLines(p => p.map((l, i) => i !== idx ? l : calcLine({ ...l, rate }, isIgst))); }}
                          className="w-full min-w-0 text-sm font-semibold text-gray-800 bg-transparent border-none outline-none text-right disabled:text-gray-500 disabled:cursor-not-allowed" />
                      </label>
                      <label className="flex items-center gap-1 border border-blue-200 rounded-lg px-2.5 py-2.5 bg-blue-50 cursor-text">
                        <span className="text-[9px] text-blue-400 uppercase tracking-wide shrink-0">Amt</span>
                        <span className="text-[9px] text-blue-300 shrink-0">:</span>
                        <input type="number" inputMode="decimal" value={line.amount || ''}
                          disabled={products.find((p: any) => String(p.id) === String(line.product_id))?.batch === 'Yes'}
                          onChange={e => { const amt = parseFloat(e.target.value) || 0; setLines(p => p.map((l, i) => i !== idx ? l : calcLineFromAmount({ ...l }, isIgst, amt))); }}
                          className="w-full min-w-0 text-sm font-bold text-blue-700 bg-transparent border-none outline-none text-right disabled:text-gray-500 disabled:cursor-not-allowed" />
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              {/* + Add Item */}
              <button type="button" onClick={() => setLines(p => [...p, emptyLine()])}
                className="flex items-center gap-1.5 text-sm text-green-600 py-2 px-1 mb-3">
                <Plus size={16} /> Add Item
              </button>

              {/* Summary section */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {/* Item Total */}
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Item Total</span>
                  <span className="text-sm font-semibold text-gray-800">₹{fmt(subtotal)}</span>
                </div>

                {/* Auto ledger rows (CGST / SGST / IGST) — flat rows */}
                {ledgerRows.filter(r => r.auto).map(row => (
                  <div key={row.id} className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-600">{row.ledger_name}</span>
                    <span className="text-sm font-medium text-gray-800">₹{fmt(row.amount)}</span>
                  </div>
                ))}

                {/* Manual ledger rows — editable */}
                {ledgerRows.filter(r => !r.auto).map(row => (
                  <div key={row.id} className="border-b border-gray-100">
                    <div className="flex items-center gap-2 px-4 py-2">
                      <div className="flex-1">
                        <input type="text" value={row.search}
                          onChange={e => updateLedgerRow(row.id, { search: e.target.value, ledger_id: null, ledger_name: '', open: e.target.value.length >= 1 })}
                          onBlur={() => setTimeout(() => updateLedgerRow(row.id, { open: false }), 300)}
                          placeholder="Search ledger..."
                          className="w-full text-sm text-gray-800 border-none outline-none bg-transparent placeholder-gray-400" />
                        {row.open && ledgerOptions(row.search).length > 0 && (
                          <div className="bg-white border border-gray-200 rounded-lg shadow-md mt-1 max-h-36 overflow-y-auto">
                            {ledgerOptions(row.search).map(l => (
                              <div key={l.id} onPointerDown={() => selectLedger(row.id, l)}
                                className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">{l.company}</div>
                            ))}
                          </div>
                        )}
                      </div>
                      <input type="number" inputMode="decimal" value={row.amount || ''}
                        onChange={e => updateLedgerRow(row.id, { amount: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="w-24 text-sm font-medium text-gray-800 text-right border-none outline-none bg-transparent" />
                      <button type="button" onClick={() => removeLedgerRow(row.id)} className="text-red-400 p-0.5"><X size={14} /></button>
                    </div>
                  </div>
                ))}

                {/* + Add Ledger */}
                <div className="px-4 py-2.5 border-b border-gray-100">
                  <button type="button" onClick={addLedgerRow}
                    className="flex items-center gap-1.5 text-sm text-blue-500">
                    <Plus size={15} /> Add Ledger
                  </button>
                </div>

                {/* Grand Total */}
                <div className="flex justify-between items-center px-4 py-3.5">
                  <span className="text-base font-bold text-gray-900">Grand Total</span>
                  <span className="text-lg font-bold text-blue-600">₹{fmt(grandTotal)}</span>
                </div>
              </div>

              {/* Remark — collapsible */}
              {remark ? (
                <div className="mt-3 bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-2">
                  <textarea value={remark} onChange={e => setRemark(e.target.value)} rows={2}
                    placeholder="Optional note..."
                    className="flex-1 text-sm text-gray-700 bg-transparent border-none outline-none resize-none" />
                  <button type="button" onClick={() => setRemark('')} className="text-gray-300 hover:text-gray-500 mt-0.5"><X size={14} /></button>
                </div>
              ) : (
                <div className="mt-3 flex justify-end">
                  <button type="button" onClick={() => setRemark(' ')}
                    className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
                    <Plus size={14} /> Add Remark
                  </button>
                </div>
              )}
            </div>
          )}
          <button type="button" onClick={() => {
            if (journalBillByBill && !allRowsBillAllocBalanced) { openBillAllocAny(); return; }
            setMobileStep(4);
          }}
            className={`w-full text-white text-sm font-semibold py-3.5 rounded-xl mt-4 ${
              journalBillByBill && !allRowsBillAllocBalanced ? 'bg-orange-500' : 'bg-blue-600'
            }`}>
            {journalBillByBill && !allRowsBillAllocBalanced ? '⚠ Complete Bill Allocation First' : 'Next: Preview →'}
          </button>
        </div>
      )}

      {/* Step 4: Preview & Save */}
      {mobileStep === 4 && (
        <div className="p-4 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            <div className="px-4 py-3 flex justify-between items-center text-sm">
              <span className="text-gray-500">Type</span>
              <span className="font-medium text-gray-800">{voucherType || allVchTypes.find(t => t.id === selectedParentId)?.name || '—'}</span>
            </div>
            <div className="px-4 py-3 flex justify-between items-center text-sm">
              <span className="text-gray-500">Vch No</span>
              <span className="font-medium text-gray-800">{voucherNo || '—'}</span>
            </div>
            <div className="px-4 py-3 flex justify-between items-center text-sm">
              <span className="text-gray-500">Date</span>
              <span className="font-medium text-gray-800">
                {voucherDate ? new Date(voucherDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              </span>
            </div>
            {!isJournalType && partyDisplay && (
              <div className="px-4 py-3 flex justify-between items-center text-sm">
                <span className="text-gray-500">Customer</span>
                <span className="font-medium text-gray-800 text-right max-w-[60%] truncate">{partyDisplay}</span>
              </div>
            )}
            {!isJournalType && lines.some(l => l.item_name) && (
              <div className="px-4 py-3">
                <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2">Items</p>
                <div className="space-y-1">
                  {lines.filter(l => l.item_name).map((l, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-700">{l.item_name} × {l.qty}</span>
                      <span className="text-gray-800 font-medium">₹{fmt(l.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isJournalType && journalRows.some(r => r.ledger_id) && (
              <div className="px-4 py-3">
                <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2">Entries</p>
                <div className="space-y-1">
                  {journalRows.filter(r => r.ledger_id).map((r, i) => (
                    <div key={i} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">{r.ledger_name}</span>
                      <span className="flex items-center gap-2">
                        {r.billByBill && (
                          <button type="button" onClick={() => openBillAlloc(r.id)}
                            className={`text-[11px] font-semibold underline decoration-dotted ${isRowBillAllocBalanced(r) ? 'text-green-600' : 'text-orange-500'}`}>
                            {isRowBillAllocBalanced(r) ? '✓ Bills' : 'Allocate'}
                          </button>
                        )}
                        <span className={`font-medium ${r.drOrCr === 'Dr' ? 'text-blue-600' : 'text-orange-600'}`}>
                          {r.drOrCr} ₹{fmt(r.drOrCr === 'Dr' ? r.dr_amount : r.cr_amount)}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="px-4 py-3 flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-800">Grand Total</span>
              <span className="text-lg font-bold text-blue-600">
                ₹{fmt(isJournalType ? journalDrTotal : grandTotal)}
              </span>
            </div>
            {/* Bill Allocation row — non-journal (single party) only; journal rows show inline above */}
            {customerBillByBill && (
              <div className="px-4 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-600">Bill Allocation</span>
                <button type="button" onClick={openCustomerBillAlloc}
                  className={`text-sm font-semibold underline decoration-dotted ${billAllocBalanced ? 'text-green-600' : 'text-orange-500'}`}>
                  {billAllocBalanced
                    ? `✓ ₹${fmt(billAllocTotal)}`
                    : `₹${fmt(billAllocTotal)} / ₹${fmt(effectiveGrandTotal)} — tap to fix`}
                </button>
              </div>
            )}
            {remark && (
              <div className="px-4 py-3">
                <p className="text-[11px] text-gray-400">Remark</p>
                <p className="text-sm text-gray-700 mt-0.5">{remark}</p>
              </div>
            )}
          </div>
          {/* Bill Allocation warning if not balanced */}
          {customerBillByBill && !billAllocBalanced && (
            <button type="button" onClick={openCustomerBillAlloc}
              className="w-full bg-orange-50 border border-orange-200 text-orange-700 text-sm font-medium py-3 rounded-xl flex items-center justify-center gap-2">
              ⚠ Complete Bill Allocation — Balance ₹{fmt(Math.abs(billAllocBalance))} remaining
            </button>
          )}
          {journalBillByBill && !allRowsBillAllocBalanced && (
            <button type="button" onClick={openBillAllocAny}
              className="w-full bg-orange-50 border border-orange-200 text-orange-700 text-sm font-medium py-3 rounded-xl flex items-center justify-center gap-2">
              ⚠ Complete Bill Allocation for all ledgers
            </button>
          )}
          {printableInvoice && partyId && hasPrintableContent && editId && !checkedBy && (
            <div className="mb-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
              🔒 Review &amp; mark this invoice as <strong>Checked</strong> to enable Print / Download / Share.
            </div>
          )}
          {printableInvoice && partyId && hasPrintableContent && editId && checkedBy && (
            <div className="flex gap-2 mb-2">
              <button type="button"
                onClick={() => navigate(`/billing/print-voucher/${editId}`)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-base font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors">
                <Printer size={18} /> Print
              </button>
              <button type="button"
                onClick={() => handleDirectDownload(editId!)}
                disabled={downloadBusy}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-base font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors">
                <Download size={18} /> {downloadBusy ? 'Preparing…' : 'Download'}
              </button>
            </div>
          )}
          {printableInvoice && partyId && hasPrintableContent && editId && checkedBy && (
            <div className="flex gap-2 mb-2">
              <button type="button" disabled={!!shareBusy} onClick={() => handleShare('email', editId!)}
                className="flex-1 bg-blue-50 border border-blue-200 text-blue-700 text-sm font-semibold py-3 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-60">
                <Mail size={15} /> {shareBusy === 'email' ? 'Sending…' : 'Email'}
              </button>
              <button type="button" disabled={!!shareBusy} onClick={() => handleShare('whatsapp', editId!)}
                className="flex-1 bg-green-50 border border-green-200 text-green-700 text-sm font-semibold py-3 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-60">
                <MessageCircle size={15} /> {shareBusy === 'whatsapp' ? 'Preparing…' : 'WhatsApp'}
              </button>
              <button type="button" disabled={!!shareBusy} onClick={() => handleShare('sms', editId!)}
                className="flex-1 bg-orange-50 border border-orange-200 text-orange-700 text-sm font-semibold py-3 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-60">
                <MessageSquare size={15} /> {shareBusy === 'sms' ? 'Preparing…' : 'SMS'}
              </button>
            </div>
          )}
          <div className="mb-2">
            <VoucherAttachments
              vchId={editId ?? null}
              readOnly={readOnly}
              canEdit={canCreate('vouchers') || canEdit('vouchers')}
              stagedFiles={pendingAttachments}
              onStagedChange={setPendingAttachments}
              compact
            />
          </div>
          <button type="button"
            onClick={handleSubmit}
            disabled={submitting || readOnly}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white text-base font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors">
            <Save size={18} />
            {submitting ? 'Saving...' : editId ? 'Update Voucher' : 'Save Voucher'}
          </button>
        </div>
      )}
    </div>

    {/* ── Desktop layout (md and above) ── */}
    <div className="hidden md:flex bg-gray-50 p-3 gap-3 items-start">
      {readOnly && (
        <div className="fixed top-14 md:top-16 left-0 right-0 z-30 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm text-center py-1.5 px-4">
          {checkedLockActive ? (
            <><strong>Checked & Locked</strong> — marked Checked by <strong>{checkedBy}</strong>{checkedAt ? ` on ${new Date(checkedAt).toLocaleString('en-IN')}` : ''}. Only an admin can edit.</>
          ) : (
            <><strong>View Only</strong> — you don't have permission to edit vouchers.</>
          )}
        </div>
      )}
      {/* ── Right Sidebar: System (parent) Vch Types ── */}
      <div className="w-[140px] flex-shrink-0 bg-white rounded-lg shadow p-3 order-last">
        <p className="text-[10px] uppercase font-semibold text-gray-400 mb-2 tracking-wide">Vch Types</p>
        <div className="flex flex-col gap-1">
          {systemParents.map(p => (
            <button key={p.id} onClick={() => setSelectedParentId(p.id)}
              className={`w-full text-left text-sm px-2.5 py-1.5 rounded transition-colors ${
                selectedParentId === p.id
                  ? 'bg-blue-600 text-white font-medium'
                  : 'text-gray-700 hover:bg-blue-50'
              }`}>
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* ── Main Form ── */}
      {/* Card = always-interactive header + editable form. The action buttons
          (Print / Download / Share) sit OUTSIDE the disabled fieldset so they
          keep working on read-only vouchers; only the fieldset's inputs go
          read-only. (A disabled <fieldset> disables every descendant control,
          which is exactly why the buttons must live outside it.) */}
      <div ref={cardRef} style={{ height: cardHeight }} className="bg-white rounded-lg shadow p-4 flex-1 min-w-0 flex flex-col min-h-0">
        <div className="flex items-end flex-wrap gap-x-3 gap-y-2 mb-4">
          <div className="flex items-center gap-2 pb-0.5">
          {/* Back returns to the previous page (e.g. Daybook with its date
              filter still in the URL). When opened directly via deep link
              with no history, fall back to the Daybook landing. */}
          <button type="button"
            onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/billing/daybook'); }}
            className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50"
            title="Back">
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="text-lg font-semibold text-gray-800">
            Vouchers {readOnly && <span className="text-sm text-amber-700 font-normal ml-2">(Read Only)</span>}
          </h1>
          {/* Supporting documents. On a new voucher there is no id yet, so the
              picker stages files and handleSubmit uploads them after save. */}
          <VoucherAttachments
            vchId={editId ?? null}
            readOnly={readOnly}
            canEdit={canCreate('vouchers') || canEdit('vouchers')}
            stagedFiles={pendingAttachments}
            onStagedChange={setPendingAttachments}
          />
          </div>

          {/* Voucher Type + No — inline with the title. */}
          <div className="w-[150px] flex-shrink-0">
            <label className="block text-[11px] text-gray-500 mb-0.5">Voucher Type</label>
            <select value={voucherType} onChange={e => setVoucherType(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400">
              {childTypes.length === 0 ? (
                voucherType
                  ? <option value={voucherType}>{voucherType}</option>
                  : <option value="">-- No types --</option>
              ) : (
                childTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)
              )}
            </select>
          </div>
          <div className="w-32 flex-shrink-0">
            <label className="block text-[11px] text-gray-500 mb-0.5">Voucher No{isAutomaticVoucherNumbering ? ' (Auto)' : ''}</label>
            <input type="text" value={voucherNo} onChange={e => setVoucherNo(e.target.value)}
              readOnly={readOnly || isAutomaticVoucherNumbering}
              aria-readonly={readOnly || isAutomaticVoucherNumbering}
              onKeyDown={handleKeyDown}
              title={isAutomaticVoucherNumbering ? 'This voucher type uses automatic numbering.' : undefined}
              placeholder={isAutomaticVoucherNumbering ? 'Generating...' : 'e.g. S-001'}
              className={`w-full border rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                isAutomaticVoucherNumbering ? 'border-blue-200 bg-blue-50 text-blue-800 cursor-not-allowed' : 'border-gray-300'
              }`} />
          </div>

          {/* Mark Checked / Unmark.
              - Mark: any user with edit access (broad — this is the routine
                reviewer action triggered from Pending Review).
              - Unmark: admin only (preserves the audit trail).
              - Checked badge: shown to everyone when the voucher is checked,
                so non-admins can see the status without being able to revert. */}
          {editId && (
            checkedBy ? (() => {
              // Format the timestamp inline: "07-May-2026 02:30 PM" — concise
              // but unambiguous (full year + 12h time).
              const checkedAtFmt = checkedAt
                ? new Date(checkedAt).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', hour12: true,
                  })
                : null;
              return canUnmarkAsChecked ? (
                <button type="button"
                  onClick={() => setCheckConfirm('unmark')}
                  title={checkedAtFmt ? `Checked on ${checkedAtFmt} — click to unmark` : 'Click to unmark'}
                  className="ml-auto inline-flex items-center gap-2 text-xs font-medium text-amber-700 border border-amber-300 bg-amber-50 hover:bg-amber-100 rounded px-2.5 py-1">
                  <span>✓ Checked by <strong>{checkedBy}</strong></span>
                  {checkedAtFmt && <span className="text-[10px] text-amber-600/80 tabular-nums">{checkedAtFmt}</span>}
                  <span className="text-amber-600/70">· click to unmark</span>
                </button>
              ) : (
                <span title={checkedAtFmt ? `Checked on ${checkedAtFmt}` : 'Only admin can unmark'}
                  className="ml-auto inline-flex items-center gap-2 text-xs font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-2.5 py-1">
                  <span>✓ Checked by <strong>{checkedBy}</strong></span>
                  {checkedAtFmt && <span className="text-[10px] text-emerald-600/80 tabular-nums">{checkedAtFmt}</span>}
                </span>
              );
            })() : (
              canMarkAsChecked && (
                <button type="button"
                  onClick={() => setCheckConfirm('mark')}
                  className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded px-2.5 py-1">
                  Mark as Checked
                </button>
              )
            )
          )}

          {/* Delete — hidden once the voucher is marked Checked. To delete a
              checked voucher, an admin must first unmark it (the Checked
              flag acts as an audit lock against accidental removal of an
              already-reviewed entry). Also hidden in read-only mode. */}
          {editId && !readOnly && !checkedBy && (canDelete('vouchers') || canDelete('activities')) && (
            <button type="button"
              onClick={() => setDeleteOpen(true)}
              className={`${canMarkAsChecked ? 'ml-2' : 'ml-auto'} inline-flex items-center gap-1 text-xs font-medium text-red-700 border border-red-300 bg-red-50 hover:bg-red-100 rounded px-2.5 py-1`}
              title="Delete voucher">
              <Trash2 size={12} /> Delete
            </button>
          )}

          {printableInvoice && partyId && hasPrintableContent && editId && (checkedBy ? (<>
            <button type="button"
              onClick={() => navigate(`/billing/print-voucher/${editId}`)}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 border border-blue-300 bg-blue-50 hover:bg-blue-100 rounded px-2.5 py-1 ml-2"
              title="Print Voucher">
              <Printer size={12} /> Print
            </button>
            <button type="button"
              onClick={() => handleDirectDownload(editId!)}
              disabled={downloadBusy}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60 rounded px-2.5 py-1 ml-1"
              title="Download PDF">
              <Download size={12} /> {downloadBusy ? 'Preparing…' : 'Download'}
            </button>
            <div className="relative inline-block ml-1">
              <button type="button"
                onClick={() => setShareOpen(o => !o)}
                disabled={!!shareBusy}
                className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 border border-purple-300 bg-purple-50 hover:bg-purple-100 disabled:opacity-60 rounded px-2.5 py-1"
                title="Share voucher with the customer">
                <Share2 size={12} /> {shareBusy ? 'Sharing…' : 'Share'}
              </button>
              {shareOpen && !shareBusy && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-40 w-44 py-1"
                  onMouseLeave={() => setShareOpen(false)}>
                  <button type="button" onClick={() => handleShare('email', editId!)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 text-left">
                    <Mail size={13} className="text-blue-600" /> Email (PDF attached)
                  </button>
                  <button type="button" onClick={() => handleShare('whatsapp', editId!)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-green-50 text-left">
                    <MessageCircle size={13} className="text-green-600" /> WhatsApp (PDF link)
                  </button>
                  <button type="button" onClick={() => handleShare('sms', editId!)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-orange-50 text-left">
                    <MessageSquare size={13} className="text-orange-600" /> SMS (PDF link)
                  </button>
                </div>
              )}
            </div>
          </>) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 border border-amber-300 bg-amber-50 rounded px-2.5 py-1 ml-2"
              title="This invoice must be reviewed and marked as Checked before it can be printed, downloaded or shared.">
              🔒 Review (mark as Checked) to print / download / share
            </span>
          ))}

          {/* Voucher Date — pinned to the right of the title row. */}
          <div className="w-[160px] flex-shrink-0 ml-auto">
            <label className="block text-[11px] text-gray-500 mb-0.5">Voucher Date</label>
            <input type="date" value={voucherDate} onChange={e => setVoucherDate(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
          </div>

          {/* Save / Update — pinned top-right, just after the date. */}
          {!readOnly && (() => {
            // Block save whenever any ledger field has orphan text OR a row
            // has an amount without a ledger picked. Same rule for both
            // journal-mode and items-mode (Sales / Purchase / Credit / Debit).
            const orphanJournal = isJournalType
              ? journalRows.find(r => !r.ledger_id && r.search.trim())
              : null;
            const amtWithoutLedgerJournal = isJournalType
              ? journalRows.find(r => !r.ledger_id && (r.dr_amount > 0 || r.cr_amount > 0))
              : null;
            const orphanLedgerRow = !isJournalType
              ? ledgerRows.find(r => !r.ledger_id && r.search.trim())
              : null;
            const amtWithoutLedgerItems = !isJournalType
              ? ledgerRows.find(r => !r.ledger_id && r.amount > 0)
              : null;
            const orphanParty = !isJournalType && !partyId && partyDisplay.trim()
              ? partyDisplay.trim()
              : null;
            const blockReason = isStockJournal ? undefined :
              orphanJournal              ? `"${orphanJournal.search.trim()}" — pick a ledger from the dropdown` :
              amtWithoutLedgerJournal    ? `Row ${journalRows.indexOf(amtWithoutLedgerJournal) + 1}: pick a ledger before entering amount` :
              orphanLedgerRow            ? `"${orphanLedgerRow.search.trim()}" — pick a ledger from the dropdown` :
              amtWithoutLedgerItems      ? `Ledger row: pick a ledger before entering amount` :
              orphanParty                ? `"${orphanParty}" — pick a customer from the dropdown` :
              isJournalType && !journalBalanced ? 'Dr total must equal Cr total' :
              isJournalType && !allRowsBillAllocBalanced ? 'Complete bill allocation for all ledgers' :
              !isJournalType && !billAllocBalanced ? 'Complete bill allocation — balance must reach zero' :
              undefined;
            return (
              <button onClick={handleSubmit}
                onKeyDown={handleKeyDown}
                disabled={submitting || !!blockReason}
                title={blockReason}
                className="flex-shrink-0 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded px-5 py-2">
                <Save size={16} />
                {submitting ? (editId ? 'Updating...' : 'Submitting...') : (editId ? 'Update Voucher' : 'Save Voucher')}
              </button>
            );
          })()}
        </div>

        {/* Mark / Unmark Checked confirm modal — replaces the native
            window.confirm() so the prompt matches the rest of the app's
            chrome. Single modal handles both flows; copy + button label
            change based on `checkConfirm` mode. Kept OUTSIDE the fieldset so a
            check-only reviewer (read-only form, no edit) can still confirm —
            it's `fixed`, so DOM position doesn't affect layout. */}
        {checkConfirm && editId && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
              <h3 className="font-semibold text-gray-800 mb-2">
                {checkConfirm === 'mark' ? 'Mark Voucher as Checked' : 'Remove Checked Flag'}
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                {checkConfirm === 'mark' ? (
                  <>Mark this voucher as <span className="font-semibold text-emerald-700">Checked</span>? Once marked, the voucher is locked — <span className="font-semibold">only an admin can unmark or delete it</span>.</>
                ) : (
                  <>Remove the Checked flag from this voucher? It will become <span className="font-semibold">editable and deletable</span> again by anyone with the appropriate permission.</>
                )}
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCheckConfirm(null)}
                  className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
                  Cancel
                </button>
                <button type="button" disabled={checkBusy}
                  onClick={async () => {
                    setCheckBusy(true);
                    try {
                      if (checkConfirm === 'mark') {
                        await vouchersApi.markChecked(editId);
                        setCheckedBy(user?.name || 'me'); setCheckedAt(new Date().toISOString());
                        showSuccess('Marked', 'Voucher marked as Checked');
                      } else {
                        await vouchersApi.markUnchecked(editId);
                        setCheckedBy(null); setCheckedAt(null);
                        showSuccess('Unchecked', 'Voucher is no longer marked as Checked');
                      }
                      setCheckConfirm(null);
                    } catch (e: any) {
                      showError('Error', e?.message || 'Failed');
                    } finally { setCheckBusy(false); }
                  }}
                  className={`px-4 py-1.5 text-sm rounded text-white disabled:opacity-50 ${
                    checkConfirm === 'mark' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'
                  }`}>
                  {checkBusy
                    ? (checkConfirm === 'mark' ? 'Marking…' : 'Unmarking…')
                    : (checkConfirm === 'mark' ? 'Mark as Checked' : 'Unmark')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scroll region — only the form body scrolls; the header above and the
            Remark/Grand-Total footer below stay put. min-h-0 lets this flex
            child actually shrink so the overflow kicks in. */}
        <div className="flex-1 min-h-0 overflow-y-auto">

        {/* Editable form — the fieldset goes `disabled` for read-only vouchers,
            making every input inside it non-editable. */}
        <fieldset ref={formRef} disabled={readOnly} className="disabled:opacity-95 border-0 p-0 m-0 min-w-0">

        {/* Delete confirm modal — kept inside the form so the Delete button
            (above) can stay close to its trigger; rendering inside doesn't
            affect the modal since it's positioned `fixed`. */}
        {deleteOpen && editId && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
              <h3 className="font-semibold text-gray-800 mb-2">Delete Voucher</h3>
              <p className="text-sm text-gray-600 mb-5">
                Are you sure you want to delete this voucher? This will also remove all ledger entries, inventory entries and bill allocation records. <span className="font-semibold text-red-500">This cannot be undone.</span>
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setDeleteOpen(false)}
                  className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
                  Cancel
                </button>
                <button type="button" disabled={deleting}
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await vouchersApi.deleteVoucher(editId);
                      showSuccess('Deleted', 'Voucher deleted successfully');
                      setDeleteOpen(false);
                      // Return to the Daybook (or wherever the user came from)
                      // — the deleted voucher no longer exists, so staying on
                      // the edit page would render stale state.
                      if (window.history.length > 1) navigate(-1);
                      else navigate('/billing/daybook');
                    } catch (e: any) {
                      showError('Error', e?.message || 'Failed to delete voucher');
                    } finally { setDeleting(false); }
                  }}
                  className="px-4 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* ══ Journal / Contra / Payment / Receipt Form ══ */}
        {isStockJournal && (() => {
          return (
            <>
              <div className="border border-gray-200 rounded mb-3 flex divide-x divide-gray-200">
                <StockSide title="Source (Consumption)" lines={stockSource} setLines={setStockSource}
                  onOpenBatch={lineId => setStockBatchPopup({ side: 'src', lineId })} />
                <StockSide title="Destination (Production)" lines={stockDest} setLines={setStockDest}
                  onOpenBatch={lineId => setStockBatchPopup({ side: 'dst', lineId })} />
              </div>
              {stockBatchPopup && stockBatchLine && (
                <StockBatchPopup
                  line={stockBatchLine}
                  onSave={rows => {
                    const totQty = rows.reduce((s, b) => s + b.qty, 0);
                    const totAmt = rows.reduce((s, b) => s + b.amount, 0);
                    const setter = stockBatchPopup.side === 'src' ? setStockSource : setStockDest;
                    setter(p => p.map(l => l.id === stockBatchPopup.lineId
                      ? { ...l, batch_rows: rows, qty: totQty, amount: totAmt }
                      : l));
                    setStockBatchPopup(null);
                  }}
                  onClose={() => setStockBatchPopup(null)}
                />
              )}
            </>
          );
        })()}

        {isJournalType && (
          <div className="border border-gray-200 rounded mb-3">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-[11px] text-gray-500 uppercase">
                  <th className="py-2 px-2 text-left w-[28px]">#</th>
                  <th className="py-2 px-2 text-left w-[68px]">Type</th>
                  <th className="py-2 px-2 text-left">Ledger</th>
                  <th className="py-2 px-2 text-right w-[110px]">Dr Amount</th>
                  <th className="py-2 px-2 text-right w-[110px]">Cr Amount</th>
                  <th className="py-2 px-2 w-[30px]"></th>
                </tr>
              </thead>
              <tbody>
                {journalRows.map((row, idx) => (
                  <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-1 px-2 text-gray-400 text-xs">{idx + 1}</td>
                    <td className="py-1 px-1">
                      <select value={row.drOrCr}
                        onKeyDown={handleKeyDown} data-row={idx} data-field="drOrCr"
                        onChange={e => setJournalRows(p => p.map(r => r.id === row.id
                          ? { ...r, drOrCr: e.target.value as 'Dr' | 'Cr', dr_amount: 0, cr_amount: 0 }
                          : r))}
                        className="w-full border border-gray-200 rounded text-sm py-1 px-1 focus:outline-none focus:ring-1 focus:ring-blue-400">
                        <option value="Dr">Dr</option>
                        <option value="Cr">Cr</option>
                      </select>
                    </td>
                    <td className="py-1 px-1">
                      <div className="relative">
                        <input type="text" value={row.search}
                          ref={(el) => { journalInputRefs.current.set(row.id, el); }}
                          onKeyDown={handleKeyDown} data-row={idx} data-field="ledger"
                          onChange={e => {
                            const q = e.target.value;
                            setJournalRows(p => p.map(r => r.id === row.id
                              ? { ...r, search: q, ledger_id: null, ledger_name: '', open: q.length >= 2 }
                              : r));
                            setActiveDropIdx(0); // pre-select first match for instant Enter
                            if (q.length >= 2) {
                              customersApi.searchAllLedgers(q).then((res: any) => {
                                const list: any[] = filterJournalLedgers(res?.data || []);
                                setJournalRows(p => p.map(r => r.id === row.id ? { ...r, results: list, open: true } : r));
                              }).catch((err: any) => console.warn('[LedgerSearch]', err?.message || err));
                            }
                          }}
                          onFocus={() => {
                            if (row.search.length >= 2 && row.results.length > 0)
                              setJournalRows(p => p.map(r => r.id === row.id ? { ...r, open: true } : r));
                          }}
                          onBlur={() => setTimeout(() => {
                            // Trap focus: refuse to leave the field if user
                            // typed text but didn't select from the dropdown.
                            setJournalRows(p => {
                              const r = p.find(x => x.id === row.id);
                              if (r && !r.ledger_id && r.search.trim()) {
                                const el = journalInputRefs.current.get(row.id);
                                if (el && document.activeElement !== el) el.focus();
                              }
                              return p.map(x => x.id === row.id ? { ...x, open: false } : x);
                            });
                          }, 200)}
                          placeholder="Search ledger..."
                          // Visual cue: green when a real ledger is locked
                          // in, red when the user has typed text but not
                          // selected from the dropdown (so they immediately
                          // see the row won't save). Empty rows stay neutral.
                          className={`w-full border rounded text-sm py-1 px-2 pr-6 focus:outline-none focus:ring-1 ${
                            row.ledger_id
                              ? 'border-green-400 bg-green-50 focus:ring-green-300'
                              : row.search.trim()
                                ? 'border-red-400 bg-red-50 focus:ring-red-300'
                                : 'border-gray-200 focus:ring-blue-400'
                          }`}
                          title={row.search.trim() && !row.ledger_id ? 'Pick a ledger from the dropdown — free text is not allowed' : undefined}
                        />
                        {row.ledger_id && (row.dr_amount > 0 || row.cr_amount > 0) && (() => {
                          const balanced = isRowBillAllocBalanced(row);
                          return row.billByBill ? (
                            <button onClick={() => openBillAlloc(row.id)}
                              title={balanced ? 'Bill allocated' : 'Allocate bills'}
                              className={`absolute right-1.5 top-1/2 -translate-y-1/2 text-xs font-bold ${balanced ? 'text-green-600' : 'text-orange-500 animate-pulse'}`}>
                              {balanced ? '✓' : '⚠'}
                            </button>
                          ) : (
                            <span title="On account" className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs font-bold text-green-600">✓</span>
                          );
                        })()}
                        {row.open && row.results.length > 0 && (
                          <AnchoredList maxH={240}>
                            {row.results.map((l: any, lIdx: number) => (
                              <div key={l.id} onPointerDown={() => {
                                const isBillByBill = l.billbybill === 'Yes';
                                setJournalRows(p => p.map(r => r.id === row.id
                                  ? { ...r, ledger_id: l.id, ledger_name: l.company, search: l.company, open: false, billByBill: isBillByBill, billAlloc: isBillByBill ? r.billAlloc : [] }
                                  : r));
                              }} className={`px-2 py-1.5 text-sm cursor-pointer ${lIdx === activeDropIdx ? 'bg-blue-600 text-white' : 'hover:bg-blue-50'}`}>
                                {l.company}
                                {l.billbybill === 'Yes' && <span className={`ml-1 text-[10px] ${lIdx === activeDropIdx ? 'text-blue-200' : 'text-blue-400'}`}>bill-by-bill</span>}
                              </div>
                            ))}
                          </AnchoredList>
                        )}
                      </div>
                    </td>
                    <td className="py-1 px-1">
                      {row.drOrCr === 'Dr' ? (
                        <input type="number" step="any" value={row.dr_amount || ''}
                          onKeyDown={handleKeyDown} data-row={idx} data-field="dr_amount"
                          onChange={e => {
                            const v = Number(e.target.value) || 0;
                            setJournalRows(p => p.map(r => r.id === row.id ? { ...r, dr_amount: v } : r));
                          }}
                          onBlur={e => {
                            if (isJournalType) {
                              const amt = parseFloat(e.target.value) || 0;
                              const allocSigned = row.billAlloc.reduce((s, en) => s + (en.direction === 'Cr' ? -(Number(en.amount) || 0) : (Number(en.amount) || 0)), 0);
                              if (amt > 0 && (row.billAlloc.length === 0 || Math.abs(amt - allocSigned) >= 0.01)) {
                                if (row.billByBill) setTimeout(() => openBillAlloc(row.id), 50);
                                else autoFillOnAccount(row.id, amt, 'Dr');
                              }
                            }
                          }}
                          className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium text-gray-700" />
                      ) : <span className="block text-center text-gray-300">—</span>}
                    </td>
                    <td className="py-1 px-1">
                      {row.drOrCr === 'Cr' ? (
                        <input type="number" step="any" value={row.cr_amount || ''}
                          onKeyDown={handleKeyDown} data-row={idx} data-field="cr_amount"
                          onChange={e => {
                            const v = Number(e.target.value) || 0;
                            setJournalRows(p => p.map(r => r.id === row.id ? { ...r, cr_amount: v } : r));
                          }}
                          onBlur={e => {
                            if (isJournalType) {
                              const amt = parseFloat(e.target.value) || 0;
                              const allocSigned = row.billAlloc.reduce((s, en) => s + (en.direction === 'Cr' ? -(Number(en.amount) || 0) : (Number(en.amount) || 0)), 0);
                              if (amt > 0 && (row.billAlloc.length === 0 || Math.abs(-amt - allocSigned) >= 0.01)) {
                                if (row.billByBill) setTimeout(() => openBillAlloc(row.id), 50);
                                else autoFillOnAccount(row.id, amt, 'Cr');
                              }
                            }
                          }}
                          className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium text-gray-700" />
                      ) : <span className="block text-center text-gray-300">—</span>}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {journalRows.length > 1 && (
                        <button onClick={() => setJournalRows(p => p.filter(r => r.id !== row.id))}
                          className="text-red-400 hover:text-red-600"><X size={14} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-100">
                  <td colSpan={6} className="py-1 px-2">
                    <button onClick={() => setJournalRows(p => [...p, emptyJournalRow()])}
                      onKeyDown={handleKeyDown}
                      className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800">
                      <Plus size={12} /> Add Row
                    </button>
                  </td>
                </tr>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td colSpan={3} className="py-2 px-2 text-sm font-bold text-gray-800">
                    Grand Total
                    {!journalBalanced && (
                      <span className="ml-2 text-xs font-normal text-red-500">
                        (Dr {fmt(journalDrTotal)} ≠ Cr {fmt(journalCrTotal)})
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right text-sm font-bold">
                    <span className="text-blue-600">{fmt(journalDrTotal)}</span>
                  </td>
                  <td className="py-2 px-2 text-right text-sm font-bold text-gray-700">{fmt(journalCrTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* ══ Normal Sales / Purchase Form ══ */}
        {/* Header — Row 2: Customer Name + Invoice Mode toggle (same row) */}
        <div className="flex flex-wrap items-end gap-x-6 gap-y-2 mb-4">
        <div ref={customerRef} className={`relative w-full sm:w-[320px] ${isJournalType || isStockJournal ? 'hidden' : ''}`}>
          <label className="block text-[11px] text-gray-500 mb-0.5 flex items-center gap-2">
            Customer Name
            {partyId && partyState && (
              <span className="text-blue-500">({partyState} → {isIgst ? 'IGST' : 'CGST+SGST'})</span>
            )}
          </label>
          <div className="flex gap-1">
            <input type="text" value={partyDisplay}
              ref={customerInputRef}
              onKeyDown={handleKeyDown}
              onChange={e => { setPartyDisplay(e.target.value); setCustomerSearch(e.target.value); setPartyId(''); setShowCustomerDrop(true); setActiveDropIdx(0); }}
              onFocus={() => {
                setShowCustomerDrop(true);
                if (partyDisplay.length >= 1 && customers.length === 0) {
                  customersApi.search(partyDisplay).then((r: any) => {
                    const list = Array.isArray(r) ? r : (r?.data || []);
                    setCustomers(list);
                  }).catch(() => {});
                }
              }}
              onBlur={() => {
                // Trap focus: if user typed something but never picked a
                // customer, snap focus back to this field after dropdown
                // clicks have settled.
                setTimeout(() => {
                  if (!partyId && partyDisplay.trim() && customerInputRef.current && document.activeElement !== customerInputRef.current) {
                    customerInputRef.current.focus();
                  }
                }, 200);
              }}
              placeholder="Type to search customer..."
              // Green when a real customer is locked in, red when the user
              // typed text but didn't pick from the dropdown (so the row
              // can't save).
              className={`w-full border rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 ${
                partyId
                  ? 'border-green-400 bg-green-50 focus:ring-green-300'
                  : partyDisplay.trim()
                    ? 'border-red-400 bg-red-50 focus:ring-red-300'
                    : 'border-gray-300 focus:ring-blue-400'
              }`}
              title={partyDisplay.trim() && !partyId ? 'Pick a customer from the dropdown — free text is not allowed' : undefined} />
            {!readOnly && (
              <button onClick={() => { setShowNewCustomer(true); setCustForm(f => ({ ...f, company: partyDisplay })); setShowCustomerDrop(false); }}
                onKeyDown={handleKeyDown}
                title="New customer" className="flex-shrink-0 bg-green-500 hover:bg-green-600 text-white rounded px-2">
                <UserPlus size={14} />
              </button>
            )}
          </div>
          {showCustomerDrop && customers.length > 0 && (
            <div className="absolute z-20 w-full bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto mt-0.5">
              {customers.slice(0, 20).map((c: any, cIdx: number) => (
                <div key={c.id} className={`px-2 py-1.5 text-sm cursor-pointer ${cIdx === activeDropIdx ? 'bg-blue-600 text-white' : 'hover:bg-blue-50'}`}
                  onClick={() => selectParty(c)}>
                  <span className="font-medium">{c.company}</span>
                  {c.mobile && <span className={`text-xs ml-2 ${cIdx === activeDropIdx ? 'text-blue-100' : 'text-gray-400'}`}>{c.mobile}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Item Invoice / Accounting Invoice toggle — goods vouchers only. */}
        {canAccountingInvoice && (
          <div className="flex items-center gap-2 pb-0.5 ml-auto">
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Mode</span>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              <button type="button" disabled={readOnly}
                onClick={() => setInvoiceMode('item')}
                className={`px-3 py-1.5 text-[13px] font-medium transition-colors ${invoiceMode === 'item' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                Item Invoice
              </button>
              <button type="button" disabled={readOnly}
                onClick={() => setInvoiceMode('accounting')}
                className={`px-3 py-1.5 text-[13px] font-medium border-l border-gray-300 transition-colors ${invoiceMode === 'accounting' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                Accounting Invoice
              </button>
            </div>
          </div>
        )}
        </div>

        {isAccountingInvoice && (
          <div className="-mt-2 mb-3 text-[12px] text-gray-400 italic">No items — post ledgers directly against the party.</div>
        )}

        {/* Remark — journal / stock-journal modes (item mode carries its own
            Remark inline in the Grand Total row). */}
        {(isJournalType || isStockJournal) && (
          <div className="mt-3">
            <input type="text" value={remark} onChange={e => setRemark(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Remark (optional)"
              disabled={readOnly}
              className="w-full border border-gray-300 rounded text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>
        )}

        {/* Line Items Table — hidden for journal types and stock journal */}
        <div className={`border border-gray-200 rounded mb-0 ${isJournalType || isStockJournal ? 'hidden' : ''}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-[11px] text-gray-500 uppercase">
                  <th className="py-2 px-2 text-left w-[28px]">#</th>
                  <th className="py-2 px-2 text-left w-[280px]">Item</th>
                  <th className="py-2 px-2 text-right w-[72px]">Qty</th>
                  <th className="py-2 px-2 text-right w-[90px]">Rate</th>
                  <th ref={amountAnchorRef} className="py-2 px-2 text-right w-[100px]">
                    <span className="flex items-center justify-end gap-1">
                      Amount
                      <button type="button" tabIndex={-1}
                        onClick={() => setShowGst(v => !v)}
                        className="text-gray-400 hover:text-blue-500"
                        title={showGst ? 'Hide GST' : 'Show GST'}>
                        {showGst ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                    </span>
                  </th>
                  {showGst && <>
                    <th className="py-2 px-2 text-right w-[60px]">GST%</th>
                    {isIgst
                      ? <th className="py-2 px-2 text-right w-[80px]">IGST</th>
                      : <>
                          <th className="py-2 px-2 text-right w-[75px]">CGST</th>
                          <th className="py-2 px-2 text-right w-[75px]">SGST</th>
                        </>
                    }
                  </>}
                  <th className="py-2 px-2 w-[36px]"></th>
                </tr>
              </thead>
              <tbody>
                {!isAccountingInvoice && lines.map((line, idx) => (
                  <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-1 px-2 text-gray-400 text-xs">{idx + 1}</td>
                    <td className="py-1 px-1">
                      {/* Custom item picker — input + absolutely-positioned
                          dropdown that always opens DOWN, plus type-to-search
                          for long item lists. Native <select> was flipping
                          upward whenever the row sat near the viewport
                          bottom. */}
                      <div className="relative">
                        <input type="text"
                          value={line.item_open ? (line.item_search ?? '') : line.item_name}
                          onKeyDown={handleKeyDown} data-row={idx} data-field="item"
                          onChange={e => {
                            updateLine(idx, 'item_search', e.target.value);
                            updateLine(idx, 'item_open', true);
                            setActiveDropIdx(0);
                          }}
                          onFocus={(e) => {
                            updateLine(idx, 'item_open', true);
                            // Seed the search with the currently-selected name so
                            // the input never appears empty when the dropdown is
                            // open. User can clear it to search fresh.
                            updateLine(idx, 'item_search', line.item_name || '');
                            setActiveDropIdx(0);
                            // Select-all so the next keystroke replaces the name
                            // rather than appending to it.
                            setTimeout(() => e.target.select(), 0);
                          }}
                          onBlur={() => setTimeout(() => updateLine(idx, 'item_open', false), 300)}
                          placeholder="-- Select Item --"
                          className={`w-full border rounded text-sm py-1 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                            line.product_id ? 'border-green-400 bg-green-50' : 'border-gray-200'
                          }`}
                        />
                        {line.item_open && (() => {
                          const q = (line.item_search ?? '').toLowerCase();
                          const matches = products
                            .filter((p: any) => !q || (p.item_name || '').toLowerCase().includes(q))
                            .slice(0, 50);
                          return (
                            <div className="absolute z-30 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto">
                              {matches.length === 0 && (
                                <div className="px-2 py-2 text-xs text-gray-400">No items found</div>
                              )}
                              {matches.map((p: any, pIdx: number) => (
                                <div key={p.id}
                                  onPointerDown={() => {
                                    updateLine(idx, 'product_id', String(p.id));
                                    updateLine(idx, 'item_name', p.item_name);
                                    updateLine(idx, 'item_search', '');
                                    updateLine(idx, 'item_open', false);
                                    if (p.batch === 'Yes') {
                                      setTimeout(() => openBatchPopup(idx, String(p.id)), 0);
                                    } else if (p.category_id === CLOUD_CATEGORY_ID) {
                                      setTimeout(() => openCloudPopup(idx), 0);
                                    }
                                  }}
                                  className={`px-2 py-1.5 text-sm cursor-pointer ${pIdx === activeDropIdx ? 'bg-blue-600 text-white' : 'hover:bg-blue-50'}`}>
                                  {p.item_name}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      {(() => {
                        const prod = products.find((p: any) => String(p.id) === String(line.product_id));
                        const namedRows = (line.batch_rows || []).filter(b => (b.batch_name || '').trim());
                        const named = namedRows.length;
                        // Show the button whenever the item is batch-tracked
                        // (even if no rows are saved yet) OR rows already
                        // exist — otherwise a batch item loaded into an
                        // existing voucher with empty batchRows has no way
                        // to open the popup at all.
                        if (prod?.batch !== 'Yes' && named === 0) return null;
                        // View mode: the surrounding <fieldset disabled> greys out
                        // a <button>, so the batch never reflected. List the serials
                        // inline and use a plain <span> opener (not a form control)
                        // so it stays clickable to inspect qty/rate per serial.
                        if (readOnly) {
                          if (named === 0) return null;
                          return (
                            <div className="text-[10px] italic text-slate-500 mt-0.5">
                              Batch : {namedRows.map(b => (b.batch_name || '').trim()).join(', ')}
                              <span role="button" tabIndex={0} onClick={() => openBatchPopup(idx)}
                                className="ml-2 not-italic text-blue-500 hover:underline cursor-pointer">view</span>
                            </div>
                          );
                        }
                        return (
                          <button onClick={() => openBatchPopup(idx)}
                            className={`text-[10px] hover:underline mt-0.5 block ${named > 0 ? 'text-blue-500' : 'text-orange-500'}`}
                            title={named > 0 ? 'Edit serials' : 'Add batch / serial details'}>
                            {named > 0 ? `${named} serial(s) — edit` : 'Add batch details'}
                          </button>
                        );
                      })()}
                      {/* Cloud details behind a Cloud Billing line — server /
                          serial, users, cycle, rate, expiry from the activity. */}
                      {cloudDetails[idx] && (() => {
                        const cd = cloudDetails[idx];
                        const dt = (s: any) => s ? new Date(s).toLocaleDateString('en-GB') : null;
                        const chip = (label: string, val: any) => (val || val === 0)
                          ? <span className="whitespace-nowrap"><span className="text-gray-400">{label}:</span> <span className="text-gray-700 font-medium">{val}</span></span> : null;
                        return (
                          <div className="mt-1 text-[10.5px] leading-tight bg-sky-50 border border-sky-100 rounded px-2 py-1">
                            <div className="flex flex-wrap gap-x-2.5 gap-y-0.5">
                              {chip('Users', cd.billing_units)}
                              {chip('Cycle', cd.billing_cycle)}
                              {chip('Mode', cd.billing_mode === 'day_to_day' ? 'D2D' : (cd.billing_mode === 'full_cycle' ? 'Full Cycle' : cd.billing_mode))}
                              {chip('Rate', cd.last_bill_rate ? `₹${Number(cd.last_bill_rate).toLocaleString('en-IN')}` : null)}
                              {chip('Type', cd.activity_type)}
                              {chip('From', dt(cd.start_from))}
                              {(cd.old_expiry_date || cd.new_expiry_date) && chip('Expiry', `${dt(cd.old_expiry_date) || '—'} → ${dt(cd.new_expiry_date) || '—'}`)}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="py-1 px-1">
                      <input type="number" step="any" value={line.qty || ''}
                        disabled={products.find((p: any) => String(p.id) === String(line.product_id))?.batch === 'Yes'}
                        onKeyDown={handleKeyDown} data-row={idx} data-field="qty"
                        onChange={e => updateLine(idx, 'qty', e.target.value === '' ? 0 : Number(e.target.value))}
                        className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" />
                    </td>
                    <td className="py-1 px-1">
                      <input type="number" step="any" value={line.rate || ''}
                        disabled={products.find((p: any) => String(p.id) === String(line.product_id))?.batch === 'Yes'}
                        onKeyDown={handleKeyDown} data-row={idx} data-field="rate"
                        onChange={e => updateLine(idx, 'rate', e.target.value === '' ? 0 : Number(e.target.value))}
                        className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" />
                    </td>
                    <td className="py-1 px-1">
                      <input type="number" step="any" value={line.amount || ''}
                        disabled={products.find((p: any) => String(p.id) === String(line.product_id))?.batch === 'Yes'}
                        onKeyDown={handleKeyDown} data-row={idx} data-field="amount"
                        onChange={e => updateLine(idx, 'amount', e.target.value === '' ? 0 : Number(e.target.value))}
                        className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium text-gray-700 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" />
                    </td>
                    {showGst && <>
                      <td className="py-1 px-2 text-right text-gray-500 text-xs">{line.gst_rate}%</td>
                      {isIgst
                        ? <td className="py-1 px-2 text-right text-orange-500 text-xs">{fmt(line.igst_amount)}</td>
                        : <>
                            <td className="py-1 px-2 text-right text-blue-500 text-xs">{fmt(line.cgst_amount)}</td>
                            <td className="py-1 px-2 text-right text-purple-500 text-xs">{fmt(line.sgst_amount)}</td>
                          </>
                      }
                    </>}
                    <td className="py-1 px-1 text-center">
                      {lines.length > 1 && (
                        <button onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                      )}
                    </td>
                  </tr>
                ))}
                {!readOnly && !isAccountingInvoice && (
                <tr className="border-t border-gray-100">
                  <td colSpan={10} className="py-1 px-2">
                    <button onClick={addRow}
                      onKeyDown={handleKeyDown}
                      className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800">
                      <Plus size={12} /> Add Item
                    </button>
                  </td>
                </tr>
                )}
              </tbody>

              {/* tfoot: Item Total → Ledger rows → Grand Total — all aligned under Amount column */}
              {(() => {
                const trailingCols = showGst ? (isIgst ? 3 : 4) : 1;
                return (
                  <tfoot>
                    {/* Item Total — hidden in Accounting Invoice (no items) */}
                    {!isAccountingInvoice && (
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td colSpan={4} className="py-1.5 px-2 text-[11px] text-gray-500 uppercase font-semibold">Item Total</td>
                      <td className="py-1.5 px-2 text-right text-sm font-semibold text-gray-800">{fmt(subtotal)}</td>
                      <td colSpan={trailingCols} />
                    </tr>
                    )}

                    {/* Ledger rows */}
                    {ledgerRows.map((row, rIdx) => (
                      <tr key={row.id} className="border-t border-gray-100">
                        <td className="py-1 px-2 text-gray-400 text-xs">{rIdx + 1}</td>
                        <td colSpan={3} className="py-1 px-2">
                          <div className="relative w-[280px]">
                            <div className={`flex items-center border rounded overflow-hidden ${
                              row.ledger_id
                                ? 'border-green-400 bg-green-50'
                                : row.search.trim()
                                  ? 'border-red-400 bg-red-50'
                                  : 'border-gray-200'
                            }`}>
                               <input type="text" value={row.search}
                                ref={(el) => { ledgerInputRefs.current.set(row.id, el); }}
                                onKeyDown={handleKeyDown} data-row={rIdx} data-field="ledger-search"
                                onChange={e => { updateLedgerRow(row.id, { search: e.target.value, ledger_id: null, open: e.target.value.trim().length >= 1 }); setActiveDropIdx(0); }}
                                onFocus={() => row.search.trim().length >= 1 && updateLedgerRow(row.id, { open: true })}
                                onBlur={() => setTimeout(() => {
                                  // Trap focus when text typed but no ledger selected.
                                  setLedgerRows(p => {
                                    const r = p.find(x => x.id === row.id);
                                    if (r && !r.ledger_id && r.search.trim()) {
                                      const el = ledgerInputRefs.current.get(row.id);
                                      if (el && document.activeElement !== el) el.focus();
                                    }
                                    return p.map(x => x.id === row.id ? { ...x, open: false } : x);
                                  });
                                }, 200)}
                                placeholder="Type to search ledger…"
                                title={row.search.trim() && !row.ledger_id ? 'Pick a ledger from the dropdown — free text is not allowed' : undefined}
                                className="flex-1 text-sm py-0.5 px-2 focus:outline-none min-w-0 bg-transparent" />
                              <ChevronDown size={12} className="mr-1 text-gray-400 flex-shrink-0" />
                            </div>
                            {row.open && row.search.trim().length >= 1 && (
                              <AnchoredList maxH={240}>
                                {ledgerOptions(row.search).map((l, lIdx) => (
                                  <div key={l.id} onPointerDown={() => selectLedger(row.id, l)}
                                    className={`px-2 py-1.5 text-sm cursor-pointer ${lIdx === activeDropIdx ? 'bg-blue-600 text-white' : 'hover:bg-blue-50'}`}>{l.company}</div>
                                ))}
                                {ledgerOptions(row.search).length === 0 && (
                                  <div className="px-2 py-2 text-xs text-gray-400">No accounts found</div>
                                )}
                              </AnchoredList>
                            )}
                          </div>
                        </td>
                        <td className="py-1 px-2 text-right">
                          <input type="number" step="any" value={row.amount || ''}
                            onKeyDown={handleKeyDown} data-row={rIdx} data-field="ledger-amount"
                            onChange={e => updateLedgerRow(row.id, { amount: Number(e.target.value) || 0 })}
                            className="w-24 border border-gray-200 rounded text-sm py-0.5 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium text-gray-700" />
                        </td>
                        <td colSpan={trailingCols} className="py-1 px-1">
                          {!row.auto && (
                            <button onClick={() => removeLedgerRow(row.id)} className="text-red-400 hover:text-red-600"><X size={13} /></button>
                          )}
                        </td>
                      </tr>
                    ))}

                    {/* Add Ledger row */}
                    {!readOnly && (
                    <tr className="border-t border-gray-100">
                      <td colSpan={4 + 1 + trailingCols} className="py-1 px-2">
                        <button onClick={addLedgerRow}
                          onKeyDown={handleKeyDown}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                          <Plus size={12} /> Add Ledger
                        </button>
                      </td>
                    </tr>
                    )}

                  </tfoot>
                );
              })()}
            </table>
          </div>
        </div>

        </fieldset>
        </div>{/* end scroll region */}

      {/* Remark + Grand Total — fixed footer of the card (item mode). Sits after
          the flex-1 scroll region so it's always visible while the form body
          scrolls. Remark fills the left; the total is measured to line up under
          the table's AMOUNT column. */}
      {!isJournalType && !isStockJournal && (
        <div ref={bottomBarRef} className="flex-none -mx-4 -mb-4 px-4 py-2.5 bg-gray-50 border-t-2 border-gray-300 shadow-[0_-2px_6px_rgba(0,0,0,0.06)] flex items-center gap-4 z-20">
          <input type="text" value={remark} onChange={e => setRemark(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Remark (optional)"
            disabled={readOnly}
            className="flex-1 min-w-0 border border-gray-300 rounded text-sm py-1.5 px-3 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-500" />
          <span className="text-sm font-bold text-gray-800 flex-shrink-0">Grand Total</span>
          <div className="text-right flex-shrink-0" style={{ paddingRight: amtPadRight }}>
            {customerBillByBill ? (
              <button onClick={openCustomerBillAlloc}
                className={`text-lg font-bold underline decoration-dotted ${billAllocBalanced ? 'text-green-600' : 'text-orange-500'}`}
                title="Click to allocate bill">
                {fmt(grandTotal)}
              </button>
            ) : (
              <span className="text-lg font-bold text-blue-600">{fmt(grandTotal)}</span>
            )}
          </div>
        </div>
      )}
      </div>{/* end main form card */}
    </div>{/* end desktop layout */}

    {/* Cloud-Activity Picker Popup — opens when a Cloud-category item is selected */}
      {cloudPopup && (() => {
        // Credit Note rows show as positive here too, so the picker matches
        // what actually lands on the voucher line (Sales stays untouched).
        const getAmt = (a: any) => {
          const amt = Number(a.bill_amount || 0);
          return cloudPopup.isCreditNote ? Math.abs(amt) : amt;
        };
        const total = cloudPopup.activities
          .filter(a => cloudPopup.selectedIds.has(String(a.id)))
          .reduce((s, a) => s + getAmt(a), 0);
        const allOnPage = cloudPopup.activities.length;
        const allChecked = allOnPage > 0 && cloudPopup.activities.every(a => cloudPopup.selectedIds.has(String(a.id)));
        const toggleAll = () => {
          setCloudPopup(prev => {
            if (!prev) return prev;
            const next = new Set<string>();
            if (!allChecked) prev.activities.forEach(a => next.add(String(a.id)));
            return { ...prev, selectedIds: next };
          });
        };
        return (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div ref={cloudPopupRef} className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col">
              <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100">
                <div>
                  <h3 className="font-semibold text-gray-800">{cloudPopup.isCreditNote ? 'Cloud Credit Note Activities' : 'Cloud Billing Activities'} — {partyDisplay}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{cloudPopup.isCreditNote ? 'Pick the credit note activities for this voucher. The amount fills automatically.' : 'Pick the activities to bill on this voucher. The amount fills automatically.'}</p>
                </div>
                <button onClick={() => setCloudPopup(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
              </div>
              <div className="flex-1 overflow-auto p-3">
                {cloudPopup.loading ? (
                  <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
                ) : cloudPopup.activities.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm">{cloudPopup.isCreditNote ? 'No pending credit note activities for this customer.' : 'No pending billing activities for this customer.'}</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase">
                      <tr>
                        <th className="py-2 px-2 text-left w-8">
                          <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                        </th>
                        <th className="py-2 px-2 text-left">Date</th>
                        <th className="py-2 px-2 text-left">Type</th>
                        <th className="py-2 px-2 text-left">Server</th>
                        <th className="py-2 px-2 text-left">Domain / IP</th>
                        <th className="py-2 px-2 text-left">SOF No.</th>
                        <th className="py-2 px-2 text-left">Cycle</th>
                        <th className="py-2 px-2 text-left">Period</th>
                        <th className="py-2 px-2 text-right">Units</th>
                        <th className="py-2 px-2 text-right">Rate</th>
                        <th className="py-2 px-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cloudPopup.activities.map((a, i) => {
                        const id = String(a.id);
                        const checked = cloudPopup.selectedIds.has(id);
                        const fmtShort = (d: any) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
                        const period = (a.start_from || a.new_expiry_date)
                          ? `${fmtShort(a.start_from)}${a.start_from && a.new_expiry_date ? ' → ' : ''}${fmtShort(a.new_expiry_date)}`
                          : '—';
                        const fmtAmt = (n: any) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return (
                          <tr key={id} onClick={() => toggleCloudActivity(id)}
                            className={`border-t border-gray-100 cursor-pointer ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                            <td className="py-2 px-2"><input type="checkbox" checked={checked} onChange={() => toggleCloudActivity(id)} onClick={e => e.stopPropagation()} /></td>
                            <td className="py-2 px-2 text-gray-600 whitespace-nowrap">{a.activity_date}</td>
                            <td className="py-2 px-2 text-gray-600 whitespace-nowrap">{a.activity_type}{a.bill_type === 'Credit Note' ? ' (Cr)' : ''}</td>
                            <td className="py-2 px-2 text-gray-700 truncate max-w-[180px]" title={a.server_name || ''}>{a.server_name || '—'}</td>
                            <td className="py-2 px-2 text-gray-600 truncate max-w-[140px]" title={a.customer_domain_ip || ''}>{a.customer_domain_ip || '—'}</td>
                            <td className="py-2 px-2 text-gray-600">{a.sof_no || '—'}</td>
                            <td className="py-2 px-2 text-gray-600 whitespace-nowrap">{a.billing_cycle || '—'}</td>
                            <td className="py-2 px-2 text-gray-600 whitespace-nowrap text-[12px]">{period}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{a.billing_units != null ? (cloudPopup.isCreditNote ? Math.abs(Number(a.billing_units)) : a.billing_units) : '—'}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{a.last_bill_rate != null ? fmtAmt(a.last_bill_rate) : '—'}</td>
                            <td className="py-2 px-2 text-right tabular-nums font-medium whitespace-nowrap">{fmtAmt(getAmt(a))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
                <div className="text-sm">
                  <span className="text-gray-500">Selected:</span>{' '}
                  <span className="font-semibold text-gray-800 tabular-nums">{cloudPopup.selectedIds.size}</span>
                  <span className="mx-2 text-gray-300">•</span>
                  <span className="text-gray-500">Total Amount:</span>{' '}
                  <span className="font-semibold text-blue-700 tabular-nums">₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setCloudPopup(null)} className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 text-gray-600">Cancel</button>
                  <button onClick={applyCloudSelection} disabled={cloudPopup.selectedIds.size === 0}
                    className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded">
                    Add Selected
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Batch Entry Popup */}
      {batchPopupIdx !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center pb-16 sm:pb-0 sm:p-4">
          <div ref={batchPopupRef} className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-xl flex flex-col h-[60vh] max-h-[92vh]">
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">Batch / Serial Entry</p>
                <h3 className="font-semibold text-gray-800 text-sm leading-tight">
                  {lines[batchPopupIdx]?.item_name || 'Item'}
                </h3>
              </div>
              <button onClick={() => setBatchPopupIdx(null)} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>

            {!isPurchaseMode && batchNoFlavour && (
              <div className="mx-4 mt-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800 flex-shrink-0">
                Flavour not set for this item — go to <strong>Items</strong> page and set the Tally Flavour so serials can be filtered.
              </div>
            )}

            {/* Desktop: original table layout */}
            <div className="hidden sm:flex flex-col flex-1 overflow-hidden px-4 pt-4 pb-2">
              <div className="overflow-y-auto flex-1" ref={batchScrollRef}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="text-[11px] text-gray-500 uppercase bg-gray-50">
                    <th className="py-1.5 px-2 text-left w-8">#</th>
                    <th className="py-1.5 px-2 text-left">Serial / Batch No.</th>
                    <th className="py-1.5 px-2 text-right w-20">Qty</th>
                    <th className="py-1.5 px-2 text-right w-24">Rate</th>
                    <th className="py-1.5 px-2 text-right w-28">Amount</th>
                    <th className="py-1.5 px-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {batchDraft.map((row, i) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="py-1 px-2 text-gray-400 text-xs">{i + 1}</td>
                      <td className="py-1 px-1">
                        {isPurchaseMode ? (
                          <input type="text" onKeyDown={handleKeyDown} data-row={i} data-field="batch_name"
                            value={row.batch_name}
                            onChange={e => setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, batch_name: e.target.value } : r))}
                            placeholder="Enter serial no."
                            className="w-full border border-gray-200 rounded text-sm py-1 px-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                        ) : (
                          <div className="relative">
                            <input type="text" data-row={i} data-field="batch_search"
                              value={row.serialSearch ?? row.batch_name}
                              onChange={e => { setBatchSerialHiIdx(-1); setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, serialSearch: e.target.value, batch_name: e.target.value, serialOpen: true } : r)); }}
                              onFocus={e => {
                                setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, serialOpen: true } : r));
                                setBatchSerialHiIdx(-1);
                                setTimeout(() => {
                                  const scroller = batchScrollRef.current;
                                  const el = e.target;
                                  if (scroller && el) {
                                    const scrollerRect = scroller.getBoundingClientRect();
                                    const elRect = el.getBoundingClientRect();
                                    if (elRect.bottom > scrollerRect.bottom - 160) {
                                      scroller.scrollTop += elRect.bottom - scrollerRect.bottom + 160;
                                    }
                                  }
                                }, 30);
                              }}
                              onBlur={() => setTimeout(() => { setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, serialOpen: false } : r)); setBatchSerialHiIdx(-1); }, 150)}
                              onKeyDown={e => {
                                const usedByOthers = new Set(batchDraft.filter(r => r.id !== row.id && r.batch_name).map(r => r.batch_name));
                                const visible = batchSerials.filter(s => !usedByOthers.has(s) && (!row.serialSearch || s.toLowerCase().includes((row.serialSearch || '').toLowerCase())));
                                if (e.key === 'ArrowDown') { e.preventDefault(); setBatchSerialHiIdx(h => Math.min(h + 1, visible.length - 1)); }
                                else if (e.key === 'ArrowUp') { e.preventDefault(); setBatchSerialHiIdx(h => Math.max(h - 1, -1)); }
                                else if (e.key === 'Enter' && batchSerialHiIdx >= 0 && visible[batchSerialHiIdx]) {
                                  e.preventDefault();
                                  const s = visible[batchSerialHiIdx];
                                  setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, batch_name: s, serialSearch: s, serialOpen: false } : r));
                                  setBatchSerialHiIdx(-1);
                                } else { handleKeyDown(e as any); }
                              }}
                              placeholder="Search serial no."
                              className="w-full border border-gray-200 rounded text-sm py-1 px-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            {row.serialOpen && (
                              <div className="absolute z-30 left-0 right-0 bg-white border border-gray-200 rounded shadow-lg mt-0.5 max-h-40 overflow-y-auto">
                                {(() => {
                                  const usedByOthers = new Set(batchDraft.filter(r => r.id !== row.id && r.batch_name).map(r => r.batch_name));
                                  const visible = batchSerials.filter(s => !usedByOthers.has(s) && (!row.serialSearch || s.toLowerCase().includes((row.serialSearch || '').toLowerCase())));
                                  return visible.length > 0 ? visible.map((s, si) => (
                                    <div key={s} onPointerDown={() => { setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, batch_name: s, serialSearch: s, serialOpen: false } : r)); setBatchSerialHiIdx(-1); }}
                                      className={`px-2 py-1.5 text-sm cursor-pointer ${si === batchSerialHiIdx ? 'bg-blue-600 text-white' : 'hover:bg-blue-50'}`}>{s}</div>
                                  )) : (
                                    <div className="px-2 py-2 text-xs text-gray-400">
                                      {batchNoFlavour ? 'Set flavour for this item in Items page' : batchSerials.length === 0 ? 'No serials found' : 'No match'}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-1 px-1">
                        <input type="number" step="any" value={row.qty || ''} onKeyDown={handleKeyDown} data-row={i} data-field="batch_qty"
                          onChange={e => { const qty = Number(e.target.value) || 0; setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, qty, amount: +(qty * r.rate).toFixed(2) } : r)); }}
                          className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400" />
                      </td>
                      <td className="py-1 px-1">
                        <input type="number" step="any" value={row.rate || ''} onKeyDown={handleKeyDown} data-row={i} data-field="batch_rate"
                          onChange={e => { const rate = Number(e.target.value) || 0; setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, rate, amount: +(r.qty * rate).toFixed(2) } : r)); }}
                          className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400" />
                      </td>
                      <td className="py-1 px-1">
                        <input type="number" step="any" value={row.amount || ''} onKeyDown={handleKeyDown} data-row={i} data-field="batch_amount"
                          onChange={e => { const amount = Number(e.target.value) || 0; setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, amount, rate: r.qty > 0 ? +(amount / r.qty).toFixed(4) : r.rate } : r)); }}
                          className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium" />
                      </td>
                      <td className="py-1 px-1 text-center">
                        {batchDraft.length > 1 && (
                          <button onClick={() => setBatchDraft(d => d.filter(r => r.id !== row.id))} className="text-red-400 hover:text-red-600"><X size={13} /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {!readOnly && (
                <button onClick={() => setBatchDraft(d => [...d, { id: uid(), batch_name: '', qty: 0, rate: 0, amount: 0, serialSearch: '', serialOpen: false }])}
                  onKeyDown={handleKeyDown} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 mt-2 flex-shrink-0">
                  <Plus size={12} /> Add Serial No.
                </button>
              )}
            </div>

            {/* Mobile: card layout */}
            <div className="sm:hidden p-4 overflow-y-auto flex-1 space-y-3">
              {batchDraft.map((row, i) => (
                <div key={row.id} className="border border-gray-200 rounded-xl p-3 space-y-2.5 bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400 w-5">{i + 1}</span>
                    {batchDraft.length > 1 && (
                      <button onClick={() => setBatchDraft(d => d.filter(r => r.id !== row.id))}
                        className="ml-auto text-red-400 hover:text-red-600 p-0.5"><X size={15} /></button>
                    )}
                  </div>
                  {isPurchaseMode ? (
                    <input type="text" value={row.batch_name} onKeyDown={handleKeyDown} data-row={i} data-field="batch_name"
                      onChange={e => setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, batch_name: e.target.value } : r))}
                      placeholder="Enter serial / batch no."
                      className="w-full border border-gray-300 rounded-lg text-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                  ) : (
                    <div>
                      <input type="text" onKeyDown={handleKeyDown} data-row={i} data-field="batch_search"
                        value={row.serialSearch ?? row.batch_name}
                        onChange={e => setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, serialSearch: e.target.value, batch_name: e.target.value, serialOpen: true } : r))}
                        onFocus={() => setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, serialOpen: true } : r))}
                        onBlur={() => setTimeout(() => setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, serialOpen: false } : r)), 150)}
                        placeholder="Search serial no."
                        className="w-full border border-gray-300 rounded-lg text-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                      {row.serialOpen && (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-md mt-1 max-h-40 overflow-y-auto">
                          {(() => {
                            const usedByOthers = new Set(batchDraft.filter(r => r.id !== row.id && r.batch_name).map(r => r.batch_name));
                            const visible = batchSerials.filter(s => !usedByOthers.has(s) && (!row.serialSearch || s.toLowerCase().includes((row.serialSearch || '').toLowerCase())));
                            return visible.length > 0 ? visible.map(s => (
                              <div key={s} onPointerDown={() => setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, batch_name: s, serialSearch: s, serialOpen: false } : r))}
                                className="px-3 py-2.5 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">{s}</div>
                            )) : (
                              <div className="px-3 py-3 text-xs text-gray-400 text-center">
                                {batchNoFlavour ? 'Set flavour in Items page to filter serials' : batchSerials.length === 0 ? 'No serials found for this customer' : 'No match'}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-1">Qty</label>
                      <input type="number" inputMode="decimal" step="any" value={row.qty || ''} onKeyDown={handleKeyDown} data-row={i} data-field="batch_qty"
                        onChange={e => { const qty = Number(e.target.value) || 0; setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, qty, amount: +(qty * r.rate).toFixed(2) } : r)); }}
                        className="w-full border border-gray-300 rounded-lg text-sm py-2 px-2 text-right focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-1">Rate</label>
                      <input type="number" inputMode="decimal" step="any" value={row.rate || ''} onKeyDown={handleKeyDown} data-row={i} data-field="batch_rate"
                        onChange={e => { const rate = Number(e.target.value) || 0; setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, rate, amount: +(r.qty * rate).toFixed(2) } : r)); }}
                        className="w-full border border-gray-300 rounded-lg text-sm py-2 px-2 text-right focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-1">Amount</label>
                      <input type="number" inputMode="decimal" step="any" value={row.amount || ''} onKeyDown={handleKeyDown} data-row={i} data-field="batch_amount"
                        onChange={e => { const amount = Number(e.target.value) || 0; setBatchDraft(d => d.map(r => r.id === row.id ? { ...r, amount, rate: r.qty > 0 ? +(amount / r.qty).toFixed(4) : r.rate } : r)); }}
                        className="w-full border border-gray-300 rounded-lg text-sm py-2 px-2 text-right focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-medium" />
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button onClick={() => setBatchDraft(d => [...d, { id: uid(), batch_name: '', qty: 0, rate: 0, amount: 0, serialSearch: '', serialOpen: false }])}
                  onKeyDown={handleKeyDown} className="flex items-center gap-1.5 text-sm text-green-600 hover:text-green-800 py-1">
                  <Plus size={15} /> Add Serial No.
                </button>
              )}
            </div>

            {/* Footer — desktop compact / mobile stacked */}
            <div className="border-t border-gray-100 bg-gray-50 rounded-b-2xl sm:rounded-b-lg flex-shrink-0">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex gap-4 text-xs text-gray-500">
                  <span>Total Qty: <strong className="text-gray-800">{batchDraft.reduce((s, r) => s + r.qty, 0).toFixed(3)}</strong></span>
                  <span>Total Amt: <strong className="text-gray-800">₹{batchDraft.reduce((s, r) => s + r.amount, 0).toFixed(2)}</strong></span>
                </div>
                <div className="flex gap-2">
                  {readOnly ? (
                    <button onClick={() => setBatchPopupIdx(null)}
                      className="px-4 py-1.5 text-sm bg-slate-600 hover:bg-slate-700 text-white rounded">Close</button>
                  ) : (
                    <>
                      <button onClick={() => setBatchPopupIdx(null)}
                        className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100">Cancel</button>
                      <button onClick={saveBatch}
                        className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">Save</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Customer Modal */}
      {showNewCustomer && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div ref={newCustomerPopupRef} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">New Customer</h3>
              <button onClick={() => setShowNewCustomer(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-0.5">Company Name *</label>
                <input autoFocus type="text" value={custForm.company}
                  onKeyDown={handleKeyDown}
                  onChange={e => setCustForm(f => ({ ...f, company: e.target.value }))}
                  placeholder="Full company name"
                  className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Mobile *</label>
                <input type="text" value={custForm.mobile}
                  onKeyDown={handleKeyDown}
                  onChange={e => setCustForm(f => ({ ...f, mobile: e.target.value }))}
                  placeholder="Phone number"
                  className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">GST IN</label>
                <input type="text" value={custForm.gstin}
                  onKeyDown={handleKeyDown}
                  onChange={e => setCustForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                  placeholder="Optional"
                  className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400 uppercase" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-0.5">Email</label>
                <input type="email" value={custForm.email}
                  onKeyDown={handleKeyDown}
                  onChange={e => setCustForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="Optional"
                  className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Pincode</label>
                <input type="text" value={custForm.pincode}
                  onKeyDown={handleKeyDown}
                  onChange={async e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCustForm(f => ({ ...f, pincode: v, area: '', state: '' }));
                    if (v.length === 6) {
                      try {
                        const { pincodeApi: pa } = await import('../services/api');
                        const res = await pa.lookup(v);
                        if (res.city) setCustForm(f => ({ ...f, area: res.city, state: res.state }));
                      } catch {}
                    }
                  }}
                  placeholder="6 digits"
                  className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Address Line 1</label>
                <input type="text" value={custForm.address1}
                  onKeyDown={handleKeyDown}
                  onChange={e => setCustForm(f => ({ ...f, address1: e.target.value }))}
                  placeholder="Building / Floor"
                  className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Address Line 2</label>
                <input type="text" value={custForm.address2}
                  onKeyDown={handleKeyDown}
                  onChange={e => setCustForm(f => ({ ...f, address2: e.target.value }))}
                  placeholder="Street / Area"
                  className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">City (auto)</label>
                <input readOnly value={custForm.area}
                  className="w-full border border-gray-100 rounded text-sm py-1.5 px-2 bg-gray-50 text-gray-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">State (auto)</label>
                <input readOnly value={custForm.state}
                  className="w-full border border-gray-100 rounded text-sm py-1.5 px-2 bg-gray-50 text-gray-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-0.5">Ledger Group</label>
                <select value={custForm.ledgergroup}
                  onChange={e => setCustForm(f => ({ ...f, ledgergroup: Number(e.target.value) || SUNDRY_DEBTORS_ID }))}
                  className="w-full border border-gray-300 rounded text-sm py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white">
                  {ledgerGroups.length === 0 && <option value={SUNDRY_DEBTORS_ID}>Sundry Debtors</option>}
                  {ledgerGroups.map((g: any) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-0.5">Which ledger group this party files under in Tally — usually Sundry Debtors.</p>
              </div>
            </div>
            <div className="px-4 pb-4">
              <button onClick={handleCreateCustomer} disabled={creatingCustomer}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded py-2">
                {creatingCustomer ? 'Creating...' : 'Create Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Allocation Popup */}
      {billAllocOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center pb-16 sm:pb-0 sm:p-4">
          <div ref={billAllocPopupRef} className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-2xl flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">Bill Allocation</p>
                <h3 className="font-semibold text-gray-800 text-sm leading-tight">
                  {isJournalType
                    ? (activeJournalRow?.ledger_name || '—')
                    : partyDisplay}
                </h3>
              </div>
              <button onClick={() => closeBillAlloc()} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>

            {/* Manual vs Auto (FIFO) allocation */}
            <div className="px-4 pt-3 flex items-center gap-2 flex-shrink-0">
              <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mr-0.5">Allocate</span>
              <button type="button" onClick={setManualAllocation}
                className="px-3 py-1 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                title="Pick bills yourself from the Pending list">
                Manual
              </button>
              <button type="button" onClick={autoAllocateFifo}
                className="px-3 py-1 text-xs font-semibold rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100"
                title="Auto-settle the oldest open bills first (FIFO)">
                Auto (FIFO)
              </button>
            </div>

            {/* Two-partition quick picker: PENDING bills (click to select) ⇄
                SELECTED bills (click to remove). Makes allocation a click, and
                shows at a glance what's still open vs already picked. */}
            {(() => {
              // The picker is only relevant when allocating against existing
              // pending bills (Agr.). Hide it once the user chooses New or
              // On Account for any row.
              const hasNewOrOnAccount = billAllocEntries.some(e => e.type === 'New' || e.type === 'On Account');
              if (hasNewOrOnAccount) return null;
              const selectedNames = new Set(billAllocEntries.filter(e => e.refno).map(e => e.refno));
              // Oldest → newest so the earliest open bills are settled first.
              const pendingAvailable = pendingRefs
                .filter(p => !selectedNames.has(p.billname))
                .slice()
                .sort((a, b) => {
                  const ta = a.vch_date ? new Date(a.vch_date).getTime() : Infinity;
                  const tb = b.vch_date ? new Date(b.vch_date).getTime() : Infinity;
                  return ta - tb;
                });
              const selectedEntries = billAllocEntries.filter(e => e.type === 'Agr.' && e.refno);
              const money = (n: any) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
              const fmtD = (s: any) => { if (!s) return '—'; const d = new Date(s); return isNaN(d.getTime()) ? String(s).slice(0, 10) : d.toLocaleDateString('en-GB'); };
              return (
                <div className="px-4 pt-3 flex-shrink-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Pending */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-2.5 py-1.5 bg-amber-50 border-b border-amber-100 text-[11px] font-bold uppercase tracking-wide text-amber-700 flex items-center justify-between">
                        <span>Pending Bills</span><span className="tabular-nums">{pendingAvailable.length}</span>
                      </div>
                      <div className="max-h-44 overflow-y-auto divide-y divide-gray-50">
                        {pendingAvailable.length === 0 ? (
                          <div className="px-2 py-4 text-xs text-gray-400 text-center">Nothing pending</div>
                        ) : pendingAvailable.map(p => (
                          <button type="button" key={p.billname} onClick={() => pickPendingBill(p)}
                            title={`Add ${p.billname}`}
                            className="w-full flex items-center gap-1.5 px-2 py-1.5 hover:bg-amber-50 text-left">
                            <Plus size={12} className="text-amber-500 flex-shrink-0" />
                            <span className="flex-1 min-w-0 text-xs font-medium text-gray-800 truncate">{p.billname}</span>
                            <span className="w-[72px] flex-shrink-0 text-[10px] text-gray-400 tabular-nums text-center">{fmtD(p.vch_date)}</span>
                            <span className="w-[84px] flex-shrink-0 text-[11px] text-gray-600 tabular-nums text-right">{money(p.amount)}</span>
                            <span className={`w-6 flex-shrink-0 text-[10px] font-semibold text-center ${p.direction === 'Cr' ? 'text-green-600' : 'text-red-500'}`}>{p.direction}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Selected */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-2.5 py-1.5 bg-blue-50 border-b border-blue-100 text-[11px] font-bold uppercase tracking-wide text-blue-700 flex items-center justify-between">
                        <span>Selected in this bill</span><span className="tabular-nums">{selectedEntries.length}</span>
                      </div>
                      <div className="max-h-44 overflow-y-auto divide-y divide-gray-50">
                        {selectedEntries.length === 0 ? (
                          <div className="px-2 py-4 text-xs text-gray-400 text-center">Click a pending bill to add</div>
                        ) : selectedEntries.map(e => (
                          <button type="button" key={e.id} onClick={() => removeSelectedBill(e.id)}
                            title={`Remove ${e.refno}`}
                            className="w-full flex items-center gap-1.5 px-2 py-1.5 hover:bg-red-50 text-left">
                            <X size={12} className="text-red-400 flex-shrink-0" />
                            <span className="flex-1 min-w-0 text-xs font-medium text-gray-800 truncate">{e.refno}</span>
                            <span className="w-[84px] flex-shrink-0 text-[11px] text-gray-700 tabular-nums text-right">{money(e.amount)}</span>
                            <span className={`w-6 flex-shrink-0 text-[10px] font-semibold text-center ${e.direction === 'Cr' ? 'text-green-600' : 'text-red-500'}`}>{e.direction}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Desktop: allocation table (the "Selected bills" detail — editable
                amounts, New / On Account, Cr/Dr). Scrolls within the modal. */}
            <div className="hidden sm:block p-4 overflow-y-auto flex-1 min-h-0">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1">Allocation detail</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] text-gray-500 uppercase bg-gray-50">
                    <th className="py-1.5 px-2 text-left w-8">#</th>
                    <th className="py-1.5 px-2 text-left w-28">Type</th>
                    <th className="py-1.5 px-2 text-left">Ref / Bill No.</th>
                    <th className="py-1.5 px-2 text-right w-28">Amount</th>
                    <th className="py-1.5 px-2 text-center w-12">Cr/Dr</th>
                    <th className="py-1.5 px-2 w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {billAllocEntries.map((entry, i) => (
                    <tr key={entry.id} className="border-t border-gray-100">
                      <td className="py-1 px-2 text-gray-400 text-xs">{i + 1}</td>
                      <td className="py-1 px-1">
                        <select value={entry.type} onKeyDown={handleKeyDown} data-row={i} data-field="alloc_type"
                          onChange={e => setBillAllocEntries(d => {
                            const newType = e.target.value as any;
                            const currentBalance = +(signedGrandTotal - d.filter(r => r.id !== entry.id).reduce((s, r) => s + (r.direction === 'Cr' ? -(Number(r.amount)||0) : (Number(r.amount)||0)), 0)).toFixed(2);
                            const autoDir = currentBalance >= 0 ? 'Dr' : 'Cr';
                            const autoAmt = Math.abs(currentBalance);
                            return d.map(r => r.id === entry.id
                              ? newType === 'New' ? { ...r, type: newType, amount: autoAmt, direction: autoDir }
                              : newType === 'On Account' ? { ...r, type: newType, refno: '', refSearch: '' }
                              : { ...r, type: newType } : r);
                          })}
                          className="w-full border border-gray-200 rounded text-sm py-1 px-1 focus:outline-none focus:ring-1 focus:ring-blue-400">
                          <option value="New">New</option>
                          <option value="Agr.">Agr.</option>
                          <option value="On Account">On Account</option>
                        </select>
                      </td>
                      <td className="py-1 px-1">
                        {entry.type === 'On Account' ? (
                          <span className="text-xs text-gray-400 px-1">—</span>
                        ) : entry.type === 'New' ? (
                          <input type="text" value={entry.refno} onKeyDown={handleKeyDown} data-row={i} data-field="alloc_ref"
                            onChange={e => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, refno: e.target.value } : r))}
                            placeholder="Reference / Bill No."
                            className="w-full border border-gray-200 rounded text-sm py-1 px-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                        ) : (
                          <div className="relative">
                            <input type="text" onKeyDown={handleKeyDown} data-row={i} data-field="alloc_search"
                              value={entry.refSearch ?? entry.refno}
                              onChange={e => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, refSearch: e.target.value, refno: e.target.value, refOpen: true } : r))}
                              onFocus={() => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, refOpen: true } : r))}
                              onBlur={() => setTimeout(() => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, refOpen: false } : r)), 150)}
                              placeholder="Search pending bill..."
                              className="w-full border border-gray-200 rounded text-sm py-1 px-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            {entry.refOpen && (
                              <div className="absolute z-30 left-0 right-0 bg-white border border-gray-200 rounded shadow-lg mt-0.5 overflow-hidden" style={{minWidth:'360px'}}>
                                <div className="grid grid-cols-4 gap-0 bg-gray-100 border-b border-gray-200 px-2 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                                  <span>Bill No.</span><span>Date</span><span className="text-right">Amount</span><span className="text-center">Cr/Dr</span>
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {pendingRefs
                                    .filter(p => !billAllocEntries.some(r => r.id !== entry.id && r.refno === p.billname))
                                    .filter(p => !entry.refSearch || p.billname.toLowerCase().includes((entry.refSearch || '').toLowerCase()))
                                    .map(p => { const dir = p.direction || (Number(p.amount) > 0 ? 'Dr' : 'Cr'); return (
                                      <div key={p.billname}
                                        onPointerDown={() => setBillAllocEntries(d => {
                                          const otherAllocated = d.filter(r => r.id !== entry.id).reduce((s, r) => s + (Number(r.amount) || 0), 0);
                                          const remaining = Math.max(0, effectiveGrandTotal - otherAllocated);
                                          const autoAmount = +Math.min(Number(p.amount), remaining).toFixed(2);
                                          const billDir = p.direction || (Number(p.amount) > 0 ? 'Dr' : 'Cr');
                                          const settleDir = billDir === 'Dr' ? 'Cr' : 'Dr';
                                          return d.map(r => r.id === entry.id ? { ...r, refno: p.billname, refSearch: p.billname, amount: autoAmount, direction: settleDir, refOpen: false } : r);
                                        })}
                                        className="grid grid-cols-4 gap-0 px-2 py-1.5 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                                        <span className="text-sm font-medium text-gray-800 truncate">{p.billname}</span>
                                        <span className="text-xs text-gray-500 self-center">{p.vch_date}</span>
                                        <span className="text-xs text-gray-800 font-medium text-right self-center">₹{Number(p.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        <span className={`text-xs font-semibold text-center self-center ${dir === 'Cr' ? 'text-green-600' : 'text-red-500'}`}>{dir}</span>
                                      </div>
                                    );})}
                                  {pendingRefs.filter(p => !billAllocEntries.some(r => r.id !== entry.id && r.refno === p.billname)).filter(p => !entry.refSearch || p.billname.toLowerCase().includes((entry.refSearch || '').toLowerCase())).length === 0 && (
                                    <div className="px-2 py-2 text-xs text-gray-400">No pending bills found</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-1 px-1">
                        <input type="number" step="any" value={entry.amount || ''} onKeyDown={handleKeyDown} data-row={i} data-field="alloc_amount"
                          onChange={e => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, amount: Number(e.target.value) || 0 } : r))}
                          className="w-full border border-gray-200 rounded text-sm py-1 px-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium" />
                      </td>
                      <td className="py-1 px-1 text-center">
                        {(() => {
                          const dir = entry.direction || pendingRefs.find(p => p.billname === entry.refno)?.direction;
                          return dir ? (
                            <button onClick={() => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, direction: r.direction === 'Cr' ? 'Dr' : 'Cr' } : r))}
                              className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${dir === 'Cr' ? 'text-green-600 border-green-300 hover:bg-green-50' : 'text-red-500 border-red-300 hover:bg-red-50'}`}
                              title="Click to toggle Cr/Dr">{dir}</button>
                          ) : <span className="text-xs text-gray-300">—</span>;
                        })()}
                      </td>
                      <td className="py-1 px-1 text-center">
                        {/* Always removable — clearing the last row resets it to
                            a blank ref so an incorrect/stray reference can be
                            removed even when it's the only allocation row. */}
                        <button onClick={() => setBillAllocEntries(d => d.length > 1
                          ? d.filter(r => r.id !== entry.id)
                          : [{ id: uid(), type: 'New', refno: '', amount: 0, direction: partyDir }])}
                          title="Remove reference" className="text-red-400 hover:text-red-600"><X size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={() => setBillAllocEntries(d => {
                const usedSigned = d.reduce((s, r) => s + (r.direction === 'Cr' ? -(Number(r.amount)||0) : (Number(r.amount)||0)), 0);
                const remaining = +(signedGrandTotal - usedSigned).toFixed(2);
                const autoDir = remaining >= 0 ? 'Dr' : 'Cr';
                return [...d, { id: uid(), type: 'New', refno: '', amount: Math.abs(remaining), direction: autoDir }];
              })} onKeyDown={handleKeyDown} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 mt-2">
                <Plus size={12} /> Add Reference
              </button>
            </div>

            {/* Mobile: card layout */}
            <div className="sm:hidden px-3 py-3 overflow-y-auto flex-1 space-y-2">
              {billAllocEntries.map((entry, i) => (
                <div key={entry.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {/* Row 1: # | Segmented type toggle | Dr/Cr | Delete */}
                  <div className="flex items-center gap-2 px-3 py-2.5">
                    <span className="text-[10px] text-gray-400 w-4 shrink-0">{i + 1}</span>
                    <div className="flex-1 flex rounded-lg border border-gray-200 overflow-hidden text-xs bg-gray-50">
                      {(['New', 'Agr.', 'On Account'] as const).map(t => (
                        <button key={t} type="button" onKeyDown={handleKeyDown}
                          onClick={() => setBillAllocEntries(d => {
                            const currentBalance = +(signedGrandTotal - d.filter(r => r.id !== entry.id).reduce((s, r) => s + (r.direction === 'Cr' ? -(Number(r.amount)||0) : (Number(r.amount)||0)), 0)).toFixed(2);
                            const autoDir = currentBalance >= 0 ? 'Dr' : 'Cr';
                            const autoAmt = Math.abs(currentBalance);
                            return d.map(r => r.id === entry.id
                              ? t === 'New' ? { ...r, type: t, amount: autoAmt, direction: autoDir }
                              : t === 'On Account' ? { ...r, type: t, refno: '', refSearch: '' }
                              : { ...r, type: t } : r);
                          })}
                          className={`flex-1 py-1.5 font-semibold transition-colors border-r border-gray-200 last:border-r-0 ${
                            entry.type === t ? 'bg-blue-600 text-white' : 'text-gray-500'
                          }`}>
                          {t}
                        </button>
                      ))}
                    </div>
                    {(() => {
                      const dir = entry.direction || pendingRefs.find(p => p.billname === entry.refno)?.direction;
                      return dir ? (
                        <button type="button" onClick={() => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, direction: r.direction === 'Cr' ? 'Dr' : 'Cr' } : r))}
                          className={`text-xs font-bold px-2.5 py-1.5 rounded-lg border shrink-0 ${dir === 'Cr' ? 'text-green-600 border-green-200 bg-green-50' : 'text-red-500 border-red-200 bg-red-50'}`}>{dir}</button>
                      ) : null;
                    })()}
                    <button type="button" onClick={() => setBillAllocEntries(d => d.length > 1
                      ? d.filter(r => r.id !== entry.id)
                      : [{ id: uid(), type: 'New', refno: '', amount: 0, direction: partyDir }])}
                      title="Remove reference" className="text-red-400 p-0.5 shrink-0"><X size={14} /></button>
                  </div>
                  {/* Row 2: Ref. no. (left) | Amount (right) — side by side */}
                  <div className="flex border-t border-gray-100 divide-x divide-gray-100">
                    {entry.type !== 'On Account' && (
                      <div className="flex-1 px-3 py-2 relative min-w-0">
                        <p className="text-[9px] text-gray-400 uppercase tracking-wide mb-0.5">
                          {entry.type === 'New' ? 'Ref. No.' : 'Bill'}
                        </p>
                        {entry.type === 'New' ? (
                          <input type="text" value={entry.refno} onKeyDown={handleKeyDown} data-row={i} data-field="alloc_ref"
                            onChange={e => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, refno: e.target.value } : r))}
                            placeholder="e.g. INV-001"
                            className="w-full text-sm text-gray-800 bg-transparent border-none outline-none truncate" />
                        ) : (
                          <>
                            <input type="text" onKeyDown={handleKeyDown} data-row={i} data-field="alloc_search"
                              value={entry.refSearch ?? entry.refno}
                              onChange={e => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, refSearch: e.target.value, refno: e.target.value, refOpen: true } : r))}
                              onFocus={() => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, refOpen: true } : r))}
                              onBlur={() => setTimeout(() => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, refOpen: false } : r)), 150)}
                              placeholder="Search pending bill..."
                              className="w-full text-sm text-gray-800 bg-transparent border-none outline-none truncate" />
                            {entry.refOpen && (
                              <div className="absolute z-20 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" style={{ top: '100%' }}>
                                <div className="grid grid-cols-4 bg-gray-100 border-b border-gray-200 px-2 py-1.5 text-[9px] font-semibold text-gray-500 uppercase tracking-wide">
                                  <span>Bill</span><span>Date</span><span className="text-right">Amt</span><span className="text-center">D/C</span>
                                </div>
                                <div className="max-h-40 overflow-y-auto">
                                  {pendingRefs
                                    .filter(p => !billAllocEntries.some(r => r.id !== entry.id && r.refno === p.billname))
                                    .filter(p => !entry.refSearch || p.billname.toLowerCase().includes((entry.refSearch || '').toLowerCase()))
                                    .map(p => {
                                      const dir = p.direction || (Number(p.amount) > 0 ? 'Dr' : 'Cr');
                                      return (
                                        <div key={p.billname}
                                          onPointerDown={() => setBillAllocEntries(d => {
                                            const otherAllocated = d.filter(r => r.id !== entry.id).reduce((s, r) => s + (Number(r.amount) || 0), 0);
                                            const remaining = Math.max(0, effectiveGrandTotal - otherAllocated);
                                            const autoAmount = +Math.min(Number(p.amount), remaining).toFixed(2);
                                            const billDir = p.direction || (Number(p.amount) > 0 ? 'Dr' : 'Cr');
                                            const settleDir: 'Dr' | 'Cr' = billDir === 'Dr' ? 'Cr' : 'Dr';
                                            return d.map(r => r.id === entry.id ? { ...r, refno: p.billname, refSearch: p.billname, amount: autoAmount, direction: settleDir, refOpen: false } : r);
                                          })}
                                          className="grid grid-cols-4 px-2 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                                          <span className="text-xs font-semibold text-gray-800 truncate">{p.billname}</span>
                                          <span className="text-[10px] text-gray-500 self-center">{p.vch_date}</span>
                                          <span className="text-xs text-gray-800 font-medium text-right self-center">₹{Number(p.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                          <span className={`text-[10px] font-bold text-center self-center ${dir === 'Cr' ? 'text-green-600' : 'text-red-500'}`}>{dir}</span>
                                        </div>
                                      );
                                    })}
                                  {pendingRefs.filter(p => !billAllocEntries.some(r => r.id !== entry.id && r.refno === p.billname)).filter(p => !entry.refSearch || p.billname.toLowerCase().includes((entry.refSearch || '').toLowerCase())).length === 0 && (
                                    <div className="px-3 py-3 text-xs text-gray-400 text-center">No pending bills found</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {/* Amount — fixed width right column (full width for On Account) */}
                    <div className={`${entry.type !== 'On Account' ? 'w-[130px] shrink-0' : 'flex-1'} px-3 py-2 bg-blue-50 flex flex-col justify-center`}>
                      <p className="text-[9px] text-blue-400 uppercase tracking-wide mb-0.5">Amount</p>
                      <input type="number" inputMode="decimal" step="any" value={entry.amount || ''} onKeyDown={handleKeyDown} data-row={i} data-field="alloc_amount"
                        onChange={e => setBillAllocEntries(d => d.map(r => r.id === entry.id ? { ...r, amount: Number(e.target.value) || 0 } : r))}
                        className="w-full text-sm font-bold text-blue-700 bg-transparent border-none outline-none text-right" />
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setBillAllocEntries(d => {
                const usedSigned = d.reduce((s, r) => s + (r.direction === 'Cr' ? -(Number(r.amount)||0) : (Number(r.amount)||0)), 0);
                const remaining = +(signedGrandTotal - usedSigned).toFixed(2);
                const autoDir = remaining >= 0 ? 'Dr' : 'Cr';
                return [...d, { id: uid(), type: 'New', refno: '', amount: Math.abs(remaining), direction: autoDir }];
              })} onKeyDown={handleKeyDown} className="flex items-center gap-1.5 text-sm text-green-600 py-1.5">
                <Plus size={15} /> Add Reference
              </button>
            </div>

            {/* Footer — desktop: compact horizontal / mobile: stacked full-width */}
            <div className="border-t border-gray-100 bg-gray-50 rounded-b-2xl sm:rounded-b-lg flex-shrink-0">
              {/* Desktop footer */}
              <div className="hidden sm:flex items-center justify-between px-4 py-3">
                <div className="flex gap-4 text-xs">
                  <span className="text-gray-500">Grand Total: <strong className="text-gray-800">{fmt(effectiveGrandTotal)}</strong> <span className="text-[10px] font-semibold text-gray-400">{signedGrandTotal >= 0 ? 'Dr.' : 'Cr.'}</span></span>
                  <span className="text-gray-500">Allocated: <strong className={billAllocBalanced ? 'text-green-600' : 'text-orange-500'}>{fmt(Math.abs(billAllocSigned))}</strong> <span className={`text-[10px] font-semibold ${billAllocBalanced ? 'text-green-500' : 'text-orange-400'}`}>{billAllocSigned >= 0 ? 'Dr.' : 'Cr.'}</span></span>
                  <span className="text-gray-500">Balance: <strong className={billAllocBalanced ? 'text-green-600' : 'text-red-500'}>{fmt(Math.abs(billAllocBalance))}</strong> <span className={`text-[10px] font-semibold ${billAllocBalanced ? 'text-green-500' : 'text-red-400'}`}>{billAllocBalance >= 0 ? 'Dr.' : 'Cr.'}</span></span>
                </div>
                <button onClick={() => closeBillAlloc()} disabled={!billAllocBalanced}
                  title={!billAllocBalanced ? 'Allocated total must equal Grand Total' : undefined}
                  className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded">Done</button>
              </div>
              {/* Mobile footer */}
              <div className="sm:hidden px-4 py-3 space-y-2.5">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span className="text-gray-500">Total: <strong className="text-gray-800">₹{fmt(effectiveGrandTotal)}</strong> <span className="text-[10px] font-semibold text-gray-400">{signedGrandTotal >= 0 ? 'Dr.' : 'Cr.'}</span></span>
                  <span className="text-gray-500">Allocated: <strong className={billAllocBalanced ? 'text-green-600' : 'text-orange-500'}>₹{fmt(Math.abs(billAllocSigned))}</strong></span>
                  <span className="text-gray-500">Balance: <strong className={billAllocBalanced ? 'text-green-600' : 'text-red-500'}>₹{fmt(Math.abs(billAllocBalance))}</strong></span>
                </div>
                <button onClick={() => closeBillAlloc()} disabled={!billAllocBalanced}
                  title={!billAllocBalanced ? 'Allocated total must equal Grand Total' : undefined}
                  className="w-full py-2.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-xl font-medium">
                  {billAllocBalanced ? 'Done' : `Balance: ₹${fmt(Math.abs(billAllocBalance))} remaining`}
                </button>
              </div>
          </div>
        </div>
      </div>
    )}
    </div>

      {/* ── Print-only invoice preview layout ── */}
      {isSalesType && partyId && lines.some(l => l.product_id) && (
        <div className="print-only">
          <div className="invoice-page bg-white text-slate-900 mx-auto" style={{ maxWidth: '820px', minHeight: '1100px' }}>
            <style>{`
              @media print {
                @page { size: A4; margin: 10mm; }
                .invoice-page { box-shadow: none !important; max-width: 100% !important; }
              }
            `}</style>
            <div className="shadow-sm border border-slate-200 print:border-0 print:shadow-none p-8 print:p-0">
              {/* Header */}
              <div className="flex items-start gap-4 pb-4 border-b border-slate-200">
                {printCompany.logo_url && (
                  <img src={printCompany.logo_url} alt={printCompany.name}
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    className="w-20 h-20 object-contain flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-2xl font-bold text-emerald-800">{printCompany.name}</div>
                  <div className="text-[12px] text-slate-700 whitespace-pre-line">{printCompany.address}</div>
                  <div className="text-[12px] text-slate-700 mt-1">
                    ✉ {printCompany.email}  &nbsp;·&nbsp;  ☎ {printCompany.phone}
                  </div>
                  <div className="text-[12px] font-semibold text-slate-800 mt-0.5">GSTIN: {printCompany.gstin}</div>
                </div>
              </div>

              {/* Title */}
              <div className="text-center py-4">
                <div className="text-2xl font-bold text-emerald-800 tracking-wide">TAX INVOICE</div>
              </div>

              {/* Invoice + Bill To */}
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div className="border border-slate-200 rounded p-3 bg-slate-50/50">
                  <div className="text-[11px] font-bold text-slate-700 uppercase mb-2">Invoice Details</div>
                  <KV label="Invoice No."     value={voucherNo || '—'} />
                  <KV label="Invoice Date"    value={displayDate(voucherDate)} />
                  <KV label="Due Date"        value={displayDate(addDays(voucherDate, 15))} />
                  <KV label="Place of Supply" value={printBillTo.state || 'Assam'} />
                  <KV label="Reverse Charge"  value="No" />
                  <KV label="Payment Terms"   value="Due within 15 Days" />
                </div>
                <div className="border border-slate-200 rounded p-3 bg-slate-50/50">
                  <div className="text-[11px] font-bold text-slate-700 uppercase mb-2">Bill To</div>
                  <div className="font-semibold text-[14px] text-slate-900">{printBillTo.name || partyDisplay || '—'}</div>
                  {[printBillTo.address1, printBillTo.address2, [printBillTo.city, printBillTo.state, printBillTo.pincode].filter(Boolean).join(', ')].filter(Boolean).map((l, i) => (
                    <div key={i} className="text-[12px] text-slate-700">{l}</div>
                  ))}
                  <div className="mt-1.5 space-y-0.5">
                    {printBillTo.gstin   && <KV label="GSTIN"          value={printBillTo.gstin} />}
                    {printBillTo.contact && <KV label="Contact Person" value={printBillTo.contact} />}
                    {printBillTo.phone   && <KV label="Phone"          value={printBillTo.phone} />}
                    {printBillTo.email   && <KV label="Email"          value={printBillTo.email} />}
                  </div>
                </div>
              </div>

              {/* Items table */}
              <table className="w-full border-collapse text-[12px] mb-3">
                <thead>
                  <tr className="bg-emerald-800 text-white">
                    <th className="border border-emerald-900 px-2 py-2 text-left w-12">Sr. No.</th>
                    <th className="border border-emerald-900 px-2 py-2 text-left">Description</th>
                    <th className="border border-emerald-900 px-2 py-2 text-left w-20">SAC</th>
                    <th className="border border-emerald-900 px-2 py-2 text-left w-20">GST Rate</th>
                    <th className="border border-emerald-900 px-2 py-2 text-right w-14">Qty</th>
                    <th className="border border-emerald-900 px-2 py-2 text-right w-24">Rate (₹)</th>
                    <th className="border border-emerald-900 px-2 py-2 text-right w-28">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.filter(l => l.product_id).map((it, i) => {
                    const prod = products.find(p => String(p.id) === String(it.product_id));
                    return (
                      <tr key={i}>
                        <td className="border border-slate-200 px-2 py-1.5 text-center tabular-nums">{i + 1}.</td>
                        <td className="border border-slate-200 px-2 py-1.5">{it.item_name}</td>
                        <td className="border border-slate-200 px-2 py-1.5 tabular-nums">{prod?.hsn || '—'}</td>
                        <td className="border border-slate-200 px-2 py-1.5 tabular-nums">{it.gst_rate}%</td>
                        <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">{it.qty}</td>
                        <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">{fmt(it.rate)}</td>
                        <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">{fmt(it.amount)}</td>
                      </tr>
                    );
                  })}
                  {lines.filter(l => l.product_id).length > 0 && (
                    <>
                      <tr>
                        <td className="border border-slate-200 px-2 py-1.5 text-center tabular-nums">{lines.filter(l => l.product_id).length + 1}.</td>
                        <td colSpan={5} className="border border-slate-200 px-2 py-1.5 text-right font-semibold">Total Taxable Amount</td>
                        <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums font-semibold">{fmt(subtotal)}</td>
                      </tr>
                      {!isIgst && totalCgst > 0 && (
                        <tr>
                          <td className="border border-slate-200 px-2 py-1.5 text-center tabular-nums">{lines.filter(l => l.product_id).length + 2}.</td>
                          <td colSpan={3} className="border border-slate-200 px-2 py-1.5"></td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right">CGST</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">—</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">{fmt(totalCgst)}</td>
                        </tr>
                      )}
                      {!isIgst && totalSgst > 0 && (
                        <tr>
                          <td className="border border-slate-200 px-2 py-1.5 text-center tabular-nums">{lines.filter(l => l.product_id).length + 3}.</td>
                          <td colSpan={3} className="border border-slate-200 px-2 py-1.5"></td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right">SGST</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">—</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">{fmt(totalSgst)}</td>
                        </tr>
                      )}
                      {isIgst && totalIgst > 0 && (
                        <tr>
                          <td className="border border-slate-200 px-2 py-1.5 text-center tabular-nums">{lines.filter(l => l.product_id).length + 2}.</td>
                          <td colSpan={3} className="border border-slate-200 px-2 py-1.5"></td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right">IGST</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">—</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums">{fmt(totalIgst)}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="border border-slate-200 px-2 py-1.5 text-center tabular-nums">{lines.filter(l => l.product_id).length + (isIgst ? 3 : 4)}.</td>
                        <td colSpan={5} className="border border-slate-200 px-2 py-1.5 text-right font-semibold">Total GST Amount</td>
                        <td className="border border-slate-200 px-2 py-1.5 text-right tabular-nums font-semibold">{fmt(totalCgst + totalSgst + totalIgst)}</td>
                      </tr>
                      <tr className="bg-emerald-50">
                        <td className="border border-slate-200 px-2 py-2 text-center tabular-nums">{lines.filter(l => l.product_id).length + (isIgst ? 4 : 5)}.</td>
                        <td colSpan={5} className="border border-slate-200 px-2 py-2 text-right font-bold text-emerald-800 uppercase tracking-wide">Total Amount Payable</td>
                        <td className="border border-slate-200 px-2 py-2 text-right tabular-nums font-bold text-emerald-800">{fmt(grandTotal)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>

              {/* Amount in words */}
              {lines.filter(l => l.product_id).length > 0 && (
                <div className="mb-3 p-2.5 bg-slate-50/50 border border-slate-200 rounded">
                  <div className="text-[11px] font-bold text-slate-700 uppercase">Amount in Words:</div>
                  <div className="text-[13px] font-medium text-slate-800">{numberToWords(grandTotal)}</div>
                </div>
              )}

              {/* Footer: bank | terms | sign */}
              {(() => {
                const activeBank = printBanks.find(b => b.id === printActiveBankId) || printBanks[0] || DEFAULT_BANK;
                return (
                  <div className="grid grid-cols-3 gap-4 pt-3 border-t border-slate-200">
                    <div>
                      <div className="text-[11px] font-bold text-slate-700 uppercase mb-2">Bank Details</div>
                      <KV label="Account Name" value={activeBank.account_name} />
                      <KV label="Account Number" value={activeBank.account_number} />
                      <KV label="IFSC Code" value={activeBank.ifsc} />
                      <KV label="Bank Name" value={activeBank.bank_name} />
                      {activeBank.branch && <KV label="Branch" value={activeBank.branch} />}
                      {(activeBank.upi_id || activeBank.qr_image) && (
                        <div className="mt-2 flex items-start gap-3">
                          {activeBank.qr_image && (
                            <img src={activeBank.qr_image} alt="UPI QR"
                              className="w-20 h-20 object-contain border border-slate-200 rounded bg-white flex-shrink-0" />
                          )}
                          {activeBank.upi_id && (
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] text-slate-500">UPI ID:</div>
                              <div className="text-[12px] font-medium break-all">{activeBank.upi_id}</div>
                              <div className="text-[10px] text-slate-500">Scan & Pay</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-[11px] font-bold text-slate-700 uppercase mb-2">Terms & Conditions</div>
                      <ol className="text-[11px] text-slate-700 space-y-1">
                        {printTerms.map((t, i) => (
                          <li key={i} className="flex gap-1.5">
                            <span className="text-slate-400 tabular-nums">{i + 1}.</span>
                            <span>{t}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                    <div className="text-center">
                      <div className="text-[11px] font-bold text-slate-700 uppercase mb-2">For {printCompany.name}</div>
                      <div className="h-20"></div>
                      <div className="border-t border-slate-400 mx-auto pt-1 text-[11px] text-slate-700" style={{ maxWidth: '200px' }}>
                        Authorised Signatory
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="text-center text-[11px] text-emerald-700 font-medium pt-3 mt-3 border-t border-slate-200">
  'Subject to Guwahati Jurisdiction.',/n
  'This is a computer generated invoice and does not require physical signature.'/n
                ♥ Thank you for your business!
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Vouchers;
